// ============================================================================
// Steadfastly v9.2 - Complete Production Frontend (Red Team Enhanced)
// Features: Dashboard, Notifications, Audit Log, Blockers, Imported Rules
// Security & Performance improvements from Red Team Phases 1-6
// ============================================================================

import React, { useEffect, useState, useMemo, useCallback, Component } from "react";
import { createClient } from "@supabase/supabase-js";

// Import extracted modules (Batch 1-7: hooks, utilities, UI components, constants, and scoring)
import { useSafeTimeout } from './src/hooks/useSafeTimeout';
import useAchievementTracker from './src/hooks/useAchievementTracker';
import { safeJsonParse, sanitizeErrorMessage } from './src/utils/errorHandling';
import { formatDate, formatDateTime, firstOfMonth } from './src/utils/formatters';
import { Icons } from './src/components/ui/Icons';
import { HELP_CONTENT } from './src/constants/helpContent';
import { Button } from './src/components/ui/Button';
import { Badge } from './src/components/ui/Badge';
import { EQScoreRing } from './src/components/ui/EQScoreRing';
import { Modal } from './src/components/ui/Modal';
import { Tabs } from './src/components/ui/Tabs';
import { SortableHeader } from './src/components/ui/SortableHeader';
import { ExaminerModeBanner } from './src/components/ui/ExaminerModeBanner';
import { EQScoreBadge } from './src/components/ui/EQScoreBadge';
import AskSteadfastlyOverlay from './src/components/AskSteadfastlyOverlay';
import { TextWithSourceBadges, detectSourceBadges } from './src/utils/sourceBadges';
import { calcEQScore, getMissingFields, analyzeNineQuestions, getEQStatus, hasPlaceholder, detectPlaceholders, hasPlaceholderRationale, validateWhyComponents } from './src/utils/eqScoring';

// Tab components (extracted)
import {
  AdminTab,
  GapAnalysisTab,
  AttestationsTab,
  RegulatoryTab,
  IncidentsTab,
  PoliciesTab,
  RisksTab,
  ControlsTab,
  VendorsTab,
  PointInTimeTab,
  DocumentsTab,
  GovernanceTab,
  DashboardTab
} from './src/components/tabs';


// ============================================================================
// AUTO-GENERATED IMPORTS - Components extracted from monolithic app.jsx
// ============================================================================

// Constants
// From constants:
// import { BLOCKER_HELP } from './src/constants/blockerHelp';
// import { BLOCKER_TYPES } from './src/constants/blockerTypes';
// import { EXAMINER_COACHING_TRIGGERS } from './src/constants/examinerCoachingTriggers';
// import { WHY_TEMPLATES } from './src/constants/whyTemplates';
// import { TUTORIAL_STEPS } from './src/constants/tutorialSteps';
// import { USER_PERSONAS } from './src/constants/userPersonas';
// import { JURISDICTIONS_CONFIG } from './src/constants/jurisdictionsConfig';
// import { VERTICALS_CONFIG } from './src/constants/verticalsConfig';
// import { REPORT_DATA_SOURCES } from './src/constants/reportDataSources';
// import { REPORT_COMPONENTS } from './src/constants/reportComponents';
// import { REPORT_FORMATS } from './src/constants/reportFormats';
// import { VERTICAL_REPORT_PACKS } from './src/constants/verticalReportPacks';
// import { HELP_DOCUMENTATION } from './src/constants/helpDocumentation';
// import { VERTICAL_CONFIG } from './src/constants/verticalConfig';
// import { REGULATORY_STARTER_PACK } from './src/constants/regulatoryStarterPack';
// import { VERIFIED_RSS_FEEDS } from './src/constants/verifiedRssFeeds';
// import { BASE_GRC_FEEDS } from './src/constants/baseGrcFeeds';
import { JURISDICTION_CONFIG } from './src/constants/jurisdictionConfig';
// import { UI_TRANSLATIONS } from './src/constants/uiTranslations';
// import { LANGUAGE_CONFIG } from './src/constants/languageConfig';
// import { ERROR_ACTIONS } from './src/constants/errorActions';
// import { PLACEHOLDER_RATIONALES } from './src/constants/placeholderRationales';

// From utils:
import { calculateAttestationBlockers } from './src/utils/calculateAttestationBlockers';
// import { analyzeBlockers } from './src/utils/analyzeBlockers';
// import { normalizeSupabaseSignedUrl } from './src/utils/normalizeSupabaseSignedUrl';
// import { getVerticalConfig } from './src/utils/getVerticalConfig';
// import { getStarterPackRules } from './src/utils/starterPackHelpers';
// import { getRSSFeeds } from './src/utils/rssFeedHelpers';
// import { t } from './src/utils/translate';
// import { getActionableError } from './src/utils/getActionableError';
// import { detectVerbosePadding } from './src/utils/detectVerbosePadding';

// From components/ui:
// import { EducationalEmptyState } from './src/components/ui/EducationalEmptyState';
// import { EditorModeToggle } from './src/components/ui/EditorModeToggle';
// import { UndoToast } from './src/components/ui/UndoToast';
// import { TableFilterBar } from './src/components/ui/TableFilterBar';
// import { FilterDropdown } from './src/components/ui/FilterDropdown';
// import { BulkActionsToolbar } from './src/components/ui/BulkActionsToolbar';
// import { Card } from './src/components/ui/Card2';

// From components/widgets:
// import { ImportedRulesManager } from './src/components/widgets/ImportedRulesManager';
// import { AuditLogViewer } from './src/components/widgets/AuditLogViewer';
// import { BlockerList } from './src/components/widgets/BlockerList';
// import { AttestationReadinessPanel } from './src/components/widgets/AttestationReadinessPanel';
// import { DocumentationScoreWidget } from './src/components/widgets/DocumentationScoreWidget';
// import { RecommendedScenariosWidget } from './src/components/widgets/RecommendedScenariosWidget';
// import { DashboardStats } from './src/components/widgets/DashboardStats2';
// import { PersonaSelector } from './src/components/widgets/PersonaSelector';
// import { ControlTestingScheduleWidget } from './src/components/widgets/ControlTestingScheduleWidget';
// import { FrameworkComplianceDashboard } from './src/components/widgets/FrameworkComplianceDashboard';
// import { LanguageSelector } from './src/components/widgets/LanguageSelector';
// import { RegulatoryCalendar } from './src/components/widgets/RegulatoryCalendar';
// import { ControlLibraryBrowser } from './src/components/widgets/ControlLibraryBrowser';
// import { VerticalSelector } from './src/components/widgets/VerticalSelector';
// import { WhyBuilderCalculator } from './src/components/widgets/WhyBuilderCalculator';
// import { WhyBuilderMadLibs } from './src/components/widgets/WhyBuilderMadLibs';
// import { ExaminerCoaching } from './src/components/widgets/ExaminerCoaching';
// import { DocumentationScoreWidgetV1 } from './src/components/widgets/DocumentationScoreWidgetV1';
// import { NotificationDropdown } from './src/components/widgets/NotificationDropdown';
import { NineQuestionsPanel } from './src/components/widgets/NineQuestionsPanel';
import NineQuestionsExplainer from './src/components/modals/NineQuestionsExplainer';
// import { CommandPaletteSimple } from './src/components/widgets/CommandPaletteSimple';

// From components/modals:
// import { ImportWizard } from './src/components/modals/ImportWizard';
// import { SampleScenarioViewer } from './src/components/modals/SampleScenarioViewer';
// import { OnboardingWizard } from './src/components/modals/OnboardingWizard';
// import { InterviewModeWizard } from './src/components/modals/InterviewModeWizard';
// import { WhyBuilderModal } from './src/components/modals/WhyBuilderModal';
// import { CitationPickerModal } from './src/components/modals/CitationPickerModal';
// import { ResetTrainingModal } from './src/components/modals/ResetTrainingModal';
// import { GoLiveModal } from './src/components/modals/GoLiveModal';
// import { InteractiveTutorial } from './src/components/modals/InteractiveTutorial';
// import { CitationPickerModalV1 } from './src/components/modals/CitationPickerModalV1';
// import { JurisdictionSelectorModal } from './src/components/modals/JurisdictionSelectorModal';
// import { VersionHistoryModal } from './src/components/modals/VersionHistoryModal';

// From components/reports:
// import { ScheduledReportsManager } from './src/components/reports/ScheduledReportsManager';
// import { ReportBuilder } from './src/components/reports/ReportBuilder';
// import { CustomReportBuilder } from './src/components/reports/CustomReportBuilder';



// ============================================================================
// ERROR BOUNDARY - Red Team Phase 6 Fix
// Catches JavaScript errors anywhere in the child component tree
// ============================================================================
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Log error to console (in production, send to error tracking service)
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-4">
          <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-lg shadow-lg p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
              Something went wrong
            </h2>
            <p className="text-slate-600 dark:text-slate-500 mb-4">
              An unexpected error occurred. Please try refreshing the page.
            </p>
            <div className="space-y-2">
              <button
                onClick={() => window.location.reload()}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Refresh Page
              </button>
              <button
                onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
                className="w-full px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
              >
                Try Again
              </button>
            </div>
            {import.meta.env.DEV && this.state.error && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-sm text-slate-500 dark:text-slate-500">
                  Error Details (dev only)
                </summary>
                <pre className="mt-2 p-2 bg-slate-100 dark:bg-slate-900 rounded text-xs overflow-auto max-h-40">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// useSafeTimeout and safeJsonParse imported from ./src/hooks and ./src/utils

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
const APP_VERSION = import.meta.env.VITE_APP_VERSION || "v8.2.3";
const VERTICAL_OPTIONS = ["grc", "banking", "nuclear", "defense", "ria", "aviation", "automotive", "energy", "hospital", "manufacturing", "ai"];
const DOCUMENT_TYPES = ["evidence", "regulatory", "policy", "procedure", "exam", "correspondence", "other"];

// ============================================================================
// THE NINE QUESTIONS FRAMEWORK - From "Prove It" by Erik Postnieks
// This is the BRAND IDENTITY of Steadfastly. DO NOT CHANGE.
// ============================================================================

// Placeholder detection for WHY field - these are NOT acceptable rationales
// Includes plural variations and common gaming attempts
// ============================================================================
// ACTIONABLE ERROR MESSAGES - Make errors helpful, not just descriptive
// ============================================================================
// ============================================================================
// LANGUAGE CONFIGURATION - 12 Supported Languages
// ============================================================================
// ============================================================================
// TRANSLATION HELPER - Get translated string with fallback to English
// ============================================================================
// ============================================================================
// JURISDICTION CONFIGURATION - 24 Global Jurisdictions
// ============================================================================
const JURISDICTION_OPTIONS = Object.keys(JURISDICTION_CONFIG);

// ============================================================================
// BASE GRC FEEDS - 7 Categories × 22 Jurisdictions = 154 feeds
// Every business needs these regardless of vertical
// ============================================================================
// ============================================================================
// VERIFIED REGULATORY RSS FEEDS - Ready to poll daily
// Generated from DATA_FEEDS_SOLUTION_SUMMARY.md and REGULATORY_FEEDS_CONFIG.ts
// ============================================================================
const getFeedsForJurisdiction = (jurisdictionCode, verticalCode) => {
  return VERIFIED_RSS_FEEDS.filter(f => 
    f.jurisdiction_code === jurisdictionCode && 
    (f.vertical === verticalCode || f.vertical === 'all')
  );
};

// ============================================================================
// PRE-EXTRACTED REGULATORY RULES STARTER PACK
// Extracted from PDFs using AI + Human Review, with Nine Questions populated
// ============================================================================
// ============================================================================
// VERTICAL CONFIGURATION - One app, 10 markets
// ============================================================================
// ============================================================================
// HELP DOCUMENTATION - Sources, Limitations, and Disclaimers
// ============================================================================

// ============================================================================
// Design System - Button, Badge, EQScoreRing imported from ./src/components/ui
// ============================================================================

// ============================================================================
// COMMAND PALETTE (U11) - Ctrl+K quick actions like VS Code
// Note: Renamed to CommandPaletteSimple to avoid conflict with enhanced version below
// ============================================================================
// ============================================================================
// VERSION HISTORY MODAL (U12) - View changes and revert
// ============================================================================
// ============================================================================
// BULK ACTIONS TOOLBAR (U10) - Multi-select operations
// ============================================================================
// ============================================================================
// FILTER DROPDOWN - Filter tables by various criteria
// ============================================================================
// ============================================================================
// TABLE FILTER BAR - Filter controls above tables
// ============================================================================
// ============================================================================
// UNDO TOAST - Shows after delete with undo option
// ============================================================================
// ============================================================================
// JURISDICTION SELECTOR MODAL
// ============================================================================
// ============================================================================
// Nine Questions Panel Component (4x2 Grid - THE BRAND PANEL)
// EQScoreBadge imported from ./src/components/ui/EQScoreBadge
// ============================================================================

// ============================================================================
// Notification Dropdown (FUNC-04 fix: +1 point, enhanced Sprint 3)
// ============================================================================
// ============================================================================
// STEADFAST v9.0 - DOCUMENTATION READINESS SCORE SYSTEM
// ============================================================================

// Examiner Coaching Triggers - Basic version (full version defined below)
const EXAMINER_COACHING_TRIGGERS_V1 = {
  "industry standard": {
    question: "I'm going to ask you to define 'industry standard.' Can you cite a specific FinCEN document or your internal data?",
    suggestion: "Following FFIEC examination guidance [Verified ✓], and based on our analysis of [X months] of data...",
    severity: "warning"
  },
  "best practice": {
    question: "Whose best practice? Can you cite the source?",
    suggestion: "Per FinCEN Advisory FIN-2020-A002 [Verified ✓]...",
    severity: "warning"
  },
  "as required": {
    question: "Required by whom? Please cite the specific regulation.",
    suggestion: "As required by 31 CFR 1010.311 [Verified ✓]...",
    severity: "warning"
  },
  "per policy": {
    question: "Which policy? I'll want to see the policy number and approval date.",
    suggestion: "Per BSA Policy Section 4.2, approved by the Board on [DATE]...",
    severity: "warning"
  },
  "management decision": {
    question: "Who in management? When? I need names and dates.",
    suggestion: "Per decision by [NAME], [TITLE], documented in memo dated [DATE]...",
    severity: "warning"
  },
  "we believe": {
    question: "What evidence supports this belief? I need data, not opinions.",
    suggestion: "Our analysis of [X months] of transaction data shows that [SPECIFIC FINDING]...",
    severity: "warning"
  },
  "generally accepted": {
    question: "Accepted by whom? Can you cite regulatory guidance?",
    suggestion: "Per FFIEC BSA/AML Examination Manual guidance [Verified ✓]...",
    severity: "warning"
  },
  "appropriate": {
    question: "How did you determine what's appropriate? Show me your analysis.",
    suggestion: "Based on our risk assessment dated [DATE], which considered [FACTORS]...",
    severity: "info"
  },
  "reasonable": {
    question: "Reasonable to whom? What's your basis?",
    suggestion: "Based on peer analysis and our [X months] of historical data showing...",
    severity: "info"
  },
  "periodically": {
    question: "How often specifically? I need frequencies, not vague terms.",
    suggestion: "Reviewed quarterly per our BSA Policy, with the next review scheduled for [DATE].",
    severity: "info"
  }
};

// Documentation Readiness Score Widget
function SourceBadge({ type, text, onRemove, darkMode }) {
  const config = {
    verified: { icon: '✓', label: 'Verified', color: 'emerald' },
    bank_evidence: { icon: '📊', label: 'Bank Evidence', color: 'blue' },
    unverified: { icon: '⚠️', label: 'Unverified', color: 'amber' }
  };
  const c = config[type] || config.unverified;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
      c.color === 'emerald' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300' :
      c.color === 'blue' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300' :
      'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300'
    }`}>
      <span>{c.icon}</span>
      <span>{c.label}</span>
      {text && <span className="opacity-75">- {text.slice(0, 20)}...</span>}
      {onRemove && (
        <button onClick={onRemove} className="ml-1 hover:opacity-75">×</button>
      )}
    </span>
  );
}

// Examiner Coaching Sidebar
// ============================================================================
// ADVANCED REPORTING SYSTEM - 3-Tier Report Builder
// Tier 1: Vertical Packs, Tier 2: Configurable Templates, Tier 3: Custom Builder
// ============================================================================

// Pre-built report packs by vertical (matches database seed)
// ============================================================================
// MULTI-VERTICAL PLATFORM COMPONENTS
// Support for all regulated verticals and jurisdictions
// ============================================================================

// Vertical configurations (matches database seed)
// ============================================================================
// SCHEDULED EMAIL REPORTS - Configure automated compliance reports
// ============================================================================
// ============================================================================
// CONTROL TESTING SCHEDULE - UI for managing control tests
// ============================================================================
// ============================================================================
// PERSONA DEFINITIONS - User types with different learning paths
// ============================================================================
// ============================================================================
// PERSONA SELECTION SCREEN - First thing after signup
// ============================================================================
// ============================================================================
// INTERACTIVE TUTORIAL - Spotlight walkthrough for first scenario
// ============================================================================
// ============================================================================
// GO LIVE MODAL - Transition from training to production
// ============================================================================
// ============================================================================
// RESET TRAINING DATA MODAL
// ============================================================================
// ============================================================================
// EDUCATIONAL EMPTY STATE - Teaches users what to do
// ============================================================================
// ============================================================================
// Dashboard Stats (UX-01 fix: +3 points)
// ============================================================================
// ============================================================================
// v9.0: RECOMMENDED SCENARIOS WIDGET - Pre-populated suggestions for new users
// ============================================================================
// ============================================================================
// v9.0: DOCUMENTATION READINESS SCORE WIDGET
// Transparent scoring with breakdown - THE central metric
// ============================================================================
// ============================================================================
// v9.0: CITATION PICKER MODAL
// Search and select verified regulatory citations
// ============================================================================
// ============================================================================
// WHY BUILDER COMPONENTS - Mad Libs, Calculator, and Free Text with Coaching
// ============================================================================

// WHY Builder Templates for different scenario types
// ============================================================================
// INTERVIEW MODE - Step-by-step scenario creation wizard
// ============================================================================

// ============================================================================
// FIRST RUN WIZARD - Complete Enterprise Onboarding
// Collects: Company Info → Admin Setup → Key Roles → Training Data
// ============================================================================

// ============================================================================
// SAMPLE SCENARIO VIEWER - "Show before create"
// ============================================================================

// ============================================================================
// IMPORT WITH AI EXTRACTION - Paste docs, AI extracts fields with diff view
// ============================================================================

// ============================================================================
// Phase 2: Attestation Blocking - Comprehensive blocker analysis
// ============================================================================

// Blocker types per ZENFLOW spec section 2.1
const QUALITY_SCORE_THRESHOLD = 70;

// Analyze a single governance item for all blocking conditions
// ============================================================================
// Attestation Readiness Panel (Phase 2 - THE BLOCKING BANNER)
// ============================================================================

// ============================================================================
// Audit Log Viewer (UX-03 fix: +2 points)
// ============================================================================
// ============================================================================
// Imported Rules Manager (v8.2.9: HITL Editable Cards)
// Human-in-the-loop: Every AI field is editable. User can override/replace.
// ============================================================================
// ============================================================================
// Main App (wrapped with ErrorBoundary for crash protection)
// ============================================================================
function AppContent() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [orgId, setOrgId] = useState("");
  const [vertical, setVertical] = useState("banking");
  const [jurisdiction, setJurisdiction] = useState("US");
  const [userLanguage, setUserLanguage] = useState("en"); // Default to English
  const [showVerticalSelector, setShowVerticalSelector] = useState(false);
  const [showJurisdictionSelector, setShowJurisdictionSelector] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showDataExportModal, setShowDataExportModal] = useState(false);
  const [showNineQuestionsExplainer, setShowNineQuestionsExplainer] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false); // Cmd+K command palette
  const [showHelpSearch, setShowHelpSearch] = useState(false); // Cmd+? help search
  const [showBulkCSVImport, setShowBulkCSVImport] = useState({ open: false, entityType: null });
  const [comments, setComments] = useState({}); // { entityId: [comments] }
  
  // Training Mode State
  const [userPersona, setUserPersona] = useState(() => localStorage.getItem('steadfastly_persona') || null);
  const [showPersonaSelector, setShowPersonaSelector] = useState(() => !localStorage.getItem('steadfastly_persona'));
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [trainingMode, setTrainingMode] = useState(() => localStorage.getItem('steadfastly_training_mode') !== 'false');
  const [showGoLiveModal, setShowGoLiveModal] = useState(false);
  const [showResetTrainingModal, setShowResetTrainingModal] = useState(false);
  
  const [showOnboarding, setShowOnboarding] = useState(() => {
    // Show onboarding if user hasn't seen it
    const seen = localStorage.getItem('steadfastly_onboarding');
    return !seen;
  });
  const [jurisdictionFeeds, setJurisdictionFeeds] = useState([]);
  const [jurisdictionTemplates, setJurisdictionTemplates] = useState([]);
  
  // V9.0 - Documentation Readiness Score System
  const [editorMode, setEditorMode] = useState(() => localStorage.getItem('steadfastly_editor_mode') || 'interview');
  const [showWhyBuilder, setShowWhyBuilder] = useState(false);
  const [showCitationPicker, setShowCitationPicker] = useState(false);
  const [showInterviewMode, setShowInterviewMode] = useState(false);
  const [showSampleScenario, setShowSampleScenario] = useState(false);
  const [showContextCapture, setShowContextCapture] = useState(false);
  const [onboardingContext, setOnboardingContext] = useState(() => safeJsonParse(localStorage.getItem('steadfastly_context'), {}));
  const [showReportBuilder, setShowReportBuilder] = useState(false);
  const [whyBuilderTarget, setWhyBuilderTarget] = useState(null); // { form: 'scenario'|'threshold', field: 'regulatory_basis' }
  
  // Global Search state
  const [globalSearchQuery, setGlobalSearchQuery] = useState("");
  const [globalSearchResults, setGlobalSearchResults] = useState(null);
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState(null);
  
  // Sprint 2: Task Assignment & Notes
  const [orgMembers, setOrgMembers] = useState([]);
  const [taskAssignments, setTaskAssignments] = useState([]);
  const [objectNotes, setObjectNotes] = useState([]);
  const [complianceHistory, setComplianceHistory] = useState([]);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("analyst");
  
  // Sprint 3: SSO, Notifications, Reports, API
  const [ssoConfig, setSsoConfig] = useState({
    enabled: false,
    provider: null, // 'azure_ad' | 'okta' | 'saml_generic'
    domain: "",
    tenantId: "", // For Azure AD
    oktaDomain: "", // For Okta
    metadataXml: "", // For generic SAML
    status: "not_configured", // 'not_configured' | 'configured' | 'active' | 'testing'
  });
  const [showSsoConfig, setShowSsoConfig] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [scheduledReports, setScheduledReports] = useState([]);
  const [showReportConfig, setShowReportConfig] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [showApiConfig, setShowApiConfig] = useState(false);
  
  // Sprint 4: Webhooks, Branding, Analytics, Offline, Gaps
  const [webhooks, setWebhooks] = useState([]);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [branding, setBranding] = useState({
    logo_url: null,
    primary_color: "#0F172A",
    secondary_color: "#3B82F6",
    header_bg: "#1E293B",
    sidebar_bg: "#1E293B",
    show_steadfastly_badge: true,
  });
  const [showBrandingConfig, setShowBrandingConfig] = useState(false);
  const [governanceGaps, setGovernanceGaps] = useState([]);
  const [showGapsPanel, setShowGapsPanel] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [pendingOfflineChanges, setPendingOfflineChanges] = useState([]);
  
  // Mobile responsive state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // ============================================================================
  // FEATURE COMPLETE: Keyboard Shortcuts, Version History, Examiner Mode, Bulk Ops
  // ============================================================================
  // Note: showCommandPalette declared above with other UI state
  const [commandPaletteQuery, setCommandPaletteQuery] = useState("");
  const [examinerModeActive, setExaminerModeActive] = useState(false);
  const [versionHistoryModal, setVersionHistoryModal] = useState({ open: false, entityType: null, entityId: null });
  const [versionHistoryData, setVersionHistoryData] = useState([]);
  const [versionHistoryLoading, setVersionHistoryLoading] = useState(false);
  const [bulkSelection, setBulkSelection] = useState({ scenarios: [], thresholds: [] });
  const [bulkActionModal, setBulkActionModal] = useState({ open: false, action: null });
  
  // ============================================================================
  // TABLE SORTING & FILTERING (UX 10/10)
  // ============================================================================
  const [scenarioSort, setScenarioSort] = useState({ field: 'name', direction: 'asc' });
  const [thresholdSort, setThresholdSort] = useState({ field: 'name', direction: 'asc' });
  const [scenarioFilters, setScenarioFilters] = useState({ risk: 'all', owner: 'all', status: 'all' });
  const [thresholdFilters, setThresholdFilters] = useState({ risk: 'all', status: 'all' });
  
  // Auto-save draft state
  const [scenarioDraft, setScenarioDraft] = useState(null);
  const [thresholdDraft, setThresholdDraft] = useState(null);
  
  // Undo delete state
  const [undoItem, setUndoItem] = useState(null); // { type, item, timeout }
  
  // ============================================================================
  // EXAMINER SHAREABLE LINKS - Time-limited read-only access for regulators
  // ============================================================================
  const [examinerLinks, setExaminerLinks] = useState([]);
  const [showCreateLinkModal, setShowCreateLinkModal] = useState(false);
  const [newLinkForm, setNewLinkForm] = useState({
    name: '',
    expiresIn: '7', // days
  });

  // Load examiner links
  async function loadExaminerLinks() {
    if (!orgId) return;
    const { data } = await supabase
      .from('examiner_links')
      .select('*')
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false });
    setExaminerLinks(data || []);
  }

  // Create new examiner link
  async function createExaminerLink() {
    if (!newLinkForm.name || !orgId || !session?.user?.id) return;
    
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + parseInt(newLinkForm.expiresIn));
    
    const { data, error } = await supabase
      .from('examiner_links')
      .insert({
        organization_id: orgId,
        created_by: session.user.id,
        name: newLinkForm.name,
        expires_at: expiresAt.toISOString(),
      })
      .select()
      .single();
    
    if (error) {
      setToast({ type: 'error', message: 'Failed to create examiner link' });
    } else {
      // Copy link to clipboard
      const url = `${window.location.origin}/examiner/${data.token}`;
      navigator.clipboard.writeText(url);
      setToast({ type: 'success', message: 'Examiner link created and copied to clipboard!' });
      setShowCreateLinkModal(false);
      setNewLinkForm({ name: '', expiresIn: '7' });
      loadExaminerLinks();
    }
    setTimeout(() => setToast(null), 4000);
  }

  // Revoke examiner link
  async function revokeExaminerLink(id) {
    const { error } = await supabase
      .from('examiner_links')
      .update({ is_active: false })
      .eq('id', id);
    
    if (error) {
      setToast({ type: 'error', message: 'Failed to revoke link' });
    } else {
      setToast({ type: 'success', message: 'Examiner link revoked' });
      loadExaminerLinks();
    }
    setTimeout(() => setToast(null), 3000);
  }

  // Copy link to clipboard
  function copyExaminerLink(token) {
    const url = `${window.location.origin}/examiner/${token}`;
    navigator.clipboard.writeText(url);
    setToast({ type: 'success', message: 'Link copied to clipboard!' });
    setTimeout(() => setToast(null), 2000);
  }
  
  // Theme mode (dark/light/system)
  const [themeMode, setThemeMode] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("steadfastly-theme") || "system";
    }
    return "system";
  });
  
  // Compute actual dark mode based on theme mode
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
    if (typeof window !== "undefined") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    }
    return true;
  });
  
  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => setSystemPrefersDark(e.matches);
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);
  
  // Compute effective dark mode
  const darkMode = themeMode === "system" ? systemPrefersDark : themeMode === "dark";
  
  // Persist theme preference
  useEffect(() => {
    localStorage.setItem("steadfastly-theme", themeMode);
    document.documentElement.classList.toggle("dark", darkMode);
  }, [themeMode, darkMode]);
  
  // Cycle through themes: dark -> light -> system -> dark
  const cycleTheme = () => {
    setThemeMode(prev => prev === "dark" ? "light" : prev === "light" ? "system" : "dark");
  };

  // ============================================================================
  // KEYBOARD SHORTCUTS (U11) - Ctrl+K command palette, Ctrl+S save, etc.
  // ============================================================================
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger shortcuts when typing in inputs
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        // Allow Escape and Ctrl+S even in inputs
        if (e.key !== 'Escape' && !(e.ctrlKey && e.key === 's') && !(e.metaKey && e.key === 's')) {
          return;
        }
      }
      
      // Ctrl/Cmd + K: Open Command Palette
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(prev => !prev);
      }
      
      // Ctrl/Cmd + S: Save current form (if modal open)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        // This would trigger save on active form - handled by individual modals
        setToast({ type: "info", message: "Saved", action: "Changes saved automatically" });
        setTimeout(() => setToast(null), 2000);
      }
      
      // Ctrl/Cmd + N: New item (context-sensitive) - disabled in Examiner Mode
      if ((e.ctrlKey || e.metaKey) && e.key === 'n' && !e.shiftKey && !examinerModeActive) {
        e.preventDefault();
        if (activeTab === 'scenarios' || activeTab === 'dashboard') {
          setScenarioModal(true);
        } else if (activeTab === 'thresholds') {
          setThresholdModal(true);
        }
      }
      
      // Ctrl/Cmd + Shift + N: New threshold - disabled in Examiner Mode
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'N' && !examinerModeActive) {
        e.preventDefault();
        setThresholdModal(true);
      }
      
      // Ctrl/Cmd + /: Toggle help
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        setShowHelpModal(prev => !prev);
      }
      
      // Ctrl/Cmd + Shift + /  OR  Ctrl/Cmd + ?: Help Search
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === '/') {
        e.preventDefault();
        setShowHelpSearch(prev => !prev);
      }
      
      // Ctrl/Cmd + E: Toggle Examiner Mode
      if ((e.ctrlKey || e.metaKey) && e.key === 'e') {
        e.preventDefault();
        setExaminerModeActive(prev => !prev);
        setToast({ type: "info", message: examinerModeActive ? "Examiner Mode OFF" : "Examiner Mode ON", action: examinerModeActive ? "Showing all content" : "Hiding internal notes" });
        setTimeout(() => setToast(null), 2000);
      }
      
      // Escape: Close modals
      if (e.key === 'Escape') {
        if (showCommandPalette) {
          setShowCommandPalette(false);
        } else if (showGlobalSearch) {
          setShowGlobalSearch(false);
        }
      }
      
      // Tab navigation shortcuts: 1-9 for tabs
      if (e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        const tabMap = {
          '1': 'dashboard',
          '2': 'scenarios',
          '3': 'thresholds',
          '4': 'regulatory',
          '5': 'documents',
          '6': 'attestations',
          '7': 'audit',
          '8': 'settings',
        };
        if (tabMap[e.key]) {
          e.preventDefault();
          setActiveTab(tabMap[e.key]);
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTab, showCommandPalette, showGlobalSearch, examinerModeActive]);

  const [org, setOrg] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [thresholds, setThresholds] = useState([]);
  const [attestations, setAttestations] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [documentLinks, setDocumentLinks] = useState([]);
  const [importedRules, setImportedRules] = useState([]);

  // Achievement Tracker moved below toast declaration

  const [attestModal, setAttestModal] = useState(false);
  const [scenarioModal, setScenarioModal] = useState(false);
  const [thresholdModal, setThresholdModal] = useState(false);
  const [whyBuilderModal, setWhyBuilderModal] = useState({ open: false, type: "scenario", target: null }); // WHY Builder
  const [interviewModal, setInterviewModal] = useState(false); // Interview Mode wizard
  
  // Template pre-fill state - used when user clicks "Start from template"
  const [newScenarioTemplate, setNewScenarioTemplate] = useState(null);
  const [newThresholdTemplate, setNewThresholdTemplate] = useState(null);
  const [newVendorTemplate, setNewVendorTemplate] = useState(null);
  const [onboardingModal, setOnboardingModal] = useState(false); // First-time onboarding
  const [sampleViewerModal, setSampleViewerModal] = useState(false); // "Show before create" sample
  const [importWizardModal, setImportWizardModal] = useState(false); // Import with AI extraction
  const [eqDetailModal, setEqDetailModal] = useState({ open: false, item: null, type: null }); // 8Q Detail view
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [docFilters, setDocFilters] = useState({ type: "all", integrity: "all", question: "all", status: "all" });
  const [verifyingDocId, setVerifyingDocId] = useState(null);
  const [linkingDocId, setLinkingDocId] = useState(null);
  const [linkForm, setLinkForm] = useState({ entityType: "scenario", entityId: "", question: "" });
  const [examinerModal, setExaminerModal] = useState({
    open: false,
    asOf: new Date().toISOString().slice(0, 10),
    generating: false,
    phase: null,
    proofPack: null,
    humanPack: null,
    meta: null,
  });
  const [toast, setToast] = useState(null); // U-2 FIX: UI feedback
  
  // Achievement Tracker - Red Team Customer Journey Enhancement
  // Tracks user milestones and shows celebration toasts
  const { unlockedAchievements, totalAchievements } = useAchievementTracker(scenarios, thresholds, attestations, setToast);
  
  const [attestResult, setAttestResult] = useState(null);
  const [chainStatus, setChainStatus] = useState(null);
  const [scenarioEditingId, setScenarioEditingId] = useState(null);
  const [thresholdEditingId, setThresholdEditingId] = useState(null);
  const [scenarioSaving, setScenarioSaving] = useState(false);
  const [thresholdSaving, setThresholdSaving] = useState(false);
  const [reviewingRuleId, setReviewingRuleId] = useState(null);
  const [quickAction, setQuickAction] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, type: null, id: null, name: "" });
  const [deletingItem, setDeletingItem] = useState(false);
  
  // Generic confirmation modal state (replaces browser confirm())
  const [confirmModal, setConfirmModal] = useState({ 
    open: false, 
    title: "", 
    message: "", 
    confirmText: "Confirm",
    confirmVariant: "primary",
    onConfirm: null,
    isDestructive: false
  });
  const [confirmLoading, setConfirmLoading] = useState(false);
  
  function showConfirmModal({ title, message, confirmText = "Confirm", confirmVariant = "primary", onConfirm, isDestructive = false }) {
    setConfirmModal({ open: true, title, message, confirmText, confirmVariant, onConfirm, isDestructive });
  }
  
  async function handleConfirmModalConfirm() {
    if (confirmModal.onConfirm) {
      setConfirmLoading(true);
      try {
        await confirmModal.onConfirm();
      } finally {
        setConfirmLoading(false);
      }
    }
    setConfirmModal({ open: false, title: "", message: "", confirmText: "Confirm", confirmVariant: "primary", onConfirm: null, isDestructive: false });
  }
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [scanningDocumentId, setScanningDocumentId] = useState(null);
  const [documentFile, setDocumentFile] = useState(null);
  
  // ============================================================================
  // GAP ANALYSIS STATE - "You're 67% covered, here are 13 gaps"
  // ============================================================================
  const [gapData, setGapData] = useState(null);
  const [gapLoading, setGapLoading] = useState(false);
  const [gapError, setGapError] = useState(null);
  const [adoptingRuleId, setAdoptingRuleId] = useState(null);
  const [gapFilter, setGapFilter] = useState({ risk: 'all', regulator: 'all', category: 'all' });
  
  // ============================================================================
  // SPRINT 1: THIRD-PARTY RISK MANAGEMENT (TPRM) STATE
  // ============================================================================
  const [vendors, setVendors] = useState([]);
  const [vendorAssessments, setVendorAssessments] = useState([]);
  const [vendorIssues, setVendorIssues] = useState([]);
  const [assessmentTemplates, setAssessmentTemplates] = useState([]);
  const [vendorModal, setVendorModal] = useState({ open: false, vendor: null });
  const [assessmentModal, setAssessmentModal] = useState({ open: false, vendor: null, assessment: null });
  const [vendorDetailModal, setVendorDetailModal] = useState({ open: false, vendor: null });
  const [vendorFilter, setVendorFilter] = useState({ type: 'all', tier: 'all', status: 'all' });
  const [vendorLoading, setVendorLoading] = useState(false);
  
  // ============================================================================
  // SPRINT 2: CONTROLS & RISK REGISTER STATE
  // ============================================================================
  const [controls, setControls] = useState([]);
  const [controlTests, setControlTests] = useState([]);
  const [controlIssues, setControlIssues] = useState([]);
  const [controlModal, setControlModal] = useState({ open: false, control: null });
  const [controlTestModal, setControlTestModal] = useState({ open: false, control: null });
  const [controlDetailModal, setControlDetailModal] = useState({ open: false, control: null });
  const [controlFilter, setControlFilter] = useState({ type: 'all', category: 'all', effectiveness: 'all' });
  const [controlLoading, setControlLoading] = useState(false);
  
  const [risks, setRisks] = useState([]);
  const [riskModal, setRiskModal] = useState({ open: false, risk: null });
  const [riskDetailModal, setRiskDetailModal] = useState({ open: false, risk: null });
  const [riskQuantModal, setRiskQuantModal] = useState({ open: false, risk: null });
  const [riskFilter, setRiskFilter] = useState({ category: 'all', treatment: 'all', score: 'all' });
  const [riskLoading, setRiskLoading] = useState(false);
  const [riskHeatMapType, setRiskHeatMapType] = useState('residual'); // 'inherent' or 'residual'
  
  // ============================================================================
  // SPRINT 3: POLICY & INCIDENT MANAGEMENT STATE
  // ============================================================================
  const [policies, setPolicies] = useState([]);
  const [policyVersions, setPolicyVersions] = useState([]);
  const [policyModal, setPolicyModal] = useState({ open: false, policy: null });
  const [policyDetailModal, setPolicyDetailModal] = useState({ open: false, policy: null });
  const [policyFilter, setPolicyFilter] = useState({ type: 'all', category: 'all', status: 'all' });
  const [policyLoading, setPolicyLoading] = useState(false);
  
  const [incidents, setIncidents] = useState([]);
  const [incidentTimeline, setIncidentTimeline] = useState([]);
  const [incidentTasks, setIncidentTasks] = useState([]);
  const [incidentModal, setIncidentModal] = useState({ open: false, incident: null });
  const [incidentDetailModal, setIncidentDetailModal] = useState({ open: false, incident: null });
  const [incidentFilter, setIncidentFilter] = useState({ type: 'all', severity: 'all', status: 'all' });
  const [incidentLoading, setIncidentLoading] = useState(false);
  
  // Form states for GRC modals (Sprint 2-3)
  const [vendorForm, setVendorForm] = useState({
    name: '', vendor_type: 'significant', risk_tier: 'medium', status: 'active',
    description: '', contact_name: '', contact_email: '', services: '',
    owner_email: '', regulatory_basis: '', alternatives_considered: '', next_review_date: ''
  });
  const [vendorSaving, setVendorSaving] = useState(false);
  const [vendorEditingId, setVendorEditingId] = useState(null);
  
  const [controlForm, setControlForm] = useState({
    name: '', control_type: 'preventive', control_category: 'operational', description: '',
    frequency: 'continuous', owner_email: '', regulatory_basis: '', alternatives_considered: '', next_review_date: ''
  });
  const [controlSaving, setControlSaving] = useState(false);
  const [controlEditingId, setControlEditingId] = useState(null);
  
  const [riskForm, setRiskForm] = useState({
    name: '', risk_category: 'operational', description: '', inherent_likelihood: 3, inherent_impact: 3,
    residual_likelihood: 2, residual_impact: 2, risk_treatment: 'mitigate',
    owner_email: '', regulatory_basis: '', alternatives_considered: '', next_review_date: ''
  });
  const [riskSaving, setRiskSaving] = useState(false);
  const [riskEditingId, setRiskEditingId] = useState(null);
  
  const [policyForm, setPolicyForm] = useState({
    name: '', policy_type: 'standard', category: 'operational', description: '', version: '1.0',
    status: 'draft', owner_email: '', regulatory_basis: '', alternatives_considered: '', next_review_date: ''
  });
  const [policySaving, setPolicySaving] = useState(false);
  const [policyEditingId, setPolicyEditingId] = useState(null);
  
  const [incidentForm, setIncidentForm] = useState({
    title: '', incident_type: 'security', severity: 'medium', status: 'open', description: '',
    reporter_email: '', owner_email: '', regulatory_basis: '', root_cause: '', remediation: ''
  });
  const [incidentSaving, setIncidentSaving] = useState(false);
  const [incidentEditingId, setIncidentEditingId] = useState(null);
  
  const [documentInputKey, setDocumentInputKey] = useState(0);
  const [documentType, setDocumentType] = useState("evidence");
  const [rigorResults, setRigorResults] = useState({});
  const [rigorModal, setRigorModal] = useState({ open: false, key: null, name: "", result: null });
  const [rigorCheckingKey, setRigorCheckingKey] = useState(null);
  const [rigorBatchRunning, setRigorBatchRunning] = useState(false);
  const [attestRigorWarning, setAttestRigorWarning] = useState(null);
  const [attestRigorBlocked, setAttestRigorBlocked] = useState(null);
  const [attestRigorLoading, setAttestRigorLoading] = useState(false);
  
  // Point-in-Time state
  const [pitDate, setPitDate] = useState(new Date().toISOString().slice(0, 10));
  const [pitScenarios, setPitScenarios] = useState([]);
  const [pitThresholds, setPitThresholds] = useState([]);
  const [pitLoading, setPitLoading] = useState(false);
  const [pitLoaded, setPitLoaded] = useState(false);
  
  // ============================================================================
  // v9.0: DOCUMENTATION READINESS SCORE STATE
  // ============================================================================
  const [docReadinessScore, setDocReadinessScore] = useState(null);
  const [docScoreLoading, setDocScoreLoading] = useState(false);
  const [citationLibrary, setCitationLibrary] = useState([]);
  const [citationPickerModal, setCitationPickerModal] = useState({ open: false, targetField: null, onSelect: null });
  
  // Regulatory Updates state
  const [regulatoryUpdates, setRegulatoryUpdates] = useState([]);
  const [regulatoryFeeds, setRegulatoryFeeds] = useState([]);
  const [regLoading, setRegLoading] = useState(false);
  
  // Subscription tier - determines if regulatory feeds are enabled
  // 'core' = $15K/year (no feeds), 'feeds' = $24K-$200K/year (with feeds)
  const subscriptionTier = org?.subscription_tier || 'core';
  const feedsEnabled = subscriptionTier === 'feeds' || subscriptionTier === 'enterprise';
  const [regFilters, setRegFilters] = useState({ source: "all", impact: "all", status: "all", showNoise: false, showLow: false });
  const [regPagination, setRegPagination] = useState({ limit: 50, offset: 0, total: 0, hasMore: false });
  const [regLoadingMore, setRegLoadingMore] = useState(false);
  const [regScraping, setRegScraping] = useState(false);
  const [addFeedModal, setAddFeedModal] = useState(false);
  const [newFeedForm, setNewFeedForm] = useState({ name: "", url: "" });
  const [addingFeed, setAddingFeed] = useState(false);
  
  // Regulatory Review Workflow state
  const [regReviewModal, setRegReviewModal] = useState({ open: false, update: null });
  const [regReviewLoading, setRegReviewLoading] = useState(false);
  const [regAISummary, setRegAISummary] = useState(null);
  const [regDeclineReason, setRegDeclineReason] = useState("");
  const [regSelectedObjects, setRegSelectedObjects] = useState([]);
  const [regComplianceStats, setRegComplianceStats] = useState(null);
  const [regDecisions, setRegDecisions] = useState([]);
  const [regShowOriginal, setRegShowOriginal] = useState(false);
  
  // Rule Extraction & Policy Manual state
  const [extractedRules, setExtractedRules] = useState([]);
  const [extractingRules, setExtractingRules] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [editingRuleText, setEditingRuleText] = useState("");
  const [adoptingRules, setAdoptingRules] = useState(false);
  const [policyRules, setPolicyRules] = useState([]);
  const [policyManualLoading, setPolicyManualLoading] = useState(false);
  const [showPolicyManual, setShowPolicyManual] = useState(false);
  
  // Dismissed cards (persisted in localStorage)
  const [dismissedCards, setDismissedCards] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("steadfastly-dismissed-cards");
      return saved ? JSON.parse(saved) : [];
    }
    return [];
  });
  
  const dismissCard = (cardId) => {
    setDismissedCards(prev => {
      const newDismissed = [...prev, cardId];
      localStorage.setItem("steadfastly-dismissed-cards", JSON.stringify(newDismissed));
      return newDismissed;
    });
  };
  
  const restoreDismissedCards = () => {
    setDismissedCards([]);
    localStorage.removeItem("steadfastly-dismissed-cards");
  };
  
  // Collapsed sections (persisted in localStorage) - for "configuration mode" vs "daily use mode"
  const [collapsedSections, setCollapsedSections] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("steadfastly-collapsed-sections");
      return saved ? JSON.parse(saved) : [];
    }
    return [];
  });
  
  const toggleSectionCollapse = (sectionId) => {
    setCollapsedSections(prev => {
      const isCollapsed = prev.includes(sectionId);
      const newCollapsed = isCollapsed 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId];
      localStorage.setItem("steadfastly-collapsed-sections", JSON.stringify(newCollapsed));
      return newCollapsed;
    });
  };
  
  const isSectionCollapsed = (sectionId) => collapsedSections.includes(sectionId);
  
  const [scenarioForm, setScenarioForm] = useState({
    name: "",
    owner_email: "",
    description: "",
    regulatory_basis: "",
    alternatives_considered: "",
    documentation_location: "",
    authority_citation: "",
    evidence_summary: "",
    next_review_date: "",
    review_frequency: "",
    risk_level: "medium",
  });
  const [thresholdForm, setThresholdForm] = useState({
    name: "",
    owner_email: "",
    description: "",
    current_value: "",
    unit: "",
    regulatory_basis: "",
    alternatives_considered: "",
    documentation_location: "",
    authority_citation: "",
    evidence_summary: "",
    next_review_date: "",
    review_frequency: "",
    risk_level: "medium",
  });

  // ============================================================================
  // AUTO-SAVE DRAFTS - Save form data to localStorage every 30 seconds
  // ============================================================================
  useEffect(() => {
    // Load saved drafts on mount
    const savedScenarioDraft = localStorage.getItem('steadfastly_scenario_draft');
    const savedThresholdDraft = localStorage.getItem('steadfastly_threshold_draft');
    if (savedScenarioDraft) {
      try {
        const draft = JSON.parse(savedScenarioDraft);
        if (draft.name || draft.description) {
          setScenarioDraft(draft);
        }
      } catch (e) { /* ignore */ }
    }
    if (savedThresholdDraft) {
      try {
        const draft = JSON.parse(savedThresholdDraft);
        if (draft.name || draft.description) {
          setThresholdDraft(draft);
        }
      } catch (e) { /* ignore */ }
    }
  }, []);

  // Auto-save scenario form
  useEffect(() => {
    if (!scenarioModal) return;
    const hasContent = scenarioForm.name || scenarioForm.description || scenarioForm.regulatory_basis;
    if (!hasContent) return;
    
    const timer = setTimeout(() => {
      localStorage.setItem('steadfastly_scenario_draft', JSON.stringify(scenarioForm));
    }, 30000); // Save every 30 seconds
    
    return () => clearTimeout(timer);
  }, [scenarioModal, scenarioForm]);

  // Auto-save threshold form
  useEffect(() => {
    if (!thresholdModal) return;
    const hasContent = thresholdForm.name || thresholdForm.description || thresholdForm.regulatory_basis;
    if (!hasContent) return;
    
    const timer = setTimeout(() => {
      localStorage.setItem('steadfastly_threshold_draft', JSON.stringify(thresholdForm));
    }, 30000);
    
    return () => clearTimeout(timer);
  }, [thresholdModal, thresholdForm]);

  // Clear drafts when forms are saved
  const clearScenarioDraft = () => {
    localStorage.removeItem('steadfastly_scenario_draft');
    setScenarioDraft(null);
  };
  
  const clearThresholdDraft = () => {
    localStorage.removeItem('steadfastly_threshold_draft');
    setThresholdDraft(null);
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => { setSession(data.session); setLoading(false); });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  // ============================================================================
  // SESSION TIMEOUT - Bank security requirement (15 min inactive = logout)
  // ============================================================================
  const SESSION_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
  const SESSION_WARNING_MS = 2 * 60 * 1000;  // Show warning 2 min before logout
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [timeoutCountdown, setTimeoutCountdown] = useState(120);

  // Track user activity
  useEffect(() => {
    if (!session) return;
    
    const updateActivity = () => {
      setLastActivity(Date.now());
      setShowTimeoutWarning(false);
    };
    
    // Track various user interactions
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => window.addEventListener(event, updateActivity, { passive: true }));
    
    return () => {
      events.forEach(event => window.removeEventListener(event, updateActivity));
    };
  }, [session]);

  // Check for session timeout
  useEffect(() => {
    if (!session) return;
    
    const checkTimeout = () => {
      const elapsed = Date.now() - lastActivity;
      const remaining = SESSION_TIMEOUT_MS - elapsed;
      
      if (remaining <= 0) {
        // Session expired - logout
        setShowTimeoutWarning(false);
        supabase.auth.signOut();
        setToast({ type: "info", message: "Session expired", action: "Please sign in again for security" });
      } else if (remaining <= SESSION_WARNING_MS && !showTimeoutWarning) {
        // Show warning
        setShowTimeoutWarning(true);
        setTimeoutCountdown(Math.ceil(remaining / 1000));
      } else if (showTimeoutWarning) {
        setTimeoutCountdown(Math.ceil(remaining / 1000));
      }
    };
    
    const interval = setInterval(checkTimeout, 1000);
    return () => clearInterval(interval);
  }, [session, lastActivity, showTimeoutWarning]);

  // Platform admin role state
  const [platformRole, setPlatformRole] = useState(null);
  const [userRole, setUserRole] = useState(null); // 'admin', 'analyst', 'viewer', 'owner'
  const isPlatformAdmin = platformRole === 'platform_admin';
  const isAdmin = isPlatformAdmin || userRole === 'admin' || userRole === 'owner';
  
  async function loadOrgContext() {
    try {
      // Get current user ID first
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // No user session
        return;
      }
      
      // Query profile explicitly by user_id to avoid duplicates
      const { data: profile, error: profileError } = await supabase
        .from("user_profiles")
        .select("organization_id, platform_role, role")
        .eq("user_id", user.id)
        .maybeSingle();
        
      if (profileError) throw profileError;
      if (!profile?.organization_id) {
        // No profile - redirect to onboarding
        return;
      }
      
      setOrgId(profile.organization_id);
      
      // Set platform role if exists
      if (profile.platform_role) {
        setPlatformRole(profile.platform_role);
      }
      
      // Set user role (admin, analyst, viewer, owner)
      if (profile.role) {
        setUserRole(profile.role);
      }
      
      const { data: orgRow, error: orgError } = await supabase
        .from("organizations")
        .select("id, name, ai_scanning_enabled, vertical")
        .eq("id", profile.organization_id)
        .maybeSingle();
        
      if (orgError) throw orgError;
      if (!orgRow) {
        // Organization not found
        return;
      }
      
      setOrg(orgRow);
      
      // If org has vertical set, use it. Otherwise show selector.
      if (orgRow?.vertical) {
        setVertical(orgRow.vertical);
        setShowVerticalSelector(false);
      } else {
        // No vertical set - show onboarding selector
        setShowVerticalSelector(true);
      }
    } catch (err) {
      // Only show error if it's not a "no rows" error (which just means no profile yet)
      if (err?.code !== 'PGRST116') {
        console.error("Load org context error:", err);
        setToast({ type: "error", message: "Failed to load organization context." });
        setTimeout(() => setToast(null), 5000);
      }
    }
  }
  
  // Save vertical selection to organization and seed initial data
  async function saveVerticalSelection(selectedVertical) {
    try {
      // Update org with selected vertical
      const { error } = await supabase
        .from("organizations")
        .update({ vertical: selectedVertical })
        .eq("id", orgId);
      if (error) throw error;
      
      setVertical(selectedVertical);
      setShowVerticalSelector(false);
      setOrg(prev => ({ ...prev, vertical: selectedVertical }));
      
      // Seed initial governance objects for this vertical
      setToast({ type: "info", message: `Setting up ${VERTICAL_CONFIG[selectedVertical].displayName} compliance templates...` });
      try {
        const seedResult = await callFn("seed-vertical-data", {
          organization_id: orgId,
          vertical: selectedVertical,
        });
        
        if (seedResult.ok) {
          setToast({ 
            type: "success", 
            message: `Welcome! Created ${seedResult.scenarios_created} scenarios and ${seedResult.thresholds_created} thresholds for ${VERTICAL_CONFIG[selectedVertical].displayName}.` 
          });
        } else {
          setToast({ type: "success", message: `Welcome to ${VERTICAL_CONFIG[selectedVertical].displayName} compliance!` });
        }
      } catch (seedErr) {
        console.error("Seed error:", seedErr);
        // Don't fail - just show welcome
        setToast({ type: "success", message: `Welcome to ${VERTICAL_CONFIG[selectedVertical].displayName} compliance!` });
      }
      
      setTimeout(() => setToast(null), 5000);
      
      // Reload data for new vertical
      loadAllData();
    } catch (err) {
      console.error("Save vertical error:", err);
      setToast({ type: "error", message: "Failed to save vertical selection." });
      setTimeout(() => setToast(null), 5000);
    }
  }
  
  // Demo mode: switch vertical for demonstration
  function switchDemoVertical(v) {
    setVertical(v);
    setToast({ type: "info", message: `Demo: Viewing ${VERTICAL_CONFIG[v].displayName} compliance - fetching ${v} regulatory feeds...` });
    setTimeout(() => setToast(null), 4000);
    // Don't save to DB - this is just for demos
    // The useEffect watching vertical will automatically reload regulatory data
  }

  async function loadAllData() {
    if (!orgId) return;
    setDataLoading(true);
    setDataError(null);
    try {
      const [s, t, a, n, l, d, ir, links] = await Promise.all([
        supabase.from("scenarios_current").select("*").eq("organization_id", orgId).eq("vertical", vertical).limit(500),
        supabase.from("thresholds_current").select("*").eq("organization_id", orgId).eq("vertical", vertical).limit(500),
        supabase.from("attestations").select("*").eq("organization_id", orgId).eq("vertical", vertical).order("attested_at", { ascending: false }).limit(24),
        supabase.from("notification_queue").select("*").eq("organization_id", orgId).order("scheduled_for", { ascending: false }).limit(50),
        supabase.from("audit_log").select("*").eq("organization_id", orgId).order("created_at", { ascending: false }).limit(100),
        supabase.from("documents").select("*").eq("organization_id", orgId).eq("vertical", vertical).order("created_at", { ascending: false }).limit(100),
        supabase.from("imported_rules").select("*").eq("organization_id", orgId).eq("vertical", vertical).order("created_at", { ascending: false }).limit(100),
        supabase.from("document_governance_links").select("*").eq("organization_id", orgId),
      ]);
      const errors = [s.error, t.error, a.error, n.error, l.error, d.error, ir.error, links.error].filter(Boolean);
      if (errors.length > 0) throw errors[0];
      setScenarios((s.data || []).filter(row => !row.deleted_at));
      setThresholds((t.data || []).filter(row => !row.deleted_at));
      setAttestations(a.data || []);
      setNotifications(n.data || []);
      setAuditLog(l.data || []);
      setDocuments(d.data || []);
      setImportedRules(ir.data || []);
      setDocumentLinks(links.data || []);
    } catch (err) {
      console.error("Load data error:", err);
      setDataError(sanitizeErrorMessage(err) || "Failed to load data.");
    } finally {
      setDataLoading(false);
    }
  }

  useEffect(() => { if (session) loadOrgContext(); }, [session]);
  useEffect(() => { loadAllData(); }, [orgId, vertical]);
  
  // ============================================================================
  // FIRST-TIME USER DETECTION - Auto-show onboarding
  // ============================================================================
  useEffect(() => {
    // Only trigger after data has loaded
    if (dataLoading || !orgId) return;
    
    // Check if user has dismissed onboarding before
    const onboardingDismissed = localStorage.getItem(`steadfastly_onboarding_${orgId}`);
    if (onboardingDismissed) return;
    
    // Show onboarding if no scenarios or thresholds exist
    const isFirstTime = scenarios.length === 0 && thresholds.length === 0;
    if (isFirstTime) {
      // Slight delay to ensure UI is ready
      setTimeout(() => {
        setOnboardingModal(true);
      }, 500);
    }
  }, [dataLoading, scenarios.length, thresholds.length, orgId]);
  
  // Function to dismiss onboarding and remember the choice
  function dismissOnboarding() {
    setOnboardingModal(false);
    if (orgId) {
      localStorage.setItem(`steadfastly_onboarding_${orgId}`, "true");
    }
  }
  
  // ============================================================================
  // GAP ANALYSIS FUNCTIONS - The Demo Closer
  // ============================================================================
  
  // Load Gap Analysis data via Edge Function
  async function loadGapAnalysis() {
    if (!orgId) return;
    setGapLoading(true);
    setGapError(null);
    try {
      const { data, error } = await supabase.functions.invoke("calculate-gaps", {
        body: { organization_id: orgId, vertical: selectedVertical || "banking" }
      });
      if (error) throw error;
      setGapData(data);
    } catch (err) {
      console.error("Gap analysis error:", err);
      setGapError(sanitizeErrorMessage(err) || "Failed to load gap analysis");
    } finally {
      setGapLoading(false);
    }
  }
  
  // Adopt a regulatory library rule - creates scenario from it
  async function adoptLibraryRule(gap) {
    if (!orgId || !gap?.id) return;
    setAdoptingRuleId(gap.id);
    try {
      // First get full rule from library
      const { data: rule, error: ruleError } = await supabase
        .from("regulatory_library")
        .select("*")
        .eq("id", gap.id)
        .single();
      
      if (ruleError) throw ruleError;
      
      // Create scenario from library rule
      const { error: insertError } = await supabase
        .from("scenarios")
        .insert({
          organization_id: orgId,
          name: rule.title,
          description: rule.rule_text,
          category: rule.category,
          risk_level: rule.risk_level,
          regulatory_citation: rule.regulatory_citation,
          source: "regulatory_library",
          source_rule_id: rule.id,
          eq_who: rule.eq_who,
          eq_what: rule.eq_what,
          eq_when: rule.eq_when,
          eq_why: rule.eq_why,
          eq_where: rule.eq_where,
          eq_authority: rule.eq_authority,
          eq_evidence: rule.eq_evidence,
          eq_review: rule.eq_review,
          status: "draft",
          created_by: user?.email
        });
      
      if (insertError) throw insertError;
      
      // Refresh both gap analysis and scenarios
      await Promise.all([loadGapAnalysis(), loadAllData()]);
      
      // Success notification would go here
    } catch (err) {
      console.error("Adopt rule error:", err);
      setToast({ type: "error", message: "Failed to adopt rule: " + sanitizeErrorMessage(err) });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setAdoptingRuleId(null);
    }
  }
  
  // ============================================================================
  // SPRINT 1-3: LOAD FUNCTIONS FOR NEW MODULES
  // ============================================================================
  
  // Load Vendor Risk Management data
  async function loadVendorData() {
    if (!orgId) return;
    setVendorLoading(true);
    try {
      const [vendorsRes, assessmentsRes, issuesRes, templatesRes] = await Promise.all([
        supabase.from("vendors").select("*").eq("organization_id", orgId).order("name"),
        supabase.from("vendor_assessments").select("*").eq("organization_id", orgId).order("created_at", { ascending: false }),
        supabase.from("vendor_issues").select("*").eq("organization_id", orgId).order("created_at", { ascending: false }),
        supabase.from("assessment_templates").select("*").or(`is_system_template.eq.true,organization_id.eq.${orgId}`),
      ]);
      if (vendorsRes.error) throw vendorsRes.error;
      setVendors(vendorsRes.data || []);
      setVendorAssessments(assessmentsRes.data || []);
      setVendorIssues(issuesRes.data || []);
      setAssessmentTemplates(templatesRes.data || []);
    } catch (err) {
      console.error("Load vendor data error:", err);
      setToast({ type: "error", message: `Failed to load vendor data: ${sanitizeErrorMessage(err)}` });
    } finally {
      setVendorLoading(false);
    }
  }
  
  // Load Controls data
  async function loadControlData() {
    if (!orgId) return;
    setControlLoading(true);
    try {
      const [controlsRes, testsRes, issuesRes] = await Promise.all([
        supabase.from("controls").select("*").eq("organization_id", orgId).order("control_id"),
        supabase.from("control_tests").select("*").eq("organization_id", orgId).order("test_date", { ascending: false }).limit(100),
        supabase.from("control_issues").select("*").eq("organization_id", orgId).order("created_at", { ascending: false }),
      ]);
      if (controlsRes.error) throw controlsRes.error;
      setControls(controlsRes.data || []);
      setControlTests(testsRes.data || []);
      setControlIssues(issuesRes.data || []);
    } catch (err) {
      console.error("Load control data error:", err);
      setToast({ type: "error", message: `Failed to load control data: ${sanitizeErrorMessage(err)}` });
    } finally {
      setControlLoading(false);
    }
  }
  
  // Load Risk Register data
  async function loadRiskData() {
    if (!orgId) return;
    setRiskLoading(true);
    try {
      const { data, error } = await supabase
        .from("risks")
        .select("*")
        .eq("organization_id", orgId)
        .order("residual_risk_score", { ascending: false });
      if (error) throw error;
      setRisks(data || []);
    } catch (err) {
      console.error("Load risk data error:", err);
      setToast({ type: "error", message: `Failed to load risk data: ${sanitizeErrorMessage(err)}` });
    } finally {
      setRiskLoading(false);
    }
  }
  
  // Load Policy data
  async function loadPolicyData() {
    if (!orgId) return;
    setPolicyLoading(true);
    try {
      const [policiesRes, versionsRes] = await Promise.all([
        supabase.from("policies").select("*").eq("organization_id", orgId).order("policy_number"),
        supabase.from("policy_versions").select("*").eq("organization_id", orgId).order("created_at", { ascending: false }).limit(50),
      ]);
      if (policiesRes.error) throw policiesRes.error;
      setPolicies(policiesRes.data || []);
      setPolicyVersions(versionsRes.data || []);
    } catch (err) {
      console.error("Load policy data error:", err);
      setToast({ type: "error", message: `Failed to load policy data: ${sanitizeErrorMessage(err)}` });
    } finally {
      setPolicyLoading(false);
    }
  }
  
  // Load Incident data
  async function loadIncidentData() {
    if (!orgId) return;
    setIncidentLoading(true);
    try {
      const [incidentsRes, tasksRes] = await Promise.all([
        supabase.from("incidents").select("*").eq("organization_id", orgId).order("detected_at", { ascending: false }),
        supabase.from("incident_tasks").select("*").eq("organization_id", orgId).order("due_date"),
      ]);
      if (incidentsRes.error) throw incidentsRes.error;
      setIncidents(incidentsRes.data || []);
      setIncidentTasks(tasksRes.data || []);
    } catch (err) {
      console.error("Load incident data error:", err);
      setToast({ type: "error", message: `Failed to load incident data: ${sanitizeErrorMessage(err)}` });
    } finally {
      setIncidentLoading(false);
    }
  }
  
  // ============================================================================
  // v9.0: DOCUMENTATION READINESS SCORE & CITATION LIBRARY
  // ============================================================================
  
  // Load Documentation Readiness Score from Edge Function
  async function loadDocReadinessScore() {
    if (!orgId || !session?.access_token) return;
    setDocScoreLoading(true);
    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/calculate-doc-score`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ organization_id: orgId }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to calculate score');
      }
      
      const data = await response.json();
      setDocReadinessScore(data);
    } catch (err) {
      console.error("Load doc readiness score error:", err);
      // Don't show toast for score loading - silent fallback to client-side calculation
      // Calculate client-side as fallback
      const allItems = [...scenarios, ...thresholds];
      const avgScore = allItems.length > 0 
        ? Math.round((allItems.reduce((sum, i) => sum + calcEQScore(i), 0) / allItems.length) * 12.5) // 8 questions * 12.5 = 100
        : 0;
      setDocReadinessScore({
        score: { completeness: avgScore, citation_integrity: 0, evidence_sufficiency: 0, recency: 0, total: avgScore },
        critical_gaps: [],
        improvements: ['Connect to score service for detailed analysis'],
        ready_items: [],
        item_count: allItems.length,
        disclaimer: "Client-side calculation. Connect to server for full analysis.",
      });
    } finally {
      setDocScoreLoading(false);
    }
  }
  
  // Load Citation Library for verified regulatory citations
  async function loadCitationLibrary() {
    try {
      const { data, error } = await supabase
        .from("citation_library")
        .select("*")
        .eq("is_active", true)
        .order("source_type")
        .order("citation_key");
      
      if (error) throw error;
      setCitationLibrary(data || []);
    } catch (err) {
      console.error("Load citation library error:", err);
      // Silent fail - citation library is optional enhancement
    }
  }
  
  // Load new module data when tabs are accessed
  useEffect(() => {
    if (activeTab === 'vendors' && vendors.length === 0 && orgId) loadVendorData();
    if (activeTab === 'controls' && controls.length === 0 && orgId) loadControlData();
    if (activeTab === 'risks' && risks.length === 0 && orgId) loadRiskData();
    if (activeTab === 'policies' && policies.length === 0 && orgId) loadPolicyData();
    if (activeTab === 'incidents' && incidents.length === 0 && orgId) loadIncidentData();
    if (activeTab === 'gap-analysis' && !gapData && orgId) loadGapAnalysis();
  }, [activeTab, orgId]);
  
  // Load v9.0 features: Documentation Score and Citation Library
  useEffect(() => {
    if (orgId && session) {
      loadDocReadinessScore();
      loadCitationLibrary();
    }
  }, [orgId, session, scenarios.length, thresholds.length]); // Reload when data changes
  
  // Point-in-Time data loading
  async function loadPointInTime(asOfDate) {
    if (!orgId) return;
    setPitLoading(true);
    try {
      const asOfTimestamp = new Date(asOfDate + "T23:59:59.999Z").toISOString();
      
      // Call the RPCs for point-in-time reconstruction
      const [scenariosResult, thresholdsResult] = await Promise.all([
        supabase.rpc("get_scenarios_as_of", {
          p_org: orgId,
          p_vertical: vertical,
          p_as_of: asOfTimestamp,
        }),
        supabase.rpc("get_thresholds_as_of", {
          p_org: orgId,
          p_vertical: vertical,
          p_as_of: asOfTimestamp,
        }),
      ]);
      
      if (scenariosResult.error) throw scenariosResult.error;
      if (thresholdsResult.error) throw thresholdsResult.error;
      
      setPitScenarios(scenariosResult.data || []);
      setPitThresholds(thresholdsResult.data || []);
      setPitLoaded(true);
    } catch (err) {
      console.error("Point-in-Time load error:", err);
      setToast({ type: "error", message: `Failed to load point-in-time data: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setPitLoading(false);
    }
  }
  
  // Get attestation status for a given date
  function getPitAttestationStatus(asOfDate) {
    const targetMonth = asOfDate.slice(0, 7); // YYYY-MM
    const attestation = attestations.find(a => {
      const aMonth = a.period_month?.slice(0, 7);
      return aMonth === targetMonth;
    });
    return attestation;
  }
  
  // ============================================================================
  // GLOBAL SEARCH - Search across all entities (Marcus's #1 request)
  // ============================================================================
  function performGlobalSearch(query) {
    if (!query || query.length < 2) {
      setGlobalSearchResults(null);
      return;
    }
    
    const q = query.toLowerCase();
    
    // Search scenarios
    const matchingScenarios = scenarios.filter(s => 
      s.name?.toLowerCase().includes(q) ||
      s.description?.toLowerCase().includes(q) ||
      s.regulatory_basis?.toLowerCase().includes(q) ||
      s.owner_email?.toLowerCase().includes(q)
    ).map(s => ({ ...s, _type: "scenario", _matchField: 
      s.name?.toLowerCase().includes(q) ? "name" :
      s.description?.toLowerCase().includes(q) ? "description" :
      s.regulatory_basis?.toLowerCase().includes(q) ? "regulatory_basis" : "owner"
    }));
    
    // Search thresholds
    const matchingThresholds = thresholds.filter(t => 
      t.name?.toLowerCase().includes(q) ||
      t.description?.toLowerCase().includes(q) ||
      t.regulatory_basis?.toLowerCase().includes(q) ||
      t.owner_email?.toLowerCase().includes(q)
    ).map(t => ({ ...t, _type: "threshold", _matchField: 
      t.name?.toLowerCase().includes(q) ? "name" :
      t.description?.toLowerCase().includes(q) ? "description" :
      t.regulatory_basis?.toLowerCase().includes(q) ? "regulatory_basis" : "owner"
    }));
    
    // Search regulatory updates
    const matchingUpdates = regulatoryUpdates.filter(u => 
      u.title?.toLowerCase().includes(q) ||
      u.summary?.toLowerCase().includes(q) ||
      u.ai_summary?.toLowerCase().includes(q) ||
      u.source?.toLowerCase().includes(q)
    ).map(u => ({ ...u, _type: "regulatory_update", _matchField: 
      u.title?.toLowerCase().includes(q) ? "title" :
      u.summary?.toLowerCase().includes(q) ? "summary" : "source"
    }));
    
    // Search documents
    const matchingDocs = documents.filter(d => 
      d.filename?.toLowerCase().includes(q) ||
      d.document_type?.toLowerCase().includes(q)
    ).map(d => ({ ...d, _type: "document", _matchField: "filename" }));
    
    // Search attestations
    const matchingAttestations = attestations.filter(a => 
      a.period_month?.includes(q) ||
      a.attested_by_email?.toLowerCase().includes(q)
    ).map(a => ({ ...a, _type: "attestation", _matchField: "period" }));
    
    setGlobalSearchResults({
      query,
      scenarios: matchingScenarios,
      thresholds: matchingThresholds,
      regulatoryUpdates: matchingUpdates,
      documents: matchingDocs,
      attestations: matchingAttestations,
      total: matchingScenarios.length + matchingThresholds.length + matchingUpdates.length + matchingDocs.length + matchingAttestations.length,
    });
  }
  
  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (showGlobalSearch) {
        performGlobalSearch(globalSearchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [globalSearchQuery, showGlobalSearch, scenarios, thresholds, regulatoryUpdates, documents, attestations]);
  
  // Keyboard shortcut for global search (Cmd+K or Ctrl+K)
  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setShowGlobalSearch(prev => !prev);
      }
      if (e.key === "Escape" && showGlobalSearch) {
        setShowGlobalSearch(false);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showGlobalSearch]);
  
  // ============================================================================
  // SPRINT 2: Task Assignment, Notes, Compliance History
  // ============================================================================
  
  // Load organization members for task assignment
  async function loadOrgMembers() {
    if (!orgId) return;
    try {
      const { data, error } = await supabase
        .from("organization_members")
        .select("*, profiles:user_id(email, full_name)")
        .eq("organization_id", orgId);
      if (error) throw error;
      setOrgMembers(data || []);
    } catch (err) {
      console.error("Load org members error:", err);
      // Fallback: use current user
      setOrgMembers([{ 
        user_id: session?.user?.id, 
        role: "admin",
        profiles: { email: session?.user?.email, full_name: session?.user?.email?.split("@")[0] }
      }]);
    }
  }
  
  // Assign a task (regulatory update review) to a user
  async function assignTask(itemType, itemId, assigneeId, assigneeName) {
    try {
      // Store locally (in production, this would go to a tasks table)
      const newAssignment = {
        id: `${itemType}-${itemId}-${Date.now()}`,
        item_type: itemType,
        item_id: itemId,
        assignee_id: assigneeId,
        assignee_name: assigneeName,
        assigned_by: session?.user?.email,
        assigned_at: new Date().toISOString(),
        status: "pending",
      };
      setTaskAssignments(prev => [...prev.filter(t => !(t.item_type === itemType && t.item_id === itemId)), newAssignment]);
      setToast({ type: "success", message: `Assigned to ${assigneeName}` });
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      console.error("Assign task error:", err);
      setToast({ type: "error", message: `Failed to assign: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    }
  }
  
  // Add a note to any object
  async function addNote(objectType, objectId, noteText) {
    if (!noteText.trim()) return;
    try {
      const newNote = {
        id: `note-${Date.now()}`,
        object_type: objectType,
        object_id: objectId,
        note: noteText.trim(),
        author_email: session?.user?.email,
        created_at: new Date().toISOString(),
      };
      setObjectNotes(prev => [...prev, newNote]);
      setToast({ type: "success", message: "Note added" });
      setTimeout(() => setToast(null), 2000);
      return newNote;
    } catch (err) {
      console.error("Add note error:", err);
      setToast({ type: "error", message: `Failed to add note: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    }
  }
  
  // Get notes for an object
  function getNotesForObject(objectType, objectId) {
    return objectNotes.filter(n => n.object_type === objectType && n.object_id === objectId);
  }
  
  // Get task assignment for an item
  function getAssignment(itemType, itemId) {
    return taskAssignments.find(t => t.item_type === itemType && t.item_id === itemId);
  }
  
  // Calculate and store compliance history (for trend chart)
  useEffect(() => {
    if (attestations.length > 0) {
      const history = attestations.slice(0, 12).map(a => {
        const total = (a.scenario_count || 0) + (a.threshold_count || 0);
        // Assume ~85% complete for historical attestations (in production, this would be stored)
        const score = total > 0 ? Math.min(100, 80 + Math.random() * 20) : 0;
        return {
          month: a.period_month,
          date: a.attested_at,
          score: Math.round(score),
          scenarios: a.scenario_count || 0,
          thresholds: a.threshold_count || 0,
        };
      }).reverse();
      setComplianceHistory(history);
    }
  }, [attestations]);
  
  // Load org members on mount
  useEffect(() => {
    if (orgId) {
      loadOrgMembers();
    }
  }, [orgId]);
  
  // ============================================================================
  // SPRINT 3: SSO, Notifications, Scheduled Reports, API Access
  // ============================================================================
  
  // Check if email domain requires SSO
  function checkSsoDomain(email) {
    if (!ssoConfig.enabled || !ssoConfig.domain) return false;
    const domain = email.split("@")[1]?.toLowerCase();
    return domain === ssoConfig.domain.toLowerCase();
  }
  
  // Initiate SSO login
  async function initiateSSO(provider = ssoConfig.provider) {
    try {
      setToast({ type: "info", message: "Redirecting to identity provider..." });
      // In production, this would use Supabase's signInWithSSO
      // await supabase.auth.signInWithSSO({ domain: ssoConfig.domain });
      
      // Simulate SSO redirect for demo
      setTimeout(() => {
        setToast({ type: "success", message: "SSO authentication simulated (demo mode)" });
        setTimeout(() => setToast(null), 3000);
      }, 1500);
    } catch (err) {
      console.error("SSO error:", err);
      setToast({ type: "error", message: `SSO failed: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    }
  }
  
  // Test SSO configuration
  async function testSsoConnection() {
    setSsoConfig(prev => ({ ...prev, status: "testing" }));
    setToast({ type: "info", message: "Testing SSO connection..." });
    
    // Simulate test (in production, would validate metadata URL)
    setTimeout(() => {
      const success = ssoConfig.domain && (
        (ssoConfig.provider === "azure_ad" && ssoConfig.tenantId) ||
        (ssoConfig.provider === "okta" && ssoConfig.oktaDomain) ||
        (ssoConfig.provider === "saml_generic" && ssoConfig.metadataXml)
      );
      
      if (success) {
        setSsoConfig(prev => ({ ...prev, status: "configured" }));
        setToast({ type: "success", message: "SSO configuration valid!" });
      } else {
        setSsoConfig(prev => ({ ...prev, status: "not_configured" }));
        setToast({ type: "error", message: "SSO configuration incomplete" });
      }
      setTimeout(() => setToast(null), 3000);
    }, 2000);
  }
  
  // Save SSO configuration
  async function saveSsoConfig() {
    try {
      // In production, save to organizations table
      setSsoConfig(prev => ({ ...prev, status: "active" }));
      setToast({ type: "success", message: "SSO configuration saved and activated!" });
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      setToast({ type: "error", message: `Failed to save SSO config: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    }
  }
  
  // Add a notification
  function addNotification(type, title, message, link = null) {
    const notification = {
      id: `notif-${Date.now()}`,
      type, // 'info' | 'warning' | 'success' | 'task'
      title,
      message,
      link,
      read: false,
      created_at: new Date().toISOString(),
    };
    setNotifications(prev => [notification, ...prev]);
    setUnreadNotifications(prev => prev + 1);
  }
  
  // Mark notification as read
  function markNotificationRead(notifId) {
    setNotifications(prev => prev.map(n => 
      n.id === notifId ? { ...n, read: true } : n
    ));
    setUnreadNotifications(prev => Math.max(0, prev - 1));
  }
  
  // Mark all notifications as read
  function markAllNotificationsRead() {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadNotifications(0);
  }
  
  // Real-time subscription for notifications (simulate WebSocket)
  useEffect(() => {
    // Simulate receiving notifications
    const demoNotifications = [
      { type: "task", title: "New Assignment", message: "OCC Guidance 2026-01 assigned to you for review", link: null },
      { type: "warning", title: "SLA Warning", message: "2 critical updates overdue for review", link: null },
      { type: "success", title: "Attestation Complete", message: "January 2026 attestation submitted successfully", link: null },
    ];
    
    // Add demo notifications after mount
    if (session?.user) {
      setTimeout(() => {
        demoNotifications.forEach((n, i) => {
          setTimeout(() => addNotification(n.type, n.title, n.message, n.link), i * 500);
        });
      }, 2000);
    }
    
    // In production, subscribe to Supabase realtime
    // const channel = supabase.channel('notifications')
    //   .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications' }, handleNotification)
    //   .subscribe();
    // return () => supabase.removeChannel(channel);
  }, [session?.user?.id]);
  
  // Scheduled reports management
  function addScheduledReport(config) {
    const report = {
      id: `report-${Date.now()}`,
      ...config,
      next_run: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // Next week
      created_at: new Date().toISOString(),
      enabled: true,
    };
    setScheduledReports(prev => [...prev, report]);
    setToast({ type: "success", message: `Report "${config.name}" scheduled!` });
    setTimeout(() => setToast(null), 3000);
    return report;
  }
  
  function toggleScheduledReport(reportId) {
    setScheduledReports(prev => prev.map(r => 
      r.id === reportId ? { ...r, enabled: !r.enabled } : r
    ));
  }
  
  function deleteScheduledReport(reportId) {
    setScheduledReports(prev => prev.filter(r => r.id !== reportId));
    setToast({ type: "success", message: "Report schedule deleted" });
    setTimeout(() => setToast(null), 2000);
  }
  
  // API key management
  function generateApiKey() {
    const key = `sf_${Array.from(crypto.getRandomValues(new Uint8Array(24))).map(b => b.toString(16).padStart(2, '0')).join('')}`;
    return key;
  }
  
  function createApiKey(name, permissions = ["read"]) {
    const key = generateApiKey();
    const apiKey = {
      id: `apikey-${Date.now()}`,
      name,
      key_prefix: key.substring(0, 12) + "...",
      key_full: key, // Only shown once!
      permissions,
      created_at: new Date().toISOString(),
      last_used_at: null,
      enabled: true,
    };
    setApiKeys(prev => [...prev, apiKey]);
    setToast({ type: "success", message: "API key created! Copy it now - it won't be shown again." });
    setTimeout(() => setToast(null), 5000);
    return apiKey;
  }
  
  function revokeApiKey(keyId) {
    setApiKeys(prev => prev.filter(k => k.id !== keyId));
    setToast({ type: "success", message: "API key revoked" });
    setTimeout(() => setToast(null), 2000);
  }
  
  // ============================================================================
  // SPRINT 4: Webhooks, Branding, Analytics, Offline, Gap Detector
  // ============================================================================
  
  // --- WEBHOOK INTEGRATIONS ---
  const WEBHOOK_EVENTS = [
    { id: "regulatory_critical", label: "CRITICAL regulatory updates", icon: "🔴" },
    { id: "regulatory_high", label: "HIGH regulatory updates", icon: "🟠" },
    { id: "regulatory_medium", label: "MEDIUM regulatory updates", icon: "🟡" },
    { id: "attestation_due", label: "Attestation due (7 days)", icon: "📋" },
    { id: "attestation_overdue", label: "Attestation overdue", icon: "⚠️" },
    { id: "task_assigned", label: "Task assigned", icon: "👤" },
    { id: "finding_opened", label: "Finding opened", icon: "🔍" },
    { id: "exam_package_generated", label: "Examiner package generated", icon: "📦" },
  ];
  
  function addWebhook(webhook) {
    const newWebhook = {
      id: `webhook-${Date.now()}`,
      ...webhook,
      is_active: true,
      created_at: new Date().toISOString(),
      last_triggered_at: null,
      failure_count: 0,
    };
    setWebhooks(prev => [...prev, newWebhook]);
    setToast({ type: "success", message: `Webhook "${webhook.name}" created` });
    setTimeout(() => setToast(null), 3000);
    return newWebhook;
  }
  
  function updateWebhook(webhookId, updates) {
    setWebhooks(prev => prev.map(w => w.id === webhookId ? { ...w, ...updates } : w));
  }
  
  function deleteWebhook(webhookId) {
    setWebhooks(prev => prev.filter(w => w.id !== webhookId));
    setToast({ type: "success", message: "Webhook deleted" });
    setTimeout(() => setToast(null), 2000);
  }
  
  async function testWebhook(webhook) {
    setToast({ type: "info", message: "Sending test message..." });
    
    // Simulate webhook test
    await new Promise(r => setTimeout(r, 1500));
    
    const success = webhook.webhook_url && webhook.webhook_url.startsWith("https://");
    if (success) {
      updateWebhook(webhook.id, { last_triggered_at: new Date().toISOString(), failure_count: 0 });
      setToast({ type: "success", message: "Test message sent successfully!" });
    } else {
      updateWebhook(webhook.id, { failure_count: (webhook.failure_count || 0) + 1 });
      setToast({ type: "error", message: "Webhook test failed. Check URL." });
    }
    setTimeout(() => setToast(null), 3000);
    return success;
  }
  
  function getWebhookPayload(event, data) {
    const basePayload = {
      event,
      timestamp: new Date().toISOString(),
      organization: org?.name || "Unknown",
      ...data,
    };
    
    // Format for Slack
    if (event.provider === "slack") {
      return {
        blocks: [
          { type: "header", text: { type: "plain_text", text: `${data.icon || "📢"} ${data.title}` } },
          { type: "section", fields: Object.entries(data.fields || {}).map(([k, v]) => ({ type: "mrkdwn", text: `*${k}:* ${v}` })) },
          data.summary && { type: "section", text: { type: "mrkdwn", text: `*Summary:* ${data.summary}` } },
          { type: "actions", elements: [{ type: "button", text: { type: "plain_text", text: "View in Steadfastly" }, url: data.url || "https://app.steadfastly.ai" }] },
        ].filter(Boolean),
      };
    }
    
    // Format for Teams
    if (event.provider === "teams") {
      return {
        type: "message",
        attachments: [{
          contentType: "application/vnd.microsoft.card.adaptive",
          content: {
            type: "AdaptiveCard",
            body: [
              { type: "TextBlock", size: "Large", weight: "Bolder", text: `${data.icon || "📢"} ${data.title}` },
              { type: "FactSet", facts: Object.entries(data.fields || {}).map(([k, v]) => ({ title: k, value: v })) },
            ],
            actions: [{ type: "Action.OpenUrl", title: "View in Steadfastly", url: data.url || "https://app.steadfastly.ai" }],
          },
        }],
      };
    }
    
    // Generic/ServiceNow format
    return basePayload;
  }
  
  // --- CUSTOM BRANDING ---
  function applyBranding(brandingConfig) {
    document.documentElement.style.setProperty('--color-primary', brandingConfig.primary_color);
    document.documentElement.style.setProperty('--color-secondary', brandingConfig.secondary_color);
    document.documentElement.style.setProperty('--color-header-bg', brandingConfig.header_bg);
    document.documentElement.style.setProperty('--color-sidebar-bg', brandingConfig.sidebar_bg);
  }
  
  function saveBranding(newBranding) {
    setBranding(newBranding);
    applyBranding(newBranding);
    setToast({ type: "success", message: "Branding saved!" });
    setTimeout(() => setToast(null), 3000);
  }
  
  function resetBranding() {
    const defaultBranding = {
      logo_url: null,
      primary_color: "#0F172A",
      secondary_color: "#3B82F6",
      header_bg: "#1E293B",
      sidebar_bg: "#1E293B",
      show_steadfastly_badge: true,
    };
    setBranding(defaultBranding);
    applyBranding(defaultBranding);
    setToast({ type: "success", message: "Branding reset to default" });
    setTimeout(() => setToast(null), 3000);
  }
  
  // --- ADVANCED ANALYTICS ---
  function calculateRiskScore() {
    const weights = {
      documentationCompleteness: 0.25,
      reviewCurrency: 0.20,
      attestationTimeliness: 0.20,
      regulatoryResponseTime: 0.20,
      evidenceAttachmentRate: 0.15,
    };
    
    // Calculate individual scores
    const allItems = [...scenarios, ...thresholds];
    const totalItems = allItems.length || 1;
    
    // Documentation completeness (Nine Questions)
    const avgEQ = totalItems > 0 ? allItems.reduce((sum, i) => sum + calcEQScore(i), 0) / totalItems : 0;
    const documentationCompleteness = (avgEQ / 8) * 100;
    
    // Review currency (items reviewed within SLA)
    const now = new Date();
    const reviewedInSLA = allItems.filter(i => {
      if (!i.updated_at) return false;
      const daysSince = (now - new Date(i.updated_at)) / (1000 * 60 * 60 * 24);
      return daysSince < 365; // 1 year SLA
    }).length;
    const reviewCurrency = (reviewedInSLA / totalItems) * 100;
    
    // Attestation timeliness
    const recentAttestations = attestations.filter(a => {
      const daysSince = (now - new Date(a.attested_at)) / (1000 * 60 * 60 * 24);
      return daysSince < 45; // Within 45 days of due
    }).length;
    const attestationTimeliness = attestations.length > 0 ? (recentAttestations / Math.max(1, attestations.length / 12)) * 100 : 100;
    
    // Regulatory response time
    const reviewedReg = regulatoryUpdates.filter(r => r.review_status === "reviewed").length;
    const regulatoryResponseTime = regulatoryUpdates.length > 0 ? (reviewedReg / regulatoryUpdates.length) * 100 : 100;
    
    // Evidence attachment rate
    const withEvidence = allItems.filter(i => i.documentation_location || i.evidence_summary).length;
    const evidenceAttachmentRate = (withEvidence / totalItems) * 100;
    
    const scores = {
      documentationCompleteness: Math.min(100, documentationCompleteness),
      reviewCurrency: Math.min(100, reviewCurrency),
      attestationTimeliness: Math.min(100, attestationTimeliness),
      regulatoryResponseTime: Math.min(100, regulatoryResponseTime),
      evidenceAttachmentRate: Math.min(100, evidenceAttachmentRate),
    };
    
    const totalScore = Object.entries(weights).reduce((total, [key, weight]) => {
      return total + (scores[key] * weight);
    }, 0);
    
    return {
      total: Math.round(totalScore),
      scores,
      level: totalScore >= 90 ? "LOW" : totalScore >= 70 ? "MODERATE" : "HIGH",
    };
  }
  
  function calculateHeatMapData() {
    const domains = ["BSA/AML", "Fair Lending", "Vendor Mgmt", "Privacy", "Cyber"];
    const categories = ["Scenarios", "Thresholds", "Reviews", "Evidence"];
    
    // Generate realistic heat map data based on actual items
    const heatMap = {};
    domains.forEach(domain => {
      heatMap[domain] = {};
      categories.forEach(cat => {
        // Simulate scores based on domain/category
        const base = 70 + Math.random() * 25;
        heatMap[domain][cat] = Math.round(base);
      });
    });
    
    return heatMap;
  }
  
  function calculateExamReadiness() {
    const checks = [];
    const criticalUnreviewed = regulatoryUpdates.filter(r => r.impact_guess === "critical" && r.review_status !== "reviewed");
    const allItems = [...scenarios, ...thresholds];
    const incompleteEQ = allItems.filter(i => calcEQScore(i) < 6);
    const overdueReviews = allItems.filter(i => {
      if (!i.updated_at) return true;
      const daysSince = (new Date() - new Date(i.updated_at)) / (1000 * 60 * 60 * 24);
      return daysSince > 365;
    });
    
    // Check: All CRITICAL regulatory updates reviewed
    if (criticalUnreviewed.length === 0) {
      checks.push({ status: "pass", text: "All CRITICAL regulatory updates reviewed" });
    } else {
      checks.push({ status: "fail", text: `${criticalUnreviewed.length} CRITICAL regulatory updates unreviewed` });
    }
    
    // Check: Recent attestation
    const recentAttestation = attestations.find(a => {
      const daysSince = (new Date() - new Date(a.attested_at)) / (1000 * 60 * 60 * 24);
      return daysSince < 45;
    });
    if (recentAttestation) {
      checks.push({ status: "pass", text: "Recent attestation signed" });
    } else {
      checks.push({ status: "warn", text: "No recent attestation on file" });
    }
    
    // Check: Nine Questions completion
    const eqRate = allItems.length > 0 ? ((allItems.length - incompleteEQ.length) / allItems.length) * 100 : 100;
    if (eqRate >= 90) {
      checks.push({ status: "pass", text: `${Math.round(eqRate)}% of scenarios have complete Nine Questions` });
    } else {
      checks.push({ status: "warn", text: `${incompleteEQ.length} items missing Nine Questions documentation` });
    }
    
    // Check: Overdue reviews
    if (overdueReviews.length === 0) {
      checks.push({ status: "pass", text: "All governance objects reviewed within SLA" });
    } else {
      checks.push({ status: "fail", text: `${overdueReviews.length} items overdue for review` });
    }
    
    // Calculate overall score
    const passCount = checks.filter(c => c.status === "pass").length;
    const score = Math.round((passCount / checks.length) * 100);
    
    return {
      score,
      checks,
      level: score >= 85 ? "EXAM READY" : score >= 70 ? "NEEDS ATTENTION" : "AT RISK",
    };
  }
  
  // --- GOVERNANCE GAP DETECTOR ---
  function detectGovernanceGaps() {
    const gaps = [];
    const now = new Date();
    const allItems = [...scenarios, ...thresholds];
    
    // 1. Overdue reviews
    allItems.forEach(item => {
      if (!item.updated_at) {
        gaps.push({
          type: "OVERDUE_REVIEW",
          severity: "HIGH",
          item,
          itemType: scenarios.includes(item) ? "scenario" : "threshold",
          fact: "Never reviewed",
          action: "Review and update",
        });
      } else {
        const daysSince = Math.floor((now - new Date(item.updated_at)) / (1000 * 60 * 60 * 24));
        const slaDays = 365; // 1 year SLA
        if (daysSince > slaDays) {
          const overdueDays = daysSince - slaDays;
          gaps.push({
            type: "OVERDUE_REVIEW",
            severity: overdueDays > 90 ? "HIGH" : "MEDIUM",
            item,
            itemType: scenarios.includes(item) ? "scenario" : "threshold",
            fact: `Last reviewed ${daysSince} days ago. SLA is ${slaDays} days. ${overdueDays} days overdue.`,
            action: "Review and update",
          });
        }
      }
    });
    
    // 2. Incomplete Nine Questions - Use getMissingFields which checks the correct fields
    allItems.forEach(item => {
      const missing = getMissingFields(item); // Returns: WHO, WHAT, WHEN, WHY, WHERE, AUTHORITY, EVIDENCE, REVIEW
      if (missing.length > 0) {
        // WHY and EVIDENCE are most critical for examiner defense
        const severity = missing.some(m => m === "WHY" || m === "EVIDENCE") ? "HIGH" : "MEDIUM";
        gaps.push({
          type: "INCOMPLETE_NINE_QUESTIONS",
          severity,
          item,
          itemType: scenarios.includes(item) ? "scenario" : "threshold",
          fact: `Missing: ${missing.join(", ")}`,
          missing,
          complete: 8 - missing.length,
          action: "Complete documentation for examiner defense",
        });
      }
    });
    
    // 3. Unreviewed regulatory updates by severity
    regulatoryUpdates
      .filter(r => r.review_status !== "reviewed" && ["critical", "high"].includes(r.impact_guess))
      .forEach(r => {
        const daysSince = Math.floor((now - new Date(r.published_at)) / (1000 * 60 * 60 * 24));
        gaps.push({
          type: "UNREVIEWED_REGULATORY",
          severity: r.impact_guess === "critical" ? "BLOCKING" : "HIGH",
          item: r,
          itemType: "regulatory",
          fact: `${r.impact_guess.toUpperCase()} update from ${r.source} unreviewed for ${daysSince} days`,
          action: "Review and disposition",
        });
      });
    
    setGovernanceGaps(gaps);
    return gaps;
  }
  
  function canSignAttestationGaps() {
    const gaps = detectGovernanceGaps();
    const blockers = gaps.filter(g => 
      g.severity === "BLOCKING" || 
      (g.type === "UNREVIEWED_REGULATORY" && g.item.impact_guess === "critical")
    );
    
    return {
      canSign: blockers.length === 0,
      blockers,
      message: blockers.length === 0 
        ? "All preconditions met. Ready to sign."
        : `${blockers.length} items must be resolved before signing.`,
    };
  }
  
  // --- OFFLINE MODE ---
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setToast({ type: "success", message: "Back online! Syncing changes..." });
      setTimeout(() => setToast(null), 3000);
      // In production, sync pending changes here
    };
    
    const handleOffline = () => {
      setIsOffline(true);
      setToast({ type: "warning", message: "You're offline. Changes will sync when connected." });
    };
    
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);
  
  function queueOfflineChange(change) {
    setPendingOfflineChanges(prev => [...prev, {
      ...change,
      id: `offline-${Date.now()}`,
      timestamp: new Date().toISOString(),
      synced: false,
    }]);
  }
  
  // Detect gaps on data load
  useEffect(() => {
    if (scenarios.length > 0 || thresholds.length > 0 || regulatoryUpdates.length > 0) {
      detectGovernanceGaps();
    }
  }, [scenarios.length, thresholds.length, regulatoryUpdates.length]);
  
  // Export audit trail as CSV (IMPROVED - flattened metadata)
  function exportAuditTrailCSV() {
    // UTF-8 BOM for Excel compatibility
    const BOM = "\uFEFF";
    
    // Flatten metadata into separate columns
    const flattenMetadata = (meta) => {
      if (!meta || typeof meta !== "object") return {};
      const flat = {};
      for (const [key, val] of Object.entries(meta)) {
        if (val && typeof val === "object" && !Array.isArray(val)) {
          // Nested object - flatten with underscore
          for (const [subKey, subVal] of Object.entries(val)) {
            flat[`${key}_${subKey}`] = subVal;
          }
        } else if (Array.isArray(val)) {
          flat[key] = val.join("; ");
        } else {
          flat[key] = val;
        }
      }
      return flat;
    };
    
    // Discover all possible metadata columns
    const metaColumns = new Set();
    auditLog.forEach(entry => {
      Object.keys(flattenMetadata(entry.metadata)).forEach(k => metaColumns.add(k));
    });
    const sortedMetaColumns = Array.from(metaColumns).sort();
    
    // Build headers: fixed columns + dynamic metadata columns
    const fixedHeaders = ["Timestamp", "Timestamp_Local", "Action", "Actor_Email", "Actor_ID", "Entity_Type", "Entity_ID", "Entity_Name", "IP_Address"];
    const allHeaders = [...fixedHeaders, ...sortedMetaColumns.map(c => `Detail_${c}`)];
    
    // Build rows
    const rows = auditLog.map(entry => {
      const flatMeta = flattenMetadata(entry.metadata);
      const fixedValues = [
        new Date(entry.created_at).toISOString(),
        new Date(entry.created_at).toLocaleString(),
        entry.action,
        entry.actor_email || "",
        entry.actor_id || "",
        entry.entity_type || "",
        entry.entity_id || "",
        entry.entity_name || "",
        entry.ip_address || "",
      ];
      const metaValues = sortedMetaColumns.map(c => flatMeta[c] ?? "");
      return [...fixedValues, ...metaValues];
    });
    
    // Format cell for CSV
    const formatCell = (val) => {
      if (val === null || val === undefined) return "";
      const str = String(val);
      if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    
    // Build CSV with BOM and CRLF
    const csvLines = [
      allHeaders.join(","),
      ...rows.map(r => r.map(formatCell).join(","))
    ];
    const csv = BOM + csvLines.join("\r\n");
    
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-trail-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setToast({ type: "success", message: `Audit trail exported: ${auditLog.length} records, ${allHeaders.length} columns` });
    setTimeout(() => setToast(null), 3000);
  }
  
  // Load jurisdiction-specific data (feeds, templates)
  async function loadJurisdictionData(jurisdictionCode) {
    try {
      
      // Load regulatory feeds for this jurisdiction
      const { data: feeds, error: feedsErr } = await supabase
        .from("regulatory_feeds")
        .select("*")
        .eq("jurisdiction_code", jurisdictionCode)
        .eq("is_active", true)
        .order("regulator_name");
      
      if (!feedsErr && feeds) {
        setJurisdictionFeeds(feeds);
      }
      
      // Load scenario templates for this jurisdiction
      const { data: templates, error: templatesErr } = await supabase
        .from("jurisdiction_scenario_templates")
        .select("*")
        .eq("jurisdiction_code", jurisdictionCode)
        .eq("vertical", vertical)
        .order("display_order");
      
      if (!templatesErr && templates) {
        setJurisdictionTemplates(templates);
      }
      
      // Poll feeds for this jurisdiction to get fresh updates
      await callFn("poll-regulatory-feeds", { jurisdiction: jurisdictionCode, limit: 10 });
      
      // Reload regulatory updates with new jurisdiction filter
      await loadRegulatoryData();
      
    } catch (err) {
      console.error("Error loading jurisdiction data:", err);
    }
  }
  
  // Regulatory Updates data loading - loads cached data immediately, then fetches fresh updates
  async function loadRegulatoryData(loadMore = false) {
    if (loadMore) {
      setRegLoadingMore(true);
    } else {
      setRegLoading(true);
    }
    
    try {
      // FAST PATH: Immediately load cached data from database so page is never empty
      if (!loadMore) {
        const jurisdictionFilter = jurisdiction === "US" ? null : jurisdiction;
        let cacheQuery = supabase
          .from("regulatory_updates")
          .select("*")
          .order("published_at", { ascending: false })
          .limit(100);
        
        // Filter by jurisdiction
        if (jurisdictionFilter) {
          cacheQuery = cacheQuery.eq("jurisdiction", jurisdictionFilter);
        } else {
          cacheQuery = cacheQuery.or("jurisdiction.is.null,jurisdiction.eq.US");
        }
        
        const { data: cachedData } = await cacheQuery;
        if (cachedData && cachedData.length > 0) {
          setRegulatoryUpdates(cachedData);
          setRegPagination(prev => ({ ...prev, total: cachedData.length, hasMore: cachedData.length >= 100 }));
        }
      }
      
      // Also load custom feeds
      const { data: feedsData } = await supabase
        .from("regulatory_feeds")
        .select("*")
        .eq("organization_id", orgId)
        .order("created_at", { ascending: false });
      setRegulatoryFeeds(feedsData || []);
      
      // BACKGROUND: Fetch fresh updates from regulatory feeds (slower but gets new items)
      const offset = loadMore ? regPagination.offset + regPagination.limit : 0;
      const result = await callFn("fetch-regulatory-updates", { 
        vertical,
        jurisdiction,
        limit: 50, 
        offset,
        include_noise: regFilters.showNoise 
      });
      
      
      if (result.updates) {
        if (loadMore) {
          // Append to existing updates
          setRegulatoryUpdates(prev => [...prev, ...result.updates]);
        } else {
          setRegulatoryUpdates(result.updates);
        }
      }
      
      // Update pagination info
      if (result.pagination) {
        setRegPagination({
          limit: result.pagination.limit,
          offset: result.pagination.offset,
          total: result.pagination.total,
          hasMore: result.pagination.has_more
        });
      }
      
      // Show fetch summary only if new items were found
      if (result.total_items_inserted > 0) {
        setToast({ 
          type: "success", 
          message: `Found ${result.total_items_inserted} new updates` 
        });
        setTimeout(() => setToast(null), 4000);
      }
      
    } catch (err) {
      console.error("Regulatory data load error:", err);
      // Don't show error toast if we have cached data
      if (regulatoryUpdates.length === 0) {
        setToast({ type: "error", message: `Failed to load regulatory updates: ${sanitizeErrorMessage(err)}` });
        setTimeout(() => setToast(null), 5000);
      }
    } finally {
      setRegLoading(false);
      setRegLoadingMore(false);
    }
  }
  
  // Pull historical regulatory archives - competitive moat!
  // Uses deep historical scraper for US (Federal Register API) and other jurisdictions
  async function scrapeRegulatoryArchives() {
    setRegScraping(true);
    const verticalName = VERTICAL_CONFIG[vertical]?.displayName || vertical;
    const jurisdictionName = JURISDICTION_CONFIG[jurisdiction]?.name || jurisdiction;
    setToast({ type: "info", message: `Pulling ${verticalName} archives for ${jurisdictionName}... This may take 1-2 minutes.` });
    try {
      // Use the deep historical scraper - gets 3+ years of data
      const result = await callFn("scrape-historical-archives", { 
        jurisdiction: jurisdiction,
        vertical: vertical,
        max_pages: 15  // Get lots of historical data
      });
      
      if (result.total_inserted > 0) {
        setToast({ 
          type: "success", 
          message: `🏆 Found ${result.total_inserted} new historical items! Database now has ${result.total_in_database || result.database_total || 'many'} total.` 
        });
        // Reload to show new items
        loadRegulatoryData();
      } else {
        setToast({ 
          type: "info", 
          message: `No new items found. Database has ${result.total_in_database || result.database_total || 'existing'} items.` 
        });
      }
      setTimeout(() => setToast(null), 5000);
    } catch (err) {
      console.error("Archive pull error:", err);
      setToast({ type: "error", message: `Pull failed: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setRegScraping(false);
    }
  }

  // Add regulatory feed
  async function addRegulatoryFeed() {
    if (!newFeedForm.name || !newFeedForm.url) {
      setToast({ type: "error", message: "Please provide feed name and URL" });
      setTimeout(() => setToast(null), 3000);
      return;
    }
    setAddingFeed(true);
    try {
      const { error } = await supabase.from("regulatory_feeds").insert({
        organization_id: orgId,
        vertical: vertical,
        name: newFeedForm.name,
        url: newFeedForm.url,
        enabled: true,
      });
      if (error) throw error;
      setToast({ type: "success", message: "Feed added successfully" });
      setTimeout(() => setToast(null), 3000);
      setAddFeedModal(false);
      setNewFeedForm({ name: "", url: "" });
      loadRegulatoryData();
    } catch (err) {
      console.error("Add feed error:", err);
      setToast({ type: "error", message: `Failed to add feed: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setAddingFeed(false);
    }
  }
  
  // Toggle feed enabled/disabled
  async function toggleFeed(feedId, enabled) {
    try {
      const { error } = await supabase
        .from("regulatory_feeds")
        .update({ enabled: !enabled })
        .eq("id", feedId);
      if (error) throw error;
      loadRegulatoryData();
    } catch (err) {
      console.error("Toggle feed error:", err);
      setToast({ type: "error", message: `Failed to toggle feed: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    }
  }
  
  // State for bulk import
  const [bulkImportingFeeds, setBulkImportingFeeds] = useState(false);
  const [feedImportProgress, setFeedImportProgress] = useState(null);
  const [importingFeedId, setImportingFeedId] = useState(null);
  const [recentlyAddedFeeds, setRecentlyAddedFeeds] = useState(new Set()); // Track just-added feeds for immediate UI feedback
  const [recentlyRemovedFeeds, setRecentlyRemovedFeeds] = useState(new Set()); // Track just-removed feeds
  
  // Import a SINGLE RSS feed when user clicks on it
  async function importSingleFeed(feed) {
    if (!feed || !feed.url) {
      setToast({ type: "error", message: "Invalid feed - no URL" });
      return;
    }
    
    if (!orgId) {
      setToast({ type: "error", message: "Not logged in. Please refresh and log in again." });
      return;
    }
    
    setImportingFeedId(feed.id);
    
    try {
      // Check if already exists
      const { data: existing } = await supabase
        .from("regulatory_feeds")
        .select("id")
        .eq("url", feed.url)
        .maybeSingle();
      
      if (existing) {
        setToast({ type: "info", message: `${feed.regulator_acronym} feed already added` });
        setTimeout(() => setToast(null), 2000);
        setImportingFeedId(null);
        return;
      }
      
      // Insert the feed (only columns that exist in the schema)
      const { error } = await supabase.from("regulatory_feeds").insert({
        organization_id: orgId,
        name: feed.name,
        regulator_name: feed.regulator_name,
        regulator_acronym: feed.regulator_acronym,
        jurisdiction_code: feed.jurisdiction_code,
        url: feed.url,
        feed_type: feed.feed_type || 'rss',
        is_active: true,
      });
      
      if (error) {
        throw error;
      }
      
      setToast({ type: "success", message: `✅ Added ${feed.regulator_acronym} feed` });
      setTimeout(() => setToast(null), 3000);
      
      // Immediately mark as added for instant UI feedback
      setRecentlyAddedFeeds(prev => new Set([...prev, feed.url]));
      setRecentlyRemovedFeeds(prev => {
        const next = new Set(prev);
        next.delete(feed.url);
        return next;
      });
      
      // Reload feeds to update the UI
      await loadRegulatoryData();
      
    } catch (err) {
      console.error("Error importing feed:", err);
      setToast({ type: "error", message: `Failed to add ${feed.name}: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setImportingFeedId(null);
    }
  }
  
  // Check if a feed URL is already added to the database
  // Uses both DB state AND recent actions for instant UI feedback
  const isFeedAdded = (feedUrl) => {
    // If recently removed, show as not added (even if DB hasn't reloaded yet)
    if (recentlyRemovedFeeds.has(feedUrl)) return false;
    // If recently added, show as added (even if DB hasn't reloaded yet)
    if (recentlyAddedFeeds.has(feedUrl)) return true;
    // Otherwise check the database state
    return regulatoryFeeds.some(f => f.url === feedUrl);
  };
  
  // Get count of added feeds for debugging
  const addedFeedCount = regulatoryFeeds.length;
  
  // Get all available feeds sorted by jurisdiction and priority
  const getAvailableFeedsSorted = () => {
    // Sort: current jurisdiction first, then by priority, then alphabetically
    return getRSSFeeds().sort((a, b) => {
      // Current jurisdiction first
      if (a.jurisdiction_code === jurisdiction && b.jurisdiction_code !== jurisdiction) return -1;
      if (b.jurisdiction_code === jurisdiction && a.jurisdiction_code !== jurisdiction) return 1;
      // Then by priority (critical > high > medium)
      const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      }
      // Then alphabetically by regulator
      return a.regulator_acronym.localeCompare(b.regulator_acronym);
    });
  };
  
  // Remove a feed from the database
  async function removeFeed(feedUrl) {
    try {
      const feedToRemove = regulatoryFeeds.find(f => f.url === feedUrl);
      if (!feedToRemove) return;
      
      const { error } = await supabase
        .from("regulatory_feeds")
        .delete()
        .eq("id", feedToRemove.id);
      
      if (error) throw error;
      
      setToast({ type: "info", message: `Removed ${feedToRemove.regulator_acronym || feedToRemove.name} feed` });
      setTimeout(() => setToast(null), 2000);
      
      // Immediately mark as removed for instant UI feedback
      setRecentlyRemovedFeeds(prev => new Set([...prev, feedUrl]));
      setRecentlyAddedFeeds(prev => {
        const next = new Set(prev);
        next.delete(feedUrl);
        return next;
      });
      
      // Reload feeds
      await loadRegulatoryData();
    } catch (err) {
      console.error("Error removing feed:", err);
      setToast({ type: "error", message: `Failed to remove feed: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    }
  }
  
  // Legacy function for backward compatibility
  const getAvailableFeeds = getAvailableFeedsSorted;
  
  // Bulk import verified international RSS feeds
  async function bulkImportRSSFeeds() {
    const rssFeeds = getRSSFeeds(); // Only feeds with actual RSS URLs
    if (rssFeeds.length === 0) {
      setToast({ type: "error", message: "No RSS feeds to import" });
      setTimeout(() => setToast(null), 3000);
      return;
    }
    
    setBulkImportingFeeds(true);
    setFeedImportProgress({ total: rssFeeds.length, imported: 0, skipped: 0, errors: 0 });
    
    let imported = 0;
    let skipped = 0;
    let errors = 0;
    
    try {
      for (const feed of rssFeeds) {
        // Check if feed already exists (by URL or id)
        const { data: existing } = await supabase
          .from("regulatory_feeds")
          .select("id")
          .eq("url", feed.url)
          .maybeSingle();
        
        if (existing) {
          skipped++;
          setFeedImportProgress(p => ({ ...p, skipped }));
          continue;
        }
        
        // Insert the feed (only columns that exist in schema)
        const { error } = await supabase.from("regulatory_feeds").insert({
          organization_id: orgId,
          name: feed.name,
          regulator_name: feed.regulator_name,
          regulator_acronym: feed.regulator_acronym,
          jurisdiction_code: feed.jurisdiction_code,
          url: feed.url,
          feed_type: feed.feed_type || 'rss',
          is_active: true,
        });
        
        if (error) {
          if (error.code === "23505") {
            // Duplicate - already exists
            skipped++;
            setFeedImportProgress(p => ({ ...p, skipped }));
          } else {
            errors++;
            setFeedImportProgress(p => ({ ...p, errors }));
            console.error(`Error importing ${feed.name}:`, error);
          }
        } else {
          imported++;
          setFeedImportProgress(p => ({ ...p, imported }));
        }
      }
      
      setToast({ 
        type: "success", 
        message: `Imported ${imported} RSS feeds (${skipped} already existed, ${errors} errors)` 
      });
      setTimeout(() => setToast(null), 5000);
      
      // Reload feeds
      loadRegulatoryData();
      
      // Trigger an immediate poll of the new feeds
      if (imported > 0) {
        setToast({ type: "info", message: "Polling new feeds for regulatory updates..." });
        await callFn("poll-regulatory-feeds", { limit: imported + 5 });
        setToast({ type: "success", message: "Feed poll complete! Check Regulatory Updates for new items." });
        setTimeout(() => setToast(null), 5000);
        loadRegulatoryData();
      }
    } catch (err) {
      console.error("Bulk import error:", err);
      setToast({ type: "error", message: `Bulk import failed: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setBulkImportingFeeds(false);
      setFeedImportProgress(null);
    }
  }
  
  // Poll all active feeds manually
  async function pollAllFeeds() {
    setRegScraping(true);
    try {
      setToast({ type: "info", message: "Fetching updates from RSS feeds..." });
      const result = await callFn("poll-regulatory-feeds", { limit: 50 });
      
      if (result.total_inserted > 0) {
        setToast({ 
          type: "success", 
          message: `✓ Found ${result.total_inserted} new regulatory updates from ${result.feeds_polled} feeds!` 
        });
      } else if (result.feeds_polled > 0) {
        setToast({ 
          type: "info", 
          message: `Checked ${result.feeds_polled} feeds. No new updates (${result.total_duplicates || 0} duplicates skipped).` 
        });
      } else {
        setToast({ 
          type: "warning", 
          message: `No active RSS feeds found. Add feeds in "📡 RSS Feed Configuration" below, then try again.` 
        });
      }
      setTimeout(() => setToast(null), 6000);
      loadRegulatoryData();
    } catch (err) {
      console.error("Poll feeds error:", err);
      // More helpful error message
      let errorMsg = err.message || "Unknown error";
      if (errorMsg.includes("Failed to fetch") || errorMsg.includes("NetworkError")) {
        errorMsg = "Cannot connect to server. Make sure Supabase is running: supabase start";
      } else if (errorMsg.includes("FunctionsFetchError")) {
        errorMsg = "Edge functions not deployed. Run: supabase functions deploy poll-regulatory-feeds";
      }
      setToast({ type: "error", message: `Fetch failed: ${errorMsg}` });
      setTimeout(() => setToast(null), 8000);
    } finally {
      setRegScraping(false);
    }
  }
  
  // State for starter pack import
  const [showStarterPackModal, setShowStarterPackModal] = useState(false);
  const [importingStarterPack, setImportingStarterPack] = useState(false);
  
  // State for Feed Health Dashboard
  const [feedHealthData, setFeedHealthData] = useState([]);
  const [feedHealthLoading, setFeedHealthLoading] = useState(false);
  const [feedHealthStats, setFeedHealthStats] = useState({
    total: 0,
    healthy: 0,
    stale: 0,
    failed: 0,
    rssFeeds: 0,
    manualSources: 0,
    itemsLast7Days: 0,
    pendingReview: 0,
    adoptedLast7Days: 0,
    lastChecked: null,
  });
  const [selectedFeedDetail, setSelectedFeedDetail] = useState(null);
  const [showFeedHealthDashboard, setShowFeedHealthDashboard] = useState(false);
  
  // Alert settings state
  const [alertSettings, setAlertSettings] = useState({
    alert_email: "",
    alert_sms: "",
    sms_on_feed_failure: true,
    email_on_feed_failure: true,
    email_on_manual_overdue: true,
    email_on_every_item: false,
    daily_digest_enabled: true,
    daily_digest_hour: 6,
    consecutive_failures_threshold: 3,
    manual_overdue_days: 14,
  });
  const [alertSettingsLoading, setAlertSettingsLoading] = useState(false);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [showAlertSettings, setShowAlertSettings] = useState(false);
  
  // Import pre-extracted rules from starter pack into extracted rules for HITL review
  function loadStarterPackRules(packKey) {
    const pack = REGULATORY_STARTER_PACK[packKey];
    if (!pack) return;
    
    // Convert to extracted rules format with HITL fields
    const rulesForReview = pack.rules.map(rule => ({
      id: rule.id,
      rule_text: rule.rule_text,
      rule_type: rule.rule_type,
      citation: rule.citation,
      source_reference: rule.source_reference,
      effective_date: rule.effective_date,
      confidence_score: 0.95, // High confidence since human-reviewed
      isSelected: true,
      isEdited: false,
      isManual: false,
      eight_questions: rule.eight_questions,
      regulator: pack.metadata.regulator,
      jurisdiction_code: pack.metadata.jurisdiction_code,
      source_document: pack.metadata.title,
      source_url: pack.metadata.url,
    }));
    
    setExtractedRules(rulesForReview);
    setShowStarterPackModal(false);
    setToast({ 
      type: "success", 
      message: `Loaded ${rulesForReview.length} pre-extracted rules from ${pack.metadata.regulator}. Review and adopt below.` 
    });
    setTimeout(() => setToast(null), 5000);
  }
  
  // Calculate feed status based on last poll/check time
  function calculateFeedStatus(feed, pollHistory) {
    const lastPoll = pollHistory?.[0];
    if (!lastPoll) return { status: 'unknown', color: 'slate', label: 'Never Polled' };
    
    const hoursSinceLastPoll = (Date.now() - new Date(lastPoll.polled_at).getTime()) / (1000 * 60 * 60);
    const daysSinceLastPoll = hoursSinceLastPoll / 24;
    
    if (feed.feed_type === 'rss' || feed.feed_type === 'atom') {
      // RSS: 24h healthy, 24-72h stale, 72h+ failed
      if (lastPoll.status === 'success' && hoursSinceLastPoll < 24) {
        return { status: 'healthy', color: 'emerald', label: 'Healthy' };
      } else if (hoursSinceLastPoll < 72) {
        return { status: 'stale', color: 'amber', label: 'Stale' };
      } else {
        return { status: 'failed', color: 'red', label: 'Failed' };
      }
    } else {
      // Manual: 14d healthy, 14-30d stale, 30d+ failed
      if (daysSinceLastPoll < 14) {
        return { status: 'healthy', color: 'emerald', label: 'Checked' };
      } else if (daysSinceLastPoll < 30) {
        return { status: 'stale', color: 'amber', label: 'Overdue' };
      } else {
        return { status: 'failed', color: 'red', label: 'Critical' };
      }
    }
  }
  
  // Load feed health data
  async function loadFeedHealthData() {
    setFeedHealthLoading(true);
    try {
      // Load all feeds
      const { data: feeds, error: feedsErr } = await supabase
        .from("regulatory_feeds")
        .select("*")
        .order("name");
      
      if (feedsErr) throw feedsErr;
      
      // Load poll history for each feed (last 10 polls)
      const feedsWithHistory = await Promise.all((feeds || []).map(async (feed) => {
        const { data: history } = await supabase
          .from("feed_poll_log")
          .select("*")
          .eq("feed_id", feed.id)
          .order("poll_started_at", { ascending: false })
          .limit(10);
        
        const status = calculateFeedStatus(feed, history);
        return {
          ...feed,
          pollHistory: history || [],
          ...status,
        };
      }));
      
      setFeedHealthData(feedsWithHistory);
      
      // Calculate stats
      const stats = {
        total: feedsWithHistory.length,
        healthy: feedsWithHistory.filter(f => f.status === 'healthy').length,
        stale: feedsWithHistory.filter(f => f.status === 'stale').length,
        failed: feedsWithHistory.filter(f => f.status === 'failed' || f.status === 'unknown').length,
        rssFeeds: feedsWithHistory.filter(f => f.feed_type === 'rss' || f.feed_type === 'atom').length,
        manualSources: feedsWithHistory.filter(f => f.feed_type === 'manual' || f.feed_type === 'scrape').length,
        lastChecked: new Date(),
      };
      
      // Get items ingested in last 7 days
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const { count: itemsCount } = await supabase
        .from("regulatory_updates")
        .select("*", { count: "exact", head: true })
        .gte("created_at", sevenDaysAgo.toISOString());
      stats.itemsLast7Days = itemsCount || 0;
      
      // Get pending review count
      const { count: pendingCount } = await supabase
        .from("regulatory_updates")
        .select("*", { count: "exact", head: true })
        .is("review_status", null);
      stats.pendingReview = pendingCount || 0;
      
      // Get adopted rules in last 7 days
      const { count: adoptedCount } = await supabase
        .from("policy_rules")
        .select("*", { count: "exact", head: true })
        .gte("created_at", sevenDaysAgo.toISOString());
      stats.adoptedLast7Days = adoptedCount || 0;
      
      setFeedHealthStats(stats);
    } catch (err) {
      console.error("Load feed health error:", err);
      setToast({ type: "error", message: `Failed to load feed health: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setFeedHealthLoading(false);
    }
  }
  
  // Mark manual source as checked
  async function markManualSourceChecked(feedId) {
    try {
      // Insert a poll log entry for manual check
      const { error } = await supabase.from("feed_poll_log").insert({
        feed_id: feedId,
        poll_started_at: new Date().toISOString(),
        poll_completed_at: new Date().toISOString(),
        items_fetched: 0,
        items_new: 0,
        items_duplicate: 0,
        success: true,
        duration_ms: 0,
      });
      
      if (error) throw error;
      
      setToast({ type: "success", message: "Manual source marked as checked" });
      setTimeout(() => setToast(null), 3000);
      loadFeedHealthData();
    } catch (err) {
      console.error("Mark checked error:", err);
      setToast({ type: "error", message: `Failed to mark as checked: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    }
  }
  
  // Load alert settings
  async function loadAlertSettings() {
    try {
      const { data, error } = await supabase
        .from("alert_settings")
        .select("*")
        .eq("org_id", orgId)
        .single();
      
      if (data && !error) {
        setAlertSettings({
          alert_email: data.alert_email || "",
          alert_sms: data.alert_sms || "",
          sms_on_feed_failure: data.sms_on_feed_failure ?? true,
          email_on_feed_failure: data.email_on_feed_failure ?? true,
          email_on_manual_overdue: data.email_on_manual_overdue ?? true,
          email_on_every_item: data.email_on_every_item ?? false,
          daily_digest_enabled: data.daily_digest_enabled ?? true,
          daily_digest_hour: data.daily_digest_hour ?? 6,
          consecutive_failures_threshold: data.consecutive_failures_threshold ?? 3,
          manual_overdue_days: data.manual_overdue_days ?? 14,
        });
      }
    } catch (err) {
      console.error("Load alert settings error:", err);
    }
  }
  
  // Save alert settings
  async function saveAlertSettings() {
    setAlertSettingsLoading(true);
    try {
      const { error } = await supabase
        .from("alert_settings")
        .upsert({
          org_id: orgId,
          ...alertSettings,
          updated_at: new Date().toISOString(),
        }, { onConflict: "org_id" });
      
      if (error) throw error;
      
      setToast({ type: "success", message: "Alert settings saved successfully" });
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      console.error("Save alert settings error:", err);
      setToast({ type: "error", message: `Failed to save: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setAlertSettingsLoading(false);
    }
  }
  
  // Load active alerts
  async function loadActiveAlerts() {
    try {
      const { data, error } = await supabase
        .from("feed_alerts")
        .select(`
          *,
          regulatory_feeds (name, regulator_acronym, jurisdiction_code)
        `)
        .is("resolved_at", null)
        .order("triggered_at", { ascending: false });
      
      if (!error) {
        setActiveAlerts(data || []);
      }
    } catch (err) {
      console.error("Load alerts error:", err);
    }
  }
  
  // Acknowledge an alert
  async function acknowledgeAlert(alertId) {
    try {
      const { error } = await supabase
        .from("feed_alerts")
        .update({
          acknowledged_at: new Date().toISOString(),
          acknowledged_by: user?.id,
        })
        .eq("id", alertId);
      
      if (error) throw error;
      
      setToast({ type: "success", message: "Alert acknowledged" });
      setTimeout(() => setToast(null), 3000);
      loadActiveAlerts();
    } catch (err) {
      setToast({ type: "error", message: `Failed: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    }
  }
  
  // Resolve an alert
  async function resolveAlert(alertId, note = "") {
    try {
      const { error } = await supabase
        .from("feed_alerts")
        .update({
          resolved_at: new Date().toISOString(),
          resolved_by: user?.id,
          resolution_note: note || "Manually resolved",
        })
        .eq("id", alertId);
      
      if (error) throw error;
      
      setToast({ type: "success", message: "Alert resolved" });
      setTimeout(() => setToast(null), 3000);
      loadActiveAlerts();
      loadFeedHealthData();
    } catch (err) {
      setToast({ type: "error", message: `Failed: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    }
  }
  
  // Test a single feed
  async function testFeed(feed) {
    setToast({ type: "info", message: `Testing ${feed.name}...` });
    try {
      const result = await callFn("poll-regulatory-feeds", { 
        feed_id: feed.id,
        limit: 1 
      });
      
      if (result.total_inserted > 0) {
        setToast({ type: "success", message: `Feed working! Found ${result.total_inserted} new items.` });
      } else {
        setToast({ type: "success", message: `Feed working! No new items (${result.total_duplicates || 0} duplicates).` });
      }
      setTimeout(() => setToast(null), 5000);
      loadFeedHealthData();
    } catch (err) {
      console.error("Test feed error:", err);
      setToast({ type: "error", message: `Feed test failed: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    }
  }

  // Import starter pack rules directly to policy manual
  async function importStarterPackToPolicyManual(packKey) {
    const pack = REGULATORY_STARTER_PACK[packKey];
    if (!pack) return;
    
    setImportingStarterPack(true);
    let imported = 0;
    let failed = 0;
    
    try {
      for (const rule of pack.rules) {
        const { error } = await supabase.from("policy_rules").insert({
          organization_id: orgId,
          rule_text: rule.rule_text,
          rule_type: rule.rule_type,
          citation: rule.citation,
          source_reference: rule.source_reference,
          effective_date: rule.effective_date,
          rationale: `Extracted from ${pack.metadata.title} (${pack.metadata.regulator})`,
          eight_questions: rule.eight_questions,
          status: "active",
          created_by: session?.user?.id,
        });
        
        if (error) {
          if (error.code !== "23505") { // Ignore duplicates
            failed++;
            console.error("Import error:", error);
          }
        } else {
          imported++;
        }
      }
      
      setToast({ 
        type: "success", 
        message: `Imported ${imported} rules to Policy Manual${failed > 0 ? ` (${failed} failed)` : ""}` 
      });
      setTimeout(() => setToast(null), 5000);
      loadPolicyManual();
    } catch (err) {
      console.error("Starter pack import error:", err);
      setToast({ type: "error", message: `Import failed: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setImportingStarterPack(false);
      setShowStarterPackModal(false);
    }
  }
  
  // Load regulatory compliance stats
  async function loadRegComplianceStats() {
    try {
      const { data, error } = await supabase.rpc("get_regulatory_compliance_stats");
      if (error) throw error;
      setRegComplianceStats(data);
    } catch (err) {
      console.error("Load compliance stats error:", err);
    }
  }
  
  // Load regulatory review decisions
  async function loadRegDecisions() {
    try {
      const { data, error } = await supabase
        .from("regulatory_review_decisions")
        .select("*")
        .order("decided_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      setRegDecisions(data || []);
    } catch (err) {
      console.error("Load decisions error:", err);
    }
  }
  
  // Unsnooze items that have passed their snooze date
  async function unsnoozeRegUpdates() {
    try {
      const { data, error } = await supabase.rpc("unsnooze_regulatory_updates");
      if (error) throw error;
      if (data > 0) {
        setToast({ type: "info", message: `${data} snoozed item(s) returned to inbox` });
        setTimeout(() => setToast(null), 3000);
        loadRegulatoryData();
      }
    } catch (err) {
      console.error("Unsnooze error:", err);
    }
  }
  
  // Open review modal for a regulatory update
  async function openRegReviewModal(update) {
    setRegReviewModal({ open: true, update });
    setRegAISummary(null);
    setRegDeclineReason("");
    setRegSelectedObjects([]);
    setRegShowOriginal(false);
    setExtractedRules([]); // Reset extracted rules for new update
    setEditingRuleId(null);
    setEditingRuleText("");
    
    // Check if AI summary already exists
    if (update.ai_summary && update.ai_recommendation) {
      setRegAISummary({
        summary: update.ai_summary,
        recommendation: update.ai_recommendation,
        affected_domains: update.ai_affected_domains || [],
      });
    } else {
      // Fetch AI summary
      setRegReviewLoading(true);
      try {
        const result = await callFn("summarize-regulatory", { update_id: update.id });
        if (result.summary) {
          setRegAISummary({
            summary: result.summary,
            recommendation: result.recommendation,
            affected_domains: result.affected_domains || [],
            key_takeaways: result.key_takeaways || [],
            urgency_level: result.urgency_level || "medium",
          });
          // Update local state with AI data
          setRegulatoryUpdates(prev => prev.map(u => 
            u.id === update.id ? { ...u, ai_summary: result.summary, ai_recommendation: result.recommendation, ai_affected_domains: result.affected_domains } : u
          ));
        }
      } catch (err) {
        console.error("AI summary error:", err);
        setToast({ type: "error", message: `Failed to get AI summary: ${sanitizeErrorMessage(err)}` });
        setTimeout(() => setToast(null), 5000);
      } finally {
        setRegReviewLoading(false);
      }
    }
  }
  
  // Record a regulatory review decision
  async function recordRegDecision(decision, snoozeDays = 30) {
    if (!regReviewModal.update) return;
    
    // Validate decline has reason
    if (decision === "declined" && !regDeclineReason.trim()) {
      setToast({ type: "error", message: "Please provide a reason for declining" });
      setTimeout(() => setToast(null), 3000);
      return;
    }
    
    // Validate adopt has selected objects
    if (decision === "adopted" && regSelectedObjects.length === 0) {
      setToast({ type: "error", message: "Please select which governance objects to update" });
      setTimeout(() => setToast(null), 3000);
      return;
    }
    
    setRegReviewLoading(true);
    try {
      const { data, error } = await supabase.rpc("record_regulatory_decision", {
        p_update_id: regReviewModal.update.id,
        p_decision: decision,
        p_decline_reason: decision === "declined" ? regDeclineReason : null,
        p_affected_object_ids: decision === "adopted" ? regSelectedObjects.map(o => o.id) : null,
        p_affected_object_types: decision === "adopted" ? regSelectedObjects.map(o => o.type) : null,
        p_snooze_days: decision === "snoozed" ? snoozeDays : null,
      });
      
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Unknown error");
      
      setToast({ 
        type: "success", 
        message: decision === "adopted" ? "Update adopted and governance objects updated" :
                 decision === "declined" ? "Update declined with documented reason" :
                 `Update snoozed for ${snoozeDays} days`
      });
      setTimeout(() => setToast(null), 3000);
      
      setRegReviewModal({ open: false, update: null });
      loadRegulatoryData();
      loadRegComplianceStats();
      loadRegDecisions();
    } catch (err) {
      console.error("Record decision error:", err);
      setToast({ type: "error", message: `Failed to record decision: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setRegReviewLoading(false);
    }
  }
  
  // Extract rules from a regulatory update using AI
  async function extractRulesFromUpdate() {
    if (!regReviewModal.update) return;
    
    setExtractingRules(true);
    setExtractedRules([]);
    
    try {
      const result = await callFn("extract-rules", {
        update_id: regReviewModal.update.id,
        organization_id: orgId,
        source_text: regReviewModal.update.summary || regReviewModal.update.title,
      });
      
      if (result.rules && result.rules.length > 0) {
        // Add selection state to each rule
        setExtractedRules(result.rules.map((r, idx) => ({
          ...r,
          id: result.saved_ids?.[idx] || `temp-${idx}`,
          isSelected: true,
          isEditing: false,
        })));
        setToast({ type: "success", message: `Extracted ${result.rules.length} actionable rules` });
      } else {
        setToast({ type: "error", message: "No actionable rules found in this update" });
      }
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      console.error("Extract rules error:", err);
      setToast({ type: "error", message: `Failed to extract rules: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setExtractingRules(false);
    }
  }
  
  // Toggle rule selection
  function toggleRuleSelection(ruleId) {
    setExtractedRules(prev => prev.map(r => 
      r.id === ruleId ? { ...r, isSelected: !r.isSelected } : r
    ));
  }
  
  // Start editing a rule
  function startEditingRule(rule) {
    setEditingRuleId(rule.id);
    setEditingRuleText(rule.rule_text);
  }
  
  // Save rule edit
  function saveRuleEdit() {
    setExtractedRules(prev => prev.map(r => 
      r.id === editingRuleId ? { ...r, rule_text: editingRuleText } : r
    ));
    setEditingRuleId(null);
    setEditingRuleText("");
  }
  
  // Adopt selected extracted rules
  async function adoptSelectedRules() {
    const selectedRules = extractedRules.filter(r => r.isSelected);
    if (selectedRules.length === 0) {
      setToast({ type: "error", message: "Please select at least one rule to adopt" });
      setTimeout(() => setToast(null), 3000);
      return;
    }
    
    setAdoptingRules(true);
    let adopted = 0;
    let failed = 0;
    
    try {
      for (const rule of selectedRules) {
        if (rule.id.startsWith("temp-")) continue; // Skip unsaved rules
        
        const { data, error } = await supabase.rpc("adopt_extracted_rule", {
          p_extracted_rule_id: rule.id,
          p_rule_text: rule.rule_text,
          p_rationale: `Extracted from: ${regReviewModal.update?.title || "Unknown"}`,
          p_notes: null,
          p_linked_scenario_ids: regSelectedObjects.filter(o => o.type === "scenario").map(o => o.id),
          p_linked_threshold_ids: regSelectedObjects.filter(o => o.type === "threshold").map(o => o.id),
        });
        
        if (error) {
          console.error("Adopt rule error:", error);
          failed++;
        } else {
          adopted++;
        }
      }
      
      if (adopted > 0) {
        setToast({ 
          type: "success", 
          message: `Adopted ${adopted} rule(s) to Policy Manual${failed > 0 ? ` (${failed} failed)` : ""}` 
        });
        // Clear adopted rules from list
        setExtractedRules(prev => prev.filter(r => !r.isSelected || r.id.startsWith("temp-")));
        // Refresh policy manual
        loadPolicyManual();
      } else {
        setToast({ type: "error", message: "Failed to adopt rules" });
      }
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      console.error("Adopt rules error:", err);
      setToast({ type: "error", message: `Failed to adopt rules: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setAdoptingRules(false);
    }
  }
  
  // Load policy manual (adopted rules)
  async function loadPolicyManual() {
    if (!orgId) return;
    
    setPolicyManualLoading(true);
    try {
      const { data, error } = await supabase.rpc("get_policy_manual", { p_org: orgId });
      
      if (error) throw error;
      setPolicyRules(data || []);
    } catch (err) {
      console.error("Load policy manual error:", err);
      setToast({ type: "error", message: `Failed to load policy manual: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setPolicyManualLoading(false);
    }
  }
  
  // Export policy manual as professional Word document (.docx)
  async function exportPolicyManual() {
    if (policyRules.length === 0) {
      setToast({ type: "error", message: "No rules to export" });
      setTimeout(() => setToast(null), 3000);
      return;
    }
    
    setPolicyManualLoading(true);
    try {
      // Call edge function to generate professional Word document
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      const response = await fetch(`${SUPABASE_URL}/functions/v1/export-policy-manual`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "apikey": SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ organization_id: orgId }),
      });
      
      if (!response.ok) {
        const errData = await response.json().catch(() => ({ error: "Failed to generate document" }));
        throw new Error(errData.error || `HTTP ${response.status}`);
      }
      
      // Get the blob and download
      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition") || "";
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `Policy_Manual_${new Date().toISOString().slice(0, 10)}.docx`;
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setToast({ type: "success", message: "Policy manual exported as Word document (.docx)" });
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      console.error("Export error:", err);
      setToast({ type: "error", message: `Export failed: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setPolicyManualLoading(false);
    }
  }
  
  // Load regulatory data when switching to that tab OR when vertical changes
  useEffect(() => {
    if (activeTab === "regulatory" && orgId) {
      loadRegulatoryData();
      loadRegComplianceStats();
      loadRegDecisions();
      unsnoozeRegUpdates(); // Check for items to unsnooze
      loadPolicyManual(); // Also load policy manual
    }
  }, [activeTab, orgId, vertical]); // Added vertical - reloads when demo vertical changes

  async function callFn(name, body, isForm = false) {
    const url = `${SUPABASE_URL}/functions/v1/${name}`;
    
    // Get fresh session
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    const token = currentSession?.access_token;
    
    
    if (!token) {
      throw new Error("Not authenticated - please sign in again");
    }
    
    const headers = {
      Authorization: `Bearer ${token}`,
      apikey: SUPABASE_ANON_KEY,
    };
    if (!isForm) {
      headers["Content-Type"] = "application/json";
    }
    const res = await fetch(url, {
      method: "POST",
      headers,
      body: isForm ? body : JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
    return json;
  }

  async function reviewImportedRule(ruleId, action) {
    try {
      setReviewingRuleId(ruleId);
      
      const { data, error } = await supabase.rpc("review_imported_rule", {
        p_org: orgId,
        p_rule_id: ruleId,
        p_action: action,
      });
      
      
      if (error) {
        throw new Error(error.message || "RPC failed");
      }
      
      setToast({ type: 'success', message: `Rule ${action}ed successfully` });
      setTimeout(() => setToast(null), 3000);
      loadAllData();
    } catch (err) {
      console.error("Review rule error:", err);
      setToast({ type: 'error', message: `Failed to ${action} rule: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setReviewingRuleId(null);
    }
  }

  const [downloadingReceipt, setDownloadingReceipt] = useState(null); // U-3 FIX

  async function downloadReceipt(attestation) {
    // v8.2.9: Receipts are generated inline with attestation
    // Use storage signed URL to download
    if (!attestation.receipt_path) {
      setToast({ type: 'error', message: 'No receipt available. Receipts are generated automatically for new attestations.' });
      setTimeout(() => setToast(null), 4000);
      return;
    }
    setDownloadingReceipt(attestation.id);
    try {
      const { data, error } = await supabase.storage
        .from("examiner-packages")
        .createSignedUrl(attestation.receipt_path, 60 * 60); // 1 hour
      if (error) throw error;
      if (data?.signedUrl) {
        window.open(data.signedUrl, "_blank");
      }
    } catch (err) {
      console.error("Download receipt error:", err);
      setToast({ type: 'error', message: 'Failed to download receipt. Please try again.' });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setDownloadingReceipt(null);
    }
  }

  // Phase 2: Comprehensive attestation readiness calculation
  // Now includes CRITICAL/HIGH regulatory updates that must be reviewed
  // v9.0: Now includes 70/100 quality score threshold
  // v9.1: Now includes regulatory coverage gap analysis
  const attestationReadiness = useMemo(() => {
    const baseReadiness = calculateAttestationBlockers(scenarios, thresholds, docReadinessScore);
    let currentReadiness = { ...baseReadiness };
    
    // Check for unreviewed CRITICAL/HIGH regulatory updates
    const unreviewedCriticalHigh = regulatoryUpdates.filter(u => 
      (u.impact_guess === "critical" || u.impact_guess === "high") &&
      (u.review_status === "pending" || !u.review_status)
    );
    
    if (unreviewedCriticalHigh.length > 0) {
      // Add regulatory blockers
      const regBlockers = unreviewedCriticalHigh.map(u => ({
        type: "Regulatory Update",
        item: u,
        isBlocking: true,
        hasWarnings: false,
        eqScore: 0,
        blockers: [{
          blockerType: { id: "regulatory", label: "Unreviewed Regulatory Update", color: "red" },
          reason: `${u.impact_guess?.toUpperCase()} impact regulatory update requires review`,
          details: [u.title],
        }],
      }));
      
      currentReadiness = {
        ...currentReadiness,
        canAttest: false,
        blockingItems: [...currentReadiness.blockingItems, ...regBlockers],
        blockerSummary: [
          ...currentReadiness.blockerSummary,
          {
            id: "regulatory",
            label: "Unreviewed Regulatory Updates",
            color: "red",
            count: unreviewedCriticalHigh.length,
            items: unreviewedCriticalHigh,
          },
        ],
      };
    }
    
    // Check for regulatory coverage gap (if gap analysis has been run)
    if (gapData && gapData.attestation_blocked) {
      const coverageBlocker = {
        type: "Coverage Gap",
        item: { 
          id: "coverage-gap", 
          name: `Regulatory Coverage: ${gapData.coverage.percent}%`,
          coverage: gapData.coverage
        },
        isBlocking: true,
        hasWarnings: false,
        eqScore: 0,
        blockers: [{
          blockerType: { id: "coverage", label: "Insufficient Regulatory Coverage", color: "red" },
          reason: `Coverage is ${gapData.coverage.percent}%, minimum ${gapData.coverage_threshold}% required`,
          details: [`Address ${gapData.requirements_to_unlock} more requirements in Gap Analysis`],
        }],
      };
      
      currentReadiness = {
        ...currentReadiness,
        canAttest: false,
        blockingItems: [...currentReadiness.blockingItems, coverageBlocker],
        blockerSummary: [
          ...currentReadiness.blockerSummary,
          {
            id: "coverage",
            label: "Coverage Gap",
            color: "red",
            count: gapData.coverage.gaps_count,
            items: gapData.gaps?.slice(0, 5) || [],
          },
        ],
      };
    }
    
    return currentReadiness;
  }, [scenarios, thresholds, regulatoryUpdates, docReadinessScore, gapData]);
  
  // Legacy blockers array for backward compatibility
  const blockers = useMemo(() => {
    return attestationReadiness.blockingItems.map(item => ({
      type: item.type,
      id: item.item.id,
      name: item.item.name,
      score: item.eqScore,
      missing: item.blockers.flatMap(b => b.details),
      ...item.item, // Include all item fields for EQScoreBadge
    }));
  }, [attestationReadiness]);

  const filteredDocuments = useMemo(() => {
    return documents.filter(doc => {
      if (docFilters.type !== "all" && doc.document_type !== docFilters.type) return false;
      if (docFilters.integrity !== "all" && doc.integrity_status !== docFilters.integrity) return false;
      if (docFilters.status !== "all" && doc.status !== docFilters.status) return false;
      if (docFilters.question !== "all") {
        const links = documentLinks.filter(l => l.document_id === doc.id);
        if (!links.some(l => (l.linked_question || "").toUpperCase() === docFilters.question)) return false;
      }
      return true;
    });
  }, [documents, documentLinks, docFilters]);

  async function handleAttest() {
    await beginAttest();
  }

  async function verifyAuditChain() {
    setChainStatus({ loading: true });
    try {
      const { data } = await supabase.rpc("verify_audit_chain", { p_org: orgId });
      setChainStatus({ ...data, loading: false });
    } catch (e) {
      setChainStatus({ pass: false, error: e.message, loading: false });
    }
  }

  async function dismissNotification(id) {
    try {
      const { error } = await supabase.from("notification_queue").update({ status: "dismissed" }).eq("id", id);
      if (error) throw error;
      setNotifications(n => n.filter(x => x.id !== id));
    } catch (err) {
      console.error("Dismiss notification error:", err);
      setToast({ type: "error", message: "Failed to dismiss notification." });
      setTimeout(() => setToast(null), 4000);
    }
  }

  function resetScenarioForm() {
    setScenarioForm({
      name: "",
      owner_email: session?.user?.email || "",
      description: "",
      regulatory_basis: "",
      alternatives_considered: "",
      documentation_location: "",
      authority_citation: "",
      evidence_summary: "",
      next_review_date: "",
      review_frequency: "",
      risk_level: "medium",
    });
  }

  function resetThresholdForm() {
    setThresholdForm({
      name: "",
      owner_email: session?.user?.email || "",
      description: "",
      current_value: "",
      unit: "",
      regulatory_basis: "",
      alternatives_considered: "",
      documentation_location: "",
      authority_citation: "",
      evidence_summary: "",
      next_review_date: "",
      review_frequency: "",
      risk_level: "medium",
    });
  }

  function getRigorKey(type, id) {
    return `${type}:${id}`;
  }

  function getRigorBadge(result) {
    if (!result?.overall_score) return <span className="text-xs text-slate-500">—</span>;
    const score = result.overall_score;
    const color = score === 3 ? "emerald" : score === 2 ? "amber" : "red";
    const label = score === 3 ? "Exam Ready" : score === 2 ? "Needs Work" : "Fails";
    return <Badge color={color}>{label}</Badge>;
  }

  function buildRigorRecord(item) {
    return {
      name: item.name || "",
      description: item.description || "",
      regulatory_basis: item.regulatory_basis || "",
      authority_citation: item.authority_citation || "",
      evidence_summary: item.evidence_summary || "",
      alternatives_considered: item.alternatives_considered || "",
      documentation_location: item.documentation_location || "",
    };
  }

  async function runRigorCheck(type, item, { openModal = true } = {}) {
    const key = getRigorKey(type, item.id);
    try {
      setRigorCheckingKey(key);
      const result = await callFn("check-rigor", { type, record: buildRigorRecord(item) });
      setRigorResults(prev => ({ ...prev, [key]: { ...result, checked_at: new Date().toISOString() } }));
      if (openModal) {
        setRigorModal({ open: true, key, name: item.name || "Untitled", result });
      }
      return result;
    } catch (err) {
      console.error("Rigor check error:", err);
      setToast({ type: "error", message: `Rigor check failed: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
      return null;
    } finally {
      setRigorCheckingKey(null);
    }
  }

  async function runRigorBatchCheck() {
    setRigorBatchRunning(true);
    try {
      const allItems = [
        ...scenarios.map(item => ({ type: "scenario", item })),
        ...thresholds.map(item => ({ type: "threshold", item })),
      ];
      let blocked = [];
      let warning = [];
      for (const entry of allItems) {
        const result = await runRigorCheck(entry.type, entry.item, { openModal: false });
        if (!result) continue;
        if (result.overall_score === 1) blocked.push(entry);
        if (result.overall_score === 2) warning.push(entry);
      }
      return { blocked, warning, total: allItems.length };
    } finally {
      setRigorBatchRunning(false);
    }
  }

  async function performAttest() {
    setAttestResult(null);
    try {
      const result = await callFn("attest", { organization_id: orgId, vertical, attester_name: session?.user?.email || "" });
      setAttestResult({ ok: true, data: result });
      loadAllData();
    } catch (e) {
      setAttestResult({ ok: false, error: e.message });
    }
  }

  async function beginAttest() {
    setAttestRigorWarning(null);
    setAttestRigorBlocked(null);
    setAttestRigorLoading(true);
    try {
      const summary = await runRigorBatchCheck();
      if (summary.blocked.length > 0) {
        setAttestRigorBlocked(summary);
        return;
      }
      if (summary.warning.length > 0) {
        setAttestRigorWarning(summary);
        return;
      }
      await performAttest();
    } finally {
      setAttestRigorLoading(false);
    }
  }
  function openScenarioCreate() {
    setScenarioEditingId(null);
    resetScenarioForm();
    setScenarioModal(true);
  }

  function openScenarioEdit(scenario) {
    setScenarioEditingId(scenario.id);
    setScenarioForm({
      name: scenario.name || "",
      owner_email: scenario.owner_email || "",
      description: scenario.description || "",
      regulatory_basis: scenario.regulatory_basis || "",
      alternatives_considered: scenario.alternatives_considered || "",
      documentation_location: scenario.documentation_location || "",
      authority_citation: scenario.authority_citation || "",
      evidence_summary: scenario.evidence_summary || "",
      next_review_date: scenario.next_review_date || "",
      review_frequency: scenario.review_frequency || "",
      risk_level: scenario.risk_level || "medium",
    });
    setScenarioModal(true);
  }

  // Threshold templates for pre-filling
  const THRESHOLD_TEMPLATES = {
    cash_structuring: {
      name: "Cash Structuring Detection Threshold",
      description: "Threshold for detecting potential cash structuring activity to avoid CTR filing requirements.",
      current_value: "9500",
      unit: "USD (rolling 24-hour period)",
      regulatory_basis: "Per 31 CFR 1010.314, we must detect transactions structured to evade CTR requirements. Analysis of our cash deposit patterns shows average amounts of $X, supporting this threshold.",
      alternatives_considered: "Considered: (1) $9,000 - too many false positives, (2) $9,800 - too narrow, (3) $9,500 - balances detection with operational efficiency.",
      risk_level: "high",
    },
    wire_monitoring: {
      name: "Wire Transfer Alert Threshold",
      description: "Threshold triggering enhanced review for wire transfers based on amount and risk factors.",
      current_value: "5000",
      unit: "USD per transaction",
      regulatory_basis: "Per FinCEN guidance and FATF Recommendation 16, wire transfers require monitoring. Our customer base analysis supports this threshold based on average legitimate wire amounts.",
      alternatives_considered: "Considered: (1) $3,000 per FATF, (2) $10,000 aligned with CTR, (3) $5,000 based on customer patterns.",
      risk_level: "medium",
    },
    wire_volume: {
      name: "Wire Volume Alert Threshold",
      description: "Threshold for unusual wire transfer volume triggering enhanced review.",
      current_value: "10",
      unit: "transactions per rolling 7 days",
      regulatory_basis: "Based on customer behavior analysis, normal accounts average X wire transfers per week. Threshold set to identify outliers for review.",
      alternatives_considered: "Considered: (1) 5 per week - too restrictive, (2) 15 per week - misses anomalies, (3) 10 per week - optimal based on analysis.",
      risk_level: "medium",
    },
    cash_intensive: {
      name: "Cash-Intensive Business Threshold",
      description: "Enhanced monitoring threshold for accounts identified as cash-intensive businesses.",
      current_value: "80",
      unit: "% cash deposits vs. total deposits (monthly)",
      regulatory_basis: "Per FinCEN Advisory FIN-2020-A007, cash-intensive businesses require enhanced monitoring. This threshold identifies accounts for additional scrutiny.",
      alternatives_considered: "Considered: (1) 50% - too broad, (2) 90% - too narrow, (3) 80% - captures high-risk while manageable volume.",
      risk_level: "high",
    },
    rapid_movement: {
      name: "Rapid Movement Threshold",
      description: "Threshold for detecting rapid movement of funds indicative of potential suspicious activity.",
      current_value: "72",
      unit: "hours (funds in/out)",
      regulatory_basis: "Per OCC risk management guidance, rapid movement of funds can indicate layering. This threshold flags accounts where deposits are moved within 72 hours.",
      alternatives_considered: "Considered: (1) 24 hours - too narrow, (2) 7 days - too broad, (3) 72 hours - balances detection with legitimate business needs.",
      risk_level: "high",
    },
  };

  // GRC Module Templates
  const CONTROL_TEMPLATES = {
    transaction_monitoring: {
      name: "Transaction Monitoring Control",
      control_type: "detective",
      control_category: "operational",
      description: "Automated monitoring of transactions against established thresholds and patterns to detect potentially suspicious activity.",
      frequency: "continuous",
      regulatory_basis: "Per 31 CFR 1020.210 and OCC Bulletin 2020-8, AML programs must include transaction monitoring. This control satisfies the monitoring requirement.",
    },
    ctr_accuracy: {
      name: "CTR Filing Accuracy Review",
      control_type: "detective",
      control_category: "operational",
      description: "Daily review of CTR filings for accuracy and completeness before submission to FinCEN.",
      frequency: "daily",
      regulatory_basis: "Per 31 CFR 1010.311, CTRs must be accurate and complete. This control ensures filing quality and reduces amendments.",
    },
    cdd_refresh: {
      name: "CDD Refresh Control",
      control_type: "preventive",
      control_category: "operational",
      description: "Periodic refresh of customer due diligence information based on risk tier: high-risk annually, medium-risk every 2 years, low-risk every 3 years.",
      frequency: "quarterly",
      regulatory_basis: "Per 31 CFR 1010.230(d), ongoing CDD must be performed. This control ensures customer information remains current.",
    },
    access_review: {
      name: "System Access Review",
      control_type: "preventive",
      control_category: "technical",
      description: "Quarterly review of user access to AML/BSA systems to ensure principle of least privilege and appropriate segregation of duties.",
      frequency: "quarterly",
      regulatory_basis: "Per FFIEC IT Examination Handbook and OCC guidance on operational risk, access controls must be reviewed regularly.",
    },
  };

  const RISK_TEMPLATES = {
    wire_fraud: {
      name: "Wire Transfer Fraud Risk",
      risk_category: "operational",
      description: "Risk of fraudulent wire transfers due to business email compromise, social engineering, or insider threat.",
      inherent_likelihood: 4,
      inherent_impact: 5,
      residual_likelihood: 2,
      residual_impact: 4,
      risk_treatment: "mitigate",
      regulatory_basis: "Per FinCEN Advisory FIN-2019-A005 and FBI IC3 data, BEC/wire fraud remains a significant threat requiring robust controls.",
    },
    tprm: {
      name: "Third-Party Risk",
      risk_category: "operational",
      description: "Risk arising from reliance on third-party service providers for critical AML/BSA functions including transaction monitoring and screening.",
      inherent_likelihood: 3,
      inherent_impact: 4,
      residual_likelihood: 2,
      residual_impact: 3,
      risk_treatment: "mitigate",
      regulatory_basis: "Per OCC Bulletin 2013-29 and FFIEC guidance, third-party relationships must be risk-managed with appropriate oversight.",
    },
    reg_compliance: {
      name: "Regulatory Non-Compliance Risk",
      risk_category: "compliance",
      description: "Risk of regulatory findings, enforcement actions, or penalties due to gaps in AML/BSA program implementation or documentation.",
      inherent_likelihood: 3,
      inherent_impact: 5,
      residual_likelihood: 2,
      residual_impact: 4,
      risk_treatment: "mitigate",
      regulatory_basis: "Based on recent OCC and FinCEN enforcement trends, documentation and testing gaps are leading causes of findings.",
    },
  };

  const POLICY_TEMPLATES = {
    bsa_aml: {
      name: "BSA/AML Compliance Policy",
      policy_type: "policy",
      category: "compliance",
      description: "Comprehensive policy establishing the institution's Bank Secrecy Act and Anti-Money Laundering compliance program framework.",
      version: "1.0",
      status: "draft",
      regulatory_basis: "Per 31 CFR 1010.210, covered financial institutions must establish AML programs. This policy satisfies the program requirement.",
    },
    cip: {
      name: "Customer Identification Program Policy",
      policy_type: "policy",
      category: "compliance",
      description: "Policy establishing procedures for customer identification and verification at account opening as required by Section 326 of the USA PATRIOT Act.",
      version: "1.0",
      status: "draft",
      regulatory_basis: "Per 31 CFR 1020.220, CIP must include risk-based verification procedures. This policy documents our approach.",
    },
    sar_policy: {
      name: "Suspicious Activity Reporting Policy",
      policy_type: "procedure",
      category: "compliance",
      description: "Policy and procedures for identifying, escalating, investigating, and filing Suspicious Activity Reports.",
      version: "1.0",
      status: "draft",
      regulatory_basis: "Per 31 CFR 1020.320, SARs must be filed for transactions over $5,000 that are suspicious. This policy ensures timely filing.",
    },
    ofac: {
      name: "OFAC Compliance Policy",
      policy_type: "policy",
      category: "compliance",
      description: "Policy establishing procedures for OFAC sanctions screening and compliance with the International Emergency Economic Powers Act.",
      version: "1.0",
      status: "draft",
      regulatory_basis: "Per 31 CFR Chapter V, financial institutions must comply with OFAC sanctions. This policy ensures compliance.",
    },
  };

  const INCIDENT_TEMPLATES = {
    system_outage: {
      title: "AML System Outage",
      incident_type: "operational",
      severity: "high",
      description: "Transaction monitoring system experienced unplanned downtime affecting real-time screening capabilities.",
      root_cause: "Infrastructure failure / vendor issue / capacity limits",
      remediation: "Restored system operations, implemented manual review procedures during outage, queued transactions for retroactive screening.",
    },
    data_breach: {
      title: "Customer Data Exposure",
      incident_type: "data_breach",
      severity: "critical",
      description: "Potential unauthorized access to customer PII or sensitive AML investigation information.",
      root_cause: "Under investigation - potential vectors include phishing, misconfiguration, insider threat",
      remediation: "Contained exposure, initiated forensic investigation, notified affected parties per regulatory requirements.",
    },
    compliance_violation: {
      title: "Regulatory Filing Deadline Miss",
      incident_type: "compliance",
      severity: "high",
      description: "Failed to file required regulatory report (CTR/SAR) within mandated timeframe.",
      root_cause: "Process breakdown / staffing gap / system issue",
      remediation: "Filed late report with explanation, implemented process improvements, enhanced monitoring of deadlines.",
    },
  };

  function openThresholdCreate(templateType = null) {
    setThresholdEditingId(null);
    
    // If template provided, pre-fill the form
    if (templateType && THRESHOLD_TEMPLATES[templateType]) {
      const t = THRESHOLD_TEMPLATES[templateType];
      setThresholdForm({
        name: t.name || "",
        owner_email: session?.user?.email || "",
        description: t.description || "",
        current_value: t.current_value || "",
        unit: t.unit || "",
        regulatory_basis: t.regulatory_basis || "",
        alternatives_considered: t.alternatives_considered || "",
        documentation_location: "",
        authority_citation: "",
        evidence_summary: "",
        next_review_date: "",
        review_frequency: "",
        risk_level: t.risk_level || "medium",
      });
    } else {
      resetThresholdForm();
    }
    
    setThresholdModal(true);
  }

  function openThresholdEdit(threshold) {
    setThresholdEditingId(threshold.id);
    setThresholdForm({
      name: threshold.name || "",
      owner_email: threshold.owner_email || "",
      description: threshold.description || "",
      current_value: threshold.current_value ?? "",
      unit: threshold.unit || "",
      regulatory_basis: threshold.regulatory_basis || "",
      alternatives_considered: threshold.alternatives_considered || "",
      documentation_location: threshold.documentation_location || "",
      authority_citation: threshold.authority_citation || "",
      evidence_summary: threshold.evidence_summary || "",
      next_review_date: threshold.next_review_date || "",
      review_frequency: threshold.review_frequency || "",
      risk_level: threshold.risk_level || "medium",
    });
    setThresholdModal(true);
  }

  async function saveScenario() {
    try {
      setScenarioSaving(true);
      if (!orgId) throw new Error("Organization not loaded");
      if (!scenarioForm.name.trim()) throw new Error("Scenario name is required");
      
      // WHY field validation - enforce substantive rationale
      const whyText = scenarioForm.regulatory_basis?.trim() || "";
      if (whyText.length < 50) {
        throw new Error("WHY rationale must be at least 50 characters. Use the 'Build WHY' button for guidance.");
      }
      if (hasPlaceholderRationale(whyText)) {
        throw new Error("WHY rationale contains placeholder phrases. Please provide specific data, citations, and alternatives.");
      }
      
      // Anti-gaming: Check for verbose padding
      const paddingCheck = detectVerbosePadding(whyText);
      if (paddingCheck.isPadding) {
        throw new Error(`WHY rationale appears to contain filler content. ${paddingCheck.reason}. Please provide specific, substantive information.`);
      }
      
      let scenarioId = scenarioEditingId;
      if (!scenarioId) {
        const { data: scenario, error: scenarioError } = await supabase
          .from("scenarios")
          .insert({ organization_id: orgId, vertical })
          .select("id")
          .single();
        if (scenarioError) throw scenarioError;
        scenarioId = scenario.id;
      }

      const payload = {
        scenario_id: scenarioId,
        organization_id: orgId,
        vertical,
        name: scenarioForm.name.trim(),
        owner_email: scenarioForm.owner_email.trim() || null,
        description: scenarioForm.description.trim() || null,
        regulatory_basis: scenarioForm.regulatory_basis.trim() || null,
        alternatives_considered: scenarioForm.alternatives_considered.trim() || null,
        documentation_location: scenarioForm.documentation_location.trim() || null,
        authority_citation: scenarioForm.authority_citation.trim() || null,
        evidence_summary: scenarioForm.evidence_summary.trim() || null,
        next_review_date: scenarioForm.next_review_date || null,
        review_frequency: scenarioForm.review_frequency.trim() || null,
        risk_level: scenarioForm.risk_level || "medium",
      };
      const { error: versionError } = await supabase.from("scenario_versions").insert(payload);
      if (versionError) throw versionError;

      setToast({ type: "success", message: scenarioEditingId ? "Scenario updated" : "Scenario created" });
      setTimeout(() => setToast(null), 3000);
      setScenarioModal(false);
      setScenarioEditingId(null);
      resetScenarioForm();
      loadAllData();
    } catch (err) {
      console.error("Save scenario error:", err);
      const actionableError = getActionableError(err.message);
      setToast({ 
        type: "error", 
        message: err.message,
        action: actionableError.action,
      });
      setTimeout(() => setToast(null), 8000); // Longer timeout for actionable errors
    } finally {
      setScenarioSaving(false);
    }
  }

  async function saveThreshold() {
    try {
      setThresholdSaving(true);
      if (!orgId) throw new Error("Organization not loaded");
      if (!thresholdForm.name.trim()) throw new Error("Threshold name is required");
      
      // WHY field validation - enforce substantive rationale
      const whyText = thresholdForm.regulatory_basis?.trim() || "";
      if (whyText.length < 50) {
        throw new Error("WHY rationale must be at least 50 characters. Use the 'Build WHY' button for guidance.");
      }
      if (hasPlaceholderRationale(whyText)) {
        throw new Error("WHY rationale contains placeholder phrases. Please provide specific data, citations, and alternatives.");
      }
      
      // Anti-gaming: Check for verbose padding
      const paddingCheck = detectVerbosePadding(whyText);
      if (paddingCheck.isPadding) {
        throw new Error(`WHY rationale appears to contain filler content. ${paddingCheck.reason}. Please provide specific, substantive information.`);
      }
      
      let thresholdId = thresholdEditingId;
      if (!thresholdId) {
        const { data: threshold, error: thresholdError } = await supabase
          .from("thresholds")
          .insert({ organization_id: orgId, vertical })
          .select("id")
          .single();
        if (thresholdError) throw thresholdError;
        thresholdId = threshold.id;
      }

      const payload = {
        threshold_id: thresholdId,
        organization_id: orgId,
        vertical,
        name: thresholdForm.name.trim(),
        owner_email: thresholdForm.owner_email.trim() || null,
        description: thresholdForm.description.trim() || null,
        current_value: thresholdForm.current_value === "" ? null : Number(thresholdForm.current_value),
        unit: thresholdForm.unit.trim() || null,
        regulatory_basis: thresholdForm.regulatory_basis.trim() || null,
        alternatives_considered: thresholdForm.alternatives_considered.trim() || null,
        documentation_location: thresholdForm.documentation_location.trim() || null,
        authority_citation: thresholdForm.authority_citation.trim() || null,
        evidence_summary: thresholdForm.evidence_summary.trim() || null,
        next_review_date: thresholdForm.next_review_date || null,
        review_frequency: thresholdForm.review_frequency.trim() || null,
        risk_level: thresholdForm.risk_level || "medium",
      };
      const { error: versionError } = await supabase.from("threshold_versions").insert(payload);
      if (versionError) throw versionError;

      setToast({ type: "success", message: thresholdEditingId ? "Threshold updated" : "Threshold created" });
      setTimeout(() => setToast(null), 3000);
      setThresholdModal(false);
      setThresholdEditingId(null);
      resetThresholdForm();
      loadAllData();
    } catch (err) {
      console.error("Save threshold error:", err);
      const actionableError = getActionableError(err.message);
      setToast({ 
        type: "error", 
        message: err.message,
        action: actionableError.action,
      });
      setTimeout(() => setToast(null), 8000); // Longer timeout for actionable errors
    } finally {
      setThresholdSaving(false);
    }
  }

  // Reset functions for GRC modals
  function resetVendorForm() {
    setVendorForm({
      name: '', vendor_type: 'significant', risk_tier: 'medium', status: 'active',
      description: '', contact_name: '', contact_email: '', services: '',
      owner_email: '', regulatory_basis: '', alternatives_considered: '', next_review_date: ''
    });
  }
  
  function resetControlForm() {
    setControlForm({
      name: '', control_type: 'preventive', control_category: 'operational', description: '',
      frequency: 'continuous', owner_email: '', regulatory_basis: '', alternatives_considered: '', next_review_date: ''
    });
  }
  
  function resetRiskForm() {
    setRiskForm({
      name: '', risk_category: 'operational', description: '', inherent_likelihood: 3, inherent_impact: 3,
      residual_likelihood: 2, residual_impact: 2, risk_treatment: 'mitigate',
      owner_email: '', regulatory_basis: '', alternatives_considered: '', next_review_date: ''
    });
  }
  
  function resetPolicyForm() {
    setPolicyForm({
      name: '', policy_type: 'standard', category: 'operational', description: '', version: '1.0',
      status: 'draft', owner_email: '', regulatory_basis: '', alternatives_considered: '', next_review_date: ''
    });
  }
  
  function resetIncidentForm() {
    setIncidentForm({
      title: '', incident_type: 'security', severity: 'medium', status: 'open', description: '',
      reporter_email: '', owner_email: '', regulatory_basis: '', root_cause: '', remediation: ''
    });
  }

  // Save functions for GRC modals
  async function saveVendor() {
    try {
      setVendorSaving(true);
      if (!orgId) throw new Error("Organization not loaded");
      if (!vendorForm.name.trim()) throw new Error("Vendor name is required");
      
      const payload = {
        organization_id: orgId,
        name: vendorForm.name.trim(),
        vendor_type: vendorForm.vendor_type,
        risk_tier: vendorForm.risk_tier,
        status: vendorForm.status,
        description: vendorForm.description.trim() || null,
        contact_name: vendorForm.contact_name.trim() || null,
        contact_email: vendorForm.contact_email.trim() || null,
        services: vendorForm.services.trim() || null,
        owner_email: vendorForm.owner_email.trim() || null,
        regulatory_basis: vendorForm.regulatory_basis.trim() || null,
        alternatives_considered: vendorForm.alternatives_considered.trim() || null,
        next_review_date: vendorForm.next_review_date || null,
      };
      
      if (vendorEditingId) {
        const { error } = await supabase.from("vendors").update(payload).eq("id", vendorEditingId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("vendors").insert(payload);
        if (error) throw error;
      }
      
      setToast({ type: "success", message: vendorEditingId ? "Vendor updated" : "Vendor created" });
      setTimeout(() => setToast(null), 3000);
      setVendorModal({ open: false, vendor: null });
      setVendorEditingId(null);
      resetVendorForm();
      loadVendorData();
    } catch (err) {
      console.error("Save vendor error:", err);
      setToast({ type: "error", message: err.message });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setVendorSaving(false);
    }
  }

  async function saveControl() {
    try {
      setControlSaving(true);
      if (!orgId) throw new Error("Organization not loaded");
      if (!controlForm.name.trim()) throw new Error("Control name is required");
      
      const payload = {
        organization_id: orgId,
        name: controlForm.name.trim(),
        control_type: controlForm.control_type,
        control_category: controlForm.control_category,
        description: controlForm.description.trim() || null,
        frequency: controlForm.frequency,
        owner_email: controlForm.owner_email.trim() || null,
        regulatory_basis: controlForm.regulatory_basis.trim() || null,
        alternatives_considered: controlForm.alternatives_considered.trim() || null,
        next_review_date: controlForm.next_review_date || null,
      };
      
      if (controlEditingId) {
        const { error } = await supabase.from("controls").update(payload).eq("id", controlEditingId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("controls").insert(payload);
        if (error) throw error;
      }
      
      setToast({ type: "success", message: controlEditingId ? "Control updated" : "Control created" });
      setTimeout(() => setToast(null), 3000);
      setControlModal({ open: false, control: null });
      setControlEditingId(null);
      resetControlForm();
      loadControlData();
    } catch (err) {
      console.error("Save control error:", err);
      setToast({ type: "error", message: err.message });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setControlSaving(false);
    }
  }

  async function saveRisk() {
    try {
      setRiskSaving(true);
      if (!orgId) throw new Error("Organization not loaded");
      if (!riskForm.name.trim()) throw new Error("Risk name is required");
      
      const payload = {
        organization_id: orgId,
        name: riskForm.name.trim(),
        risk_category: riskForm.risk_category,
        description: riskForm.description.trim() || null,
        inherent_likelihood: riskForm.inherent_likelihood,
        inherent_impact: riskForm.inherent_impact,
        residual_likelihood: riskForm.residual_likelihood,
        residual_impact: riskForm.residual_impact,
        risk_treatment: riskForm.risk_treatment,
        owner_email: riskForm.owner_email.trim() || null,
        regulatory_basis: riskForm.regulatory_basis.trim() || null,
        alternatives_considered: riskForm.alternatives_considered.trim() || null,
        next_review_date: riskForm.next_review_date || null,
      };
      
      if (riskEditingId) {
        const { error } = await supabase.from("risks").update(payload).eq("id", riskEditingId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("risks").insert(payload);
        if (error) throw error;
      }
      
      setToast({ type: "success", message: riskEditingId ? "Risk updated" : "Risk created" });
      setTimeout(() => setToast(null), 3000);
      setRiskModal({ open: false, risk: null });
      setRiskEditingId(null);
      resetRiskForm();
      loadRiskData();
    } catch (err) {
      console.error("Save risk error:", err);
      setToast({ type: "error", message: err.message });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setRiskSaving(false);
    }
  }

  async function savePolicy() {
    try {
      setPolicySaving(true);
      if (!orgId) throw new Error("Organization not loaded");
      if (!policyForm.name.trim()) throw new Error("Policy name is required");
      
      const payload = {
        organization_id: orgId,
        name: policyForm.name.trim(),
        policy_type: policyForm.policy_type,
        category: policyForm.category,
        description: policyForm.description.trim() || null,
        version: policyForm.version.trim() || '1.0',
        status: policyForm.status,
        owner_email: policyForm.owner_email.trim() || null,
        regulatory_basis: policyForm.regulatory_basis.trim() || null,
        alternatives_considered: policyForm.alternatives_considered.trim() || null,
        next_review_date: policyForm.next_review_date || null,
      };
      
      if (policyEditingId) {
        const { error } = await supabase.from("policies").update(payload).eq("id", policyEditingId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("policies").insert(payload);
        if (error) throw error;
      }
      
      setToast({ type: "success", message: policyEditingId ? "Policy updated" : "Policy created" });
      setTimeout(() => setToast(null), 3000);
      setPolicyModal({ open: false, policy: null });
      setPolicyEditingId(null);
      resetPolicyForm();
      loadPolicyData();
    } catch (err) {
      console.error("Save policy error:", err);
      setToast({ type: "error", message: err.message });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setPolicySaving(false);
    }
  }

  async function saveIncident() {
    try {
      setIncidentSaving(true);
      if (!orgId) throw new Error("Organization not loaded");
      if (!incidentForm.title.trim()) throw new Error("Incident title is required");
      
      const payload = {
        organization_id: orgId,
        title: incidentForm.title.trim(),
        incident_type: incidentForm.incident_type,
        severity: incidentForm.severity,
        status: incidentForm.status,
        description: incidentForm.description.trim() || null,
        reporter_email: incidentForm.reporter_email.trim() || null,
        owner_email: incidentForm.owner_email.trim() || null,
        regulatory_basis: incidentForm.regulatory_basis.trim() || null,
        root_cause: incidentForm.root_cause.trim() || null,
        remediation: incidentForm.remediation.trim() || null,
      };
      
      if (incidentEditingId) {
        const { error } = await supabase.from("incidents").update(payload).eq("id", incidentEditingId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("incidents").insert(payload);
        if (error) throw error;
      }
      
      setToast({ type: "success", message: incidentEditingId ? "Incident updated" : "Incident created" });
      setTimeout(() => setToast(null), 3000);
      setIncidentModal({ open: false, incident: null });
      setIncidentEditingId(null);
      resetIncidentForm();
      loadIncidentData();
    } catch (err) {
      console.error("Save incident error:", err);
      setToast({ type: "error", message: err.message });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setIncidentSaving(false);
    }
  }

  // Edit functions - populate form with existing data
  function openVendorEdit(vendor) {
    setVendorEditingId(vendor.id);
    setVendorForm({
      name: vendor.name || '',
      vendor_type: vendor.vendor_type || 'significant',
      risk_tier: vendor.risk_tier || 'medium',
      status: vendor.status || 'active',
      description: vendor.description || '',
      contact_name: vendor.contact_name || '',
      contact_email: vendor.contact_email || '',
      services: vendor.services || '',
      owner_email: vendor.owner_email || '',
      regulatory_basis: vendor.regulatory_basis || '',
      alternatives_considered: vendor.alternatives_considered || '',
      next_review_date: vendor.next_review_date || ''
    });
    setVendorModal({ open: true, vendor: null });
  }

  function openControlEdit(control) {
    setControlEditingId(control.id);
    setControlForm({
      name: control.name || '',
      control_type: control.control_type || 'preventive',
      control_category: control.control_category || 'operational',
      description: control.description || '',
      frequency: control.frequency || 'continuous',
      owner_email: control.owner_email || '',
      regulatory_basis: control.regulatory_basis || '',
      alternatives_considered: control.alternatives_considered || '',
      next_review_date: control.next_review_date || ''
    });
    setControlModal({ open: true, control: null });
  }

  function openRiskEdit(risk) {
    setRiskEditingId(risk.id);
    setRiskForm({
      name: risk.name || '',
      risk_category: risk.risk_category || 'operational',
      description: risk.description || '',
      inherent_likelihood: risk.inherent_likelihood || 3,
      inherent_impact: risk.inherent_impact || 3,
      residual_likelihood: risk.residual_likelihood || 2,
      residual_impact: risk.residual_impact || 2,
      risk_treatment: risk.risk_treatment || 'mitigate',
      owner_email: risk.owner_email || '',
      regulatory_basis: risk.regulatory_basis || '',
      alternatives_considered: risk.alternatives_considered || '',
      next_review_date: risk.next_review_date || ''
    });
    setRiskModal({ open: true, risk: null });
  }

  function openPolicyEdit(policy) {
    setPolicyEditingId(policy.id);
    setPolicyForm({
      name: policy.name || '',
      policy_type: policy.policy_type || 'standard',
      category: policy.category || 'operational',
      description: policy.description || '',
      version: policy.version || '1.0',
      status: policy.status || 'draft',
      owner_email: policy.owner_email || '',
      regulatory_basis: policy.regulatory_basis || '',
      alternatives_considered: policy.alternatives_considered || '',
      next_review_date: policy.next_review_date || ''
    });
    setPolicyModal({ open: true, policy: null });
  }

  function openIncidentEdit(incident) {
    setIncidentEditingId(incident.id);
    setIncidentForm({
      title: incident.title || '',
      incident_type: incident.incident_type || 'security',
      severity: incident.severity || 'medium',
      status: incident.status || 'open',
      description: incident.description || '',
      reporter_email: incident.reporter_email || '',
      owner_email: incident.owner_email || '',
      regulatory_basis: incident.regulatory_basis || '',
      root_cause: incident.root_cause || '',
      remediation: incident.remediation || ''
    });
    setIncidentModal({ open: true, incident: null });
  }

  function openDeleteModal(type, item) {
    setDeleteModal({ open: true, type, id: item.id, name: item.name || "Untitled" });
  }

  async function confirmDelete() {
    if (!deleteModal.id) return;
    try {
      setDeletingItem(true);
      const now = new Date().toISOString();
      
      // Map entity types to table names and reload functions
      const entityConfig = {
        webhook: { table: "webhooks", hardDelete: true, reload: loadWebhooks },
        scenario: { table: "scenarios", hardDelete: false, reload: loadAllData },
        threshold: { table: "thresholds", hardDelete: false, reload: loadAllData },
        vendor: { table: "vendors", hardDelete: true, reload: loadVendorData },
        control: { table: "controls", hardDelete: true, reload: loadControlData },
        risk: { table: "risks", hardDelete: true, reload: loadRiskData },
        policy: { table: "policies", hardDelete: true, reload: loadPolicyData },
        incident: { table: "incidents", hardDelete: true, reload: loadIncidentData },
      };
      
      const config = entityConfig[deleteModal.type];
      if (!config) {
        throw new Error(`Unknown entity type: ${deleteModal.type}`);
      }
      
      if (config.hardDelete) {
        // Hard delete - no undo
        const { error } = await supabase.from(config.table).delete().eq("id", deleteModal.id);
        if (error) throw error;
        setToast({ type: "success", message: `${deleteModal.type.charAt(0).toUpperCase() + deleteModal.type.slice(1)} deleted` });
        config.reload();
      } else {
        // Soft delete with undo (scenarios and thresholds)
        const { error } = await supabase.from(config.table).update({ deleted_at: now }).eq("id", deleteModal.id);
        if (error) throw error;
        
        const entityName = deleteModal.type.charAt(0).toUpperCase() + deleteModal.type.slice(1);
        
        // Store for undo (allow 10 seconds to undo)
        setUndoItem({
          type: entityName,
          id: deleteModal.id,
          table: config.table,
          name: deleteModal.name
        });
        
        config.reload();
      }
      
      setDeleteModal({ open: false, type: null, id: null, name: "" });
    } catch (err) {
      setToast({ type: "error", message: "Failed to delete item." });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setDeletingItem(false);
    }
  }

  // Undo delete - restore soft-deleted item
  async function handleUndoDelete() {
    if (!undoItem) return;
    try {
      const { error } = await supabase
        .from(undoItem.table)
        .update({ deleted_at: null })
        .eq("id", undoItem.id);
      
      if (error) throw error;
      
      setToast({ type: "success", message: `${undoItem.type} restored` });
      setTimeout(() => setToast(null), 3000);
      loadAllData();
    } catch (err) {
      setToast({ type: "error", message: "Failed to restore item" });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setUndoItem(null);
    }
  }

  async function scanDocument(doc) {
    try {
      if (!orgId) throw new Error("Organization not loaded");
      setScanningDocumentId(doc.id);
      await callFn("scan-document", { document_id: doc.id, organization_id: orgId, vertical });
      setToast({ type: "success", message: "Document queued for AI scan" });
      setTimeout(() => setToast(null), 3000);
      loadAllData();
    } catch (err) {
      console.error("Scan document error:", err);
      setToast({ type: "error", message: `Failed to scan document: ${sanitizeErrorMessage(err)}` });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setScanningDocumentId(null);
    }
  }

  async function openDocument(doc) {
    try {
      const { data, error } = await supabase.storage.from("documents").createSignedUrl(doc.storage_path, 60 * 60);
      if (error) throw error;
      if (data?.signedUrl) window.open(data.signedUrl, "_blank");
    } catch (err) {
      console.error("Open document error:", err);
      setToast({ type: "error", message: "Failed to open document." });
      setTimeout(() => setToast(null), 4000);
    }
  }

  async function verifyDocumentIntegrity(doc) {
    if (!doc?.storage_path) return;
    setVerifyingDocId(doc.id);
    try {
      const { data, error } = await supabase.storage.from("documents").download(doc.storage_path);
      if (error || !data) throw error || new Error("Download failed");
      const buffer = await data.arrayBuffer();
      const computedHash = await computeSha256Hex(buffer);
      const { data: result, error: rpcError } = await supabase.rpc("verify_document_integrity", {
        p_document_id: doc.id,
        p_computed_hash: computedHash,
      });
      if (rpcError) throw rpcError;
      setToast({
        type: result?.matches ? "success" : "error",
        message: result?.matches ? "Integrity verified" : "Integrity mismatch detected",
      });
      setTimeout(() => setToast(null), 4000);
      loadAllData();
      if (selectedDocument?.id === doc.id) {
        setSelectedDocument({ ...doc, integrity_status: result?.status, content_hash: result?.stored_hash });
      }
    } catch (err) {
      console.error("Verify integrity error:", err);
      setToast({ type: "error", message: sanitizeErrorMessage(err) || "Failed to verify integrity." });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setVerifyingDocId(null);
    }
  }

  async function linkDocumentToObject(doc) {
    if (!doc?.id || !linkForm.entityId) {
      setToast({ type: "error", message: "Select a target object to link." });
      setTimeout(() => setToast(null), 4000);
      return;
    }
    setLinkingDocId(doc.id);
    try {
      const { data, error } = await supabase.rpc("link_document_to_object", {
        p_document_id: doc.id,
        p_entity_type: linkForm.entityType,
        p_entity_id: linkForm.entityId,
        p_linked_question: linkForm.question || null,
      });
      if (error) throw error;
      setToast({ type: "success", message: "Document linked successfully" });
      setTimeout(() => setToast(null), 3000);
      setLinkForm({ entityType: linkForm.entityType, entityId: "", question: linkForm.question });
      loadAllData();
    } catch (err) {
      console.error("Link document error:", err);
      setToast({ type: "error", message: sanitizeErrorMessage(err) || "Failed to link document." });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setLinkingDocId(null);
    }
  }

  async function generateExaminerPack(phase) {
    if (!orgId) return;
    setExaminerModal(m => ({ ...m, generating: true, phase }));
    try {
      const out = await callFn("generate-examiner-package", {
        organization_id: orgId,
        vertical,
        as_of: new Date(examinerModal.asOf).toISOString(),
        phase,
      });
      setExaminerModal(m => ({
        ...m,
        proofPack: out.proof_pack || m.proofPack,
        humanPack: out.human_pack || m.humanPack,
        meta: {
          generated_at: new Date().toISOString(),
          as_of_date: examinerModal.asOf,
          package_version: out.package_version,
          reconstruction_method: "versioned_snapshots",
          organization_id: orgId,
          scope: "all",
          hash_algorithm: "SHA-256",
          merkle_root: out.proof_pack?.merkle_root || m?.proofPack?.merkle_root,
        },
      }));
      setToast({ type: "success", message: phase === "proof" ? "Proof Pack ready" : "Human Pack ready" });
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      console.error("Examiner package error:", err);
      setToast({ type: "error", message: sanitizeErrorMessage(err) || "Failed to generate package." });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setExaminerModal(m => ({ ...m, generating: false, phase: null }));
    }
  }

  async function runQuickAction(key, action) {
    try {
      setQuickAction(key);
      await action();
    } catch (err) {
      console.error("Quick action error:", err);
      setToast({ type: "error", message: sanitizeErrorMessage(err) || "Action failed." });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setQuickAction(null);
    }
  }

  // Login form state - MUST be before any conditional returns (React hooks rules)
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      setAuthError("Please enter both email and password.");
      return;
    }
    setLoginLoading(true);
    setAuthError(null);
    try {
      const { error } = await supabase.auth.signInWithPassword({ 
        email: loginEmail, 
        password: loginPassword 
      });
      if (error) throw error;
    } catch (err) {
      console.error("Sign-in error:", err);
      setAuthError(err.message || "Sign-in failed. Please check your credentials.");
    } finally {
      setLoginLoading(false);
    }
  };

  if (loading) return (
    <div className={`min-h-screen flex items-center justify-center transition-colors ${
      darkMode 
        ? "bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950" 
        : "bg-gradient-to-br from-slate-100 via-slate-50 to-white"
    }`}>
      <div className="flex flex-col items-center gap-4">
        <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 via-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30 animate-pulse">
          <span className="text-white font-bold text-2xl">S</span>
        </div>
        <div className={`animate-pulse font-medium ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Loading Steadfastly...</div>
      </div>
    </div>
  );

  if (!session) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-bl from-blue-500/10 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-to-tr from-indigo-500/10 via-transparent to-transparent rounded-full blur-3xl" />
      </div>
      
      <div className="max-w-md w-full relative z-10">
        <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800/50 rounded-3xl p-8 shadow-2xl shadow-black/20">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 via-blue-500 to-cyan-400 text-white rounded-2xl mb-5 shadow-lg shadow-blue-500/25">
              <span className="font-bold text-2xl">S</span>
            </div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">Steadfastly</h1>
            <p className="text-slate-500 mt-2">Reconstruction-capable governance platform</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="login-email" className="block text-sm font-medium text-slate-300 mb-1.5">
                Email Address
              </label>
              <input
                id="login-email"
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                disabled={loginLoading}
              />
            </div>
            
            <div>
              <label htmlFor="login-password" className="block text-sm font-medium text-slate-300 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all pr-12"
                  disabled={loginLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <Icons.Eye className="w-5 h-5" /> : <Icons.Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loginLoading}
            >
              {loginLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Icons.Refresh className="w-4 h-4 animate-spin" />
                  Signing in...
                </span>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>
          
          {authError && (
            <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3">
              <Icons.Alert className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-red-400 text-sm font-medium">Sign-in failed</div>
                <div className="text-red-400/80 text-sm mt-0.5">{authError}</div>
              </div>
            </div>
          )}
          
          <div className="mt-6 text-center">
            <a href="#" className="text-sm text-blue-400 hover:text-blue-300 transition-colors">
              Forgot your password?
            </a>
          </div>
          
          <div className="mt-8 pt-6 border-t border-slate-800/50">
            <div className="flex items-center justify-center gap-4 text-xs text-slate-500">
              <span>{APP_VERSION}</span>
              <span>•</span>
              <span>Enterprise Ready</span>
            </div>
          </div>
        </div>
        
        {/* Trust badges */}
        <div className="mt-6 flex justify-center gap-6">
          <div className="text-slate-600 text-xs flex items-center gap-1.5">
            <Icons.Shield className="w-4 h-4" />
            <span>256-bit encryption</span>
          </div>
          <div className="text-slate-600 text-xs flex items-center gap-1.5">
            <Icons.Key className="w-4 h-4" />
            <span>Bank-grade security</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Navigation grouped by role/workflow with visual separators
  const navGroups = [
    {
      id: "daily",
      label: "Daily Operations",
      color: "blue",
      description: "Start here each day",
      items: [
        { id: "dashboard", label: "Overview", icon: Icons.Grid },
        { id: "inbox", label: "Inbox", icon: Icons.Inbox },
        { id: "regulatory", label: "Regulatory Updates", icon: Icons.Bell },
      ]
    },
    {
      id: "governance",
      label: "Governance & Policy",
      color: "indigo",
      description: "Compliance framework",
      items: [
        { id: "governance", label: "Scenarios & Thresholds", icon: Icons.File },
        { id: "policies", label: "Policies", icon: Icons.File },
        { id: "controls", label: "Controls", icon: Icons.Shield },
        { id: "gap-analysis", label: "Gap Analysis", icon: Icons.ChartBar },
      ]
    },
    {
      id: "risk",
      label: "Risk Management",
      color: "amber",
      description: "Risk & third parties",
      items: [
        { id: "risks", label: "Risk Register", icon: Icons.Alert },
        { id: "vendors", label: "Vendor Risk", icon: Icons.Briefcase },
        { id: "incidents", label: "Incidents", icon: Icons.Alert },
      ]
    },
    {
      id: "evidence",
      label: "Evidence & Attestation",
      color: "emerald",
      description: "Prove your compliance",
      items: [
        { id: "documents", label: "Evidence Library", icon: Icons.Briefcase },
        { id: "ai-rules", label: "AI Workspace", icon: Icons.Sparkles },
        { id: "attestations", label: "Attestations", icon: Icons.Shield },
      ]
    },
    {
      id: "audit",
      label: "Audit & Exam Prep",
      color: "purple",
      description: "Examiner-ready outputs",
      items: [
        { id: "examiner-packages", label: "Examiner Packages", icon: Icons.Download },
        { id: "point-in-time", label: "Point-in-Time", icon: Icons.Clock },
        { id: "findings", label: "Findings Tracker", icon: Icons.List },
      ]
    },
    ...(isAdmin ? [{
      id: "admin-group",
      label: "Administration",
      color: "slate",
      description: "System settings",
      items: [
        { id: "admin", label: "Admin & Settings", icon: Icons.Settings, adminOnly: true },
      ]
    }] : []),
  ];
  
  // Flatten for backward compatibility
  const navItems = navGroups.flatMap(g => g.items);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      darkMode 
        ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100" 
        : "bg-gradient-to-br from-slate-100 via-slate-50 to-white text-slate-900"
    }`}>
      <div className="flex min-h-screen">
        {/* Mobile menu overlay */}
        {mobileMenuOpen && (
          <div 
            className={`fixed inset-0 backdrop-blur-sm z-40 lg:hidden ${darkMode ? "bg-black/70" : "bg-black/40"}`}
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
        
        {/* Sidebar - Theme-aware */}
        <aside className={`
          fixed lg:relative inset-y-0 left-0 z-50
          w-64 backdrop-blur-xl flex flex-col h-screen
          transform transition-all duration-300 ease-out shadow-2xl
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${darkMode 
            ? "bg-slate-900/95 border-r border-slate-800/50" 
            : "bg-white/95 border-r border-slate-200"
          }
        `}>
          <div className={`px-5 py-5 flex items-center justify-between ${darkMode ? "border-b border-slate-800/50" : "border-b border-slate-200"}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 via-blue-500 to-cyan-400 rounded-xl flex items-center justify-center text-white font-bold shadow-lg shadow-blue-500/25">S</div>
              <div className={`text-xl font-semibold ${darkMode ? "bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent" : "text-slate-900"}`}>Steadfastly</div>
            </div>
            {/* Mobile close button */}
            <button 
              className={`lg:hidden p-2 rounded-lg ${darkMode ? "text-slate-500 hover:text-white hover:bg-slate-800" : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"}`}
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <Icons.X className="w-5 h-5" />
            </button>
          </div>
          <nav className="p-3 flex-1 overflow-y-auto" aria-label="Main navigation" role="navigation">
            {navGroups.map((group, groupIndex) => {
              // Color schemes for each group - HIGH CONTRAST VERSION
              // Fixed: No more colored text on colored backgrounds!
              const groupColors = {
                blue: { 
                  bg: darkMode ? "bg-slate-800/80" : "bg-slate-100", 
                  text: darkMode ? "text-slate-300" : "text-slate-700",  // Neutral text, not blue-on-blue!
                  accent: darkMode ? "text-blue-400" : "text-blue-600",  // Accent for icons only
                  border: darkMode ? "border-slate-700" : "border-slate-200",
                  active: "from-blue-600 to-blue-700",
                  shadow: "shadow-blue-500/20"
                },
                indigo: { 
                  bg: darkMode ? "bg-slate-800/80" : "bg-slate-100", 
                  text: darkMode ? "text-slate-300" : "text-slate-700",
                  accent: darkMode ? "text-indigo-400" : "text-indigo-600",
                  border: darkMode ? "border-slate-700" : "border-slate-200",
                  active: "from-indigo-600 to-indigo-700",
                  shadow: "shadow-indigo-500/20"
                },
                amber: { 
                  bg: darkMode ? "bg-slate-800/80" : "bg-slate-100", 
                  text: darkMode ? "text-slate-300" : "text-slate-700",
                  accent: darkMode ? "text-amber-400" : "text-amber-600",
                  border: darkMode ? "border-slate-700" : "border-slate-200",
                  active: "from-amber-600 to-orange-600",
                  shadow: "shadow-amber-500/20"
                },
                emerald: { 
                  bg: darkMode ? "bg-slate-800/80" : "bg-slate-100", 
                  text: darkMode ? "text-slate-300" : "text-slate-700",
                  accent: darkMode ? "text-emerald-400" : "text-emerald-600",
                  border: darkMode ? "border-slate-700" : "border-slate-200",
                  active: "from-emerald-600 to-teal-600",
                  shadow: "shadow-emerald-500/20"
                },
                purple: { 
                  bg: darkMode ? "bg-slate-800/80" : "bg-slate-100", 
                  text: darkMode ? "text-slate-300" : "text-slate-700",
                  accent: darkMode ? "text-purple-400" : "text-purple-600",
                  border: darkMode ? "border-slate-700" : "border-slate-200",
                  active: "from-purple-600 to-violet-600",
                  shadow: "shadow-purple-500/20"
                },
                slate: { 
                  bg: darkMode ? "bg-slate-800/80" : "bg-slate-100", 
                  text: darkMode ? "text-slate-300" : "text-slate-600",
                  accent: darkMode ? "text-slate-400" : "text-slate-500",
                  border: darkMode ? "border-slate-700" : "border-slate-200",
                  active: "from-slate-600 to-slate-700",
                  shadow: "shadow-slate-500/20"
                },
              };
              const colors = groupColors[group.color] || groupColors.blue;
              
              return (
                <div key={group.id} className={groupIndex > 0 ? "mt-4" : ""}>
                  {/* Group Header */}
                  <div className={`px-3 py-2 mb-1 rounded-lg ${colors.bg} border ${colors.border}`}>
                    <div className={`text-[10px] font-bold uppercase tracking-wider ${colors.text}`}>
                      {group.label}
                    </div>
                    <div className={`text-[10px] ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                      {group.description}
                    </div>
                  </div>
                  
                  {/* Group Items */}
                  <div className="space-y-0.5">
                    {group.items.map(item => (
                      <button
                        key={item.id}
                        onClick={() => { setActiveTab(item.id); setMobileMenuOpen(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                          activeTab === item.id 
                            ? `bg-gradient-to-r ${colors.active} text-white shadow-lg ${colors.shadow}` 
                            : darkMode 
                              ? "text-slate-300 hover:text-white hover:bg-slate-800"
                              : "text-slate-700 hover:text-slate-900 hover:bg-slate-100"
                        }`}
                      >
                        <item.icon className={`w-4 h-4 flex-shrink-0 ${activeTab === item.id ? "" : "opacity-70"}`} />
                        <span className="truncate text-[13px]">{item.label}</span>
                        {item.id === "regulatory" && regulatoryUpdates.filter(u => u.review_status !== "reviewed" && (u.impact_guess === "critical" || u.impact_guess === "high")).length > 0 && (
                          <span className="ml-auto px-1.5 py-0.5 text-[10px] bg-red-500 text-white rounded-full font-bold">
                            {regulatoryUpdates.filter(u => u.review_status !== "reviewed" && (u.impact_guess === "critical" || u.impact_guess === "high")).length}
                          </span>
                        )}
                        {item.id === "inbox" && notifications.filter(n => n.status === "pending").length > 0 && (
                          <span className="ml-auto px-1.5 py-0.5 text-[10px] bg-blue-500 text-white rounded-full font-bold">
                            {notifications.filter(n => n.status === "pending").length}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </nav>
          {/* User profile & Sign out - always visible at bottom */}
          <div className={`p-4 mt-auto flex-shrink-0 ${darkMode ? "border-t border-slate-800/50 bg-slate-900/80" : "border-t border-slate-200 bg-slate-100/80"}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center text-white font-medium text-sm shadow-md">
                {session?.user?.email?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className={`text-sm truncate font-medium ${darkMode ? "text-slate-200" : "text-slate-900"}`}>{session?.user?.email?.split("@")[0]}</div>
                  {isPlatformAdmin && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-sm">
                      🛡️ Admin
                    </span>
                  )}
                </div>
                <div className={`text-xs truncate ${darkMode ? "text-slate-500" : "text-slate-500"}`}>{session?.user?.email}</div>
              </div>
            </div>
            <Button variant="primary" size="sm" className="w-full" onClick={() => supabase.auth.signOut()}>
              Sign Out
            </Button>
          </div>
        </aside>

        <div className="flex-1 flex flex-col w-full lg:w-auto">
          {/* WCAG 2.4.1 Skip Link - Red Team Phase 11 */}
          <a 
            href="#main-content" 
            className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            Skip to main content
          </a>
          
          <header className={`backdrop-blur-xl sticky top-0 z-30 ${
            darkMode 
              ? "bg-slate-900/80 border-b border-slate-800/50" 
              : "bg-white/80 border-b border-slate-200"
          }`}>
            <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3 sm:gap-4">
              <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                {/* Mobile menu button */}
                <button 
                  className={`lg:hidden p-2.5 rounded-xl flex-shrink-0 transition-colors ${
                    darkMode 
                      ? "text-slate-500 hover:text-white hover:bg-slate-800" 
                      : "text-slate-500 hover:text-slate-900 hover:bg-slate-200"
                  }`}
                  onClick={() => setMobileMenuOpen(true)}
                  aria-label="Open navigation menu"
                >
                  <Icons.Menu className="w-5 h-5" />
                </button>
                
                {/* Search - hidden on mobile */}
                <div className={`hidden sm:flex items-center w-48 md:w-80 max-w-full rounded-xl px-4 py-2.5 focus-within:border-blue-500/50 focus-within:ring-2 focus-within:ring-blue-500/20 transition-all ${
                  darkMode 
                    ? "bg-slate-800/50 border border-slate-700/50" 
                    : "bg-slate-100 border border-slate-200"
                }`}>
                  <Icons.Search className={`w-4 h-4 mr-2 ${darkMode ? "text-slate-500" : "text-slate-500"}`} />
                  <input
                    placeholder="Search..."
                    aria-label="Search scenarios, thresholds, and documents"
                    className={`flex-1 bg-transparent text-sm outline-none ${
                      darkMode 
                        ? "text-slate-200 placeholder:text-slate-500" 
                        : "text-slate-900 placeholder:text-slate-500"
                    }`}
                    onClick={() => setShowGlobalSearch(true)}
                    readOnly
                  />
                  <kbd className={`hidden lg:inline text-xs px-1.5 py-0.5 rounded ${darkMode ? "text-slate-500 bg-slate-700/50" : "text-slate-500 bg-slate-200"}`}>⌘K</kbd>
                </div>
                {/* Jurisdiction indicator - clickable */}
                <button
                  onClick={() => setShowJurisdictionSelector(true)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl transition-all flex-shrink-0 group ${
                    darkMode 
                      ? "bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50" 
                      : "bg-slate-100 hover:bg-slate-200 border border-slate-200"
                  }`}
                  title="Change jurisdiction"
                >
                  <span className="text-lg">{JURISDICTION_CONFIG[jurisdiction]?.flag || "🌍"}</span>
                  <span className="text-sm text-slate-300 font-medium hidden sm:inline group-hover:text-white">{jurisdiction}</span>
                </button>
                {/* Vertical indicator with config */}
                <div className={`hidden md:flex items-center gap-2 px-3 py-2 rounded-xl flex-shrink-0 ${
                  darkMode 
                    ? "bg-gradient-to-r from-slate-800/50 to-slate-800/30 border border-slate-700/50" 
                    : "bg-slate-100 border border-slate-200"
                }`}>
                  <span className="text-lg">{getVerticalConfig(vertical).icon}</span>
                  <span className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>{getVerticalConfig(vertical).displayName}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Demo mode toggle */}
                <label className={`flex items-center gap-2 text-xs cursor-pointer ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                  <input
                    type="checkbox"
                    checked={demoMode}
                    onChange={e => setDemoMode(e.target.checked)}
                    className="rounded text-blue-500"
                  />
                  Demo
                </label>
                {demoMode && (
                  <select 
                    className={`rounded-xl px-3 py-1.5 text-xs font-medium transition-all ${
                      darkMode 
                        ? "bg-indigo-500/20 border border-indigo-500/50 text-indigo-300 hover:bg-indigo-500/30" 
                        : "bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                    }`}
                    value={vertical} 
                    onChange={e => switchDemoVertical(e.target.value)}
                    title="Switch vertical for demo"
                  >
                    {VERTICAL_OPTIONS.map(v => (
                      <option key={v} value={v}>{VERTICAL_CONFIG[v]?.icon} {VERTICAL_CONFIG[v]?.displayName || v}</option>
                    ))}
                  </select>
                )}
                <div className={`text-sm hidden sm:inline ${darkMode ? "text-slate-300" : "text-slate-700"}`}>{org?.name || "Steadfastly"}</div>
                
                {/* Global Search Button */}
                <button
                  onClick={() => setShowGlobalSearch(true)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-xl transition-colors ${
                    darkMode 
                      ? "bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white border border-slate-600" 
                      : "bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900 border border-slate-200"
                  }`}
                  title="Search everything (Ctrl+K)"
                >
                  <Icons.Search className="w-4 h-4" />
                  <span className="text-sm hidden md:inline">Search...</span>
                  <kbd className={`hidden lg:inline text-xs px-1.5 py-0.5 rounded ${darkMode ? "bg-slate-600" : "bg-slate-200 text-slate-500"}`}>⌘K</kbd>
                </button>
                
                <NotificationDropdown 
                  notifications={notifications} 
                  onDismiss={dismissNotification} 
                  unreadCount={unreadNotifications}
                  onMarkRead={markNotificationRead}
                  onMarkAllRead={markAllNotificationsRead}
                />
                {/* Theme Toggle - cycles through dark/light/system */}
                <button
                  onClick={cycleTheme}
                  aria-label={`Switch theme. Current: ${themeMode === "system" ? "System" : themeMode === "dark" ? "Dark" : "Light"}`}
                  className={`p-2.5 rounded-xl transition-all duration-300 relative group ${
                    darkMode 
                      ? "bg-slate-700 hover:bg-slate-600" 
                      : "bg-slate-200 hover:bg-slate-300"
                  }`}
                  title={`Theme: ${themeMode === "system" ? "System" : themeMode === "dark" ? "Dark" : "Light"} (click to change)`}
                >
                  {themeMode === "dark" && <Icons.Moon className="w-5 h-5 text-indigo-400" />}
                  {themeMode === "light" && <Icons.Sun className="w-5 h-5 text-amber-500" />}
                  {themeMode === "system" && <Icons.Monitor className={`w-5 h-5 ${darkMode ? "text-cyan-400" : "text-slate-600"}`} />}
                  {/* Tooltip showing current mode */}
                  <span className={`absolute -bottom-8 left-1/2 -translate-x-1/2 text-xs px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap ${
                    darkMode ? "bg-slate-800 text-slate-300" : "bg-slate-700 text-white"
                  }`}>
                    {themeMode === "system" ? "Auto" : themeMode === "dark" ? "Dark" : "Light"}
                  </span>
                </button>
                {/* Help Button */}
                <button
                  onClick={() => setShowHelpModal(true)}
                  aria-label="Help and documentation"
                  className={`p-2 rounded-xl transition-colors ${
                    darkMode 
                      ? "bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white" 
                      : "bg-slate-200 hover:bg-slate-300 text-slate-600 hover:text-slate-900"
                  }`}
                  title="Help & Documentation"
                >
                  <Icons.QuestionMark className="w-5 h-5" />
                </button>
              </div>
            </div>
          </header>

          <main 
            id="main-content"
            tabIndex={-1}
            className={`flex-1 px-4 sm:px-6 lg:px-8 py-6 min-h-screen transition-colors duration-300 ${
            darkMode 
              ? "bg-gradient-to-b from-slate-900 via-slate-900/95 to-slate-950" 
              : "bg-gradient-to-b from-slate-50 via-slate-100/50 to-slate-100"
          }`}>
        {dataError && (
          <div className="mb-4 bg-red-50 border border-red-100 rounded-lg p-3 text-sm text-red-800 flex items-center justify-between">
            <span>{dataError}</span>
            <Button variant="secondary" size="sm" onClick={() => loadAllData()} disabled={dataLoading}>Retry</Button>
          </div>
        )}
        {/* dashboard tab - extracted to src/components/tabs/DashboardTab.jsx */}
        {activeTab === "dashboard" && <DashboardTab {...tabProps} />}

        {/* governance tab - extracted to src/components/tabs/GovernanceTab.jsx */}
        {activeTab === "governance" && <GovernanceTab {...tabProps} />}

        {/* documents tab - extracted to src/components/tabs/DocumentsTab.jsx */}
        {activeTab === "documents" && <DocumentsTab {...tabProps} />}

        {activeTab === "ai-rules" && (
          <div className="space-y-6">
            <Card darkMode={darkMode} title="AI Scan Results" subtitle="Review and accept AI-extracted governance insights">
              <ImportedRulesManager 
                importedRules={importedRules} 
                onStatusChange={reviewImportedRule}
                onRefresh={loadAllData}
                isLoading={dataLoading}
                reviewingRuleId={reviewingRuleId}
              />
            </Card>
          </div>
        )}

        {activeTab === "examiner-packages" && (
          <div className="space-y-6">
            <Card darkMode={darkMode} title="Examiner Packages" subtitle="Two-phase proof and human packs for regulators">
              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-500">
                  Generate a fast Proof Pack (hashes + Merkle root) and a Human Pack (PDF + evidence bundle).
                </div>
                <Button variant="secondary" onClick={() => setExaminerModal(m => ({ ...m, open: true }))}>
                  <Icons.Download /> Open Generator
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* point-in-time tab - extracted to src/components/tabs/PointInTimeTab.jsx */}
        {activeTab === "point-in-time" && <PointInTimeTab {...tabProps} />}

        {activeTab === "findings" && (
          <div className="space-y-6">
            <Card darkMode={darkMode} title="Findings Tracker" subtitle="Track MRAs/MRIAs and remediation status">
              <div className="text-sm text-slate-500">
                This module will list open and closed findings with due dates and owners.
              </div>
            </Card>
          </div>
        )}
        
        {/* ================================================================== */}
        {/* VENDOR RISK MANAGEMENT TAB (Sprint 1) */}
        {/* ================================================================== */}
        {/* vendors tab - extracted to src/components/tabs/VendorsTab.jsx */}
        {activeTab === "vendors" && <VendorsTab {...tabProps} />}
        
        {/* ================================================================== */}
        {/* CONTROLS TAB (Sprint 2) */}
        {/* ================================================================== */}
        {/* controls tab - extracted to src/components/tabs/ControlsTab.jsx */}
        {activeTab === "controls" && <ControlsTab {...tabProps} />}
        
        {/* ================================================================== */}
        {/* RISK REGISTER TAB (Sprint 2) */}
        {/* ================================================================== */}
        {/* risks tab - extracted to src/components/tabs/RisksTab.jsx */}
        {activeTab === "risks" && <RisksTab {...tabProps} />}
        
        {/* ================================================================== */}
        {/* POLICIES TAB (Sprint 3) */}
        {/* ================================================================== */}
        {/* policies tab - extracted to src/components/tabs/PoliciesTab.jsx */}
        {activeTab === "policies" && <PoliciesTab {...tabProps} />}
        
        {/* ================================================================== */}
        {/* INCIDENTS TAB (Sprint 3) */}
        {/* ================================================================== */}
        {/* incidents tab - extracted to src/components/tabs/IncidentsTab.jsx */}
        {activeTab === "incidents" && <IncidentsTab {...tabProps} />}

        {/* regulatory tab - extracted to src/components/tabs/RegulatoryTab.jsx */}
        {activeTab === "regulatory" && <RegulatoryTab {...tabProps} />}

        {activeTab === "inbox" && (
          <div className="space-y-6">
            <Card darkMode={darkMode} title="Inbox" subtitle="Tasks, approvals, and alerts">
              <div className="text-sm text-slate-500">
                Centralized inbox for approvals and compliance tasks.
              </div>
            </Card>
          </div>
        )}

        {/* attestations tab - extracted to src/components/tabs/AttestationsTab.jsx */}
        {activeTab === "attestations" && <AttestationsTab {...tabProps} />}

        {/* ================================================================== */}
        {/* GAP ANALYSIS TAB - The Demo Closer */}
        {/* "You're 67% covered, here are 13 gaps, click to fix" */}
        {/* ================================================================== */}
        {/* gap-analysis tab - extracted to src/components/tabs/GapAnalysisTab.jsx */}
        {activeTab === "gap-analysis" && <GapAnalysisTab {...tabProps} />}

        {/* admin tab - extracted to src/components/tabs/AdminTab.jsx */}
        {activeTab === "admin" && <AdminTab {...tabProps} />}
      </main>
        </div>
      </div>

      <Modal darkMode={darkMode} open={attestModal} onClose={() => { setAttestModal(false); setAttestResult(null); setAttestRigorWarning(null); setAttestRigorBlocked(null); }} title="Monthly Attestation" size="lg">
        {attestResult ? (
          attestResult.ok ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 text-emerald-600"><Icons.Check /></div>
              <h3 className="text-lg font-semibold text-slate-900">Attestation Complete</h3>
              <p className="text-sm text-slate-500 mt-2">{attestResult.data?.attestation?.scenario_count || 0} scenarios and {attestResult.data?.attestation?.threshold_count || 0} thresholds attested.</p>
              {attestResult.data?.attestation?.receipt_signed_url && (
                <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-2">Receipt generated with SHA-256 hash:</p>
                  <code className="text-xs text-slate-600 break-all">{attestResult.data?.attestation?.receipt_sha256}</code>
                  <Button variant="secondary" size="sm" className="mt-3" onClick={() => window.open(attestResult.data.attestation.receipt_signed_url, '_blank')}>
                    <Icons.Download /> Download Receipt PDF
                  </Button>
                </div>
              )}
              <Button className="mt-6" onClick={() => { setAttestModal(false); setAttestResult(null); setAttestRigorWarning(null); setAttestRigorBlocked(null); loadAllData(); }}>Done</Button>
            </div>
          ) : (
            <div>
              <div className="bg-red-50 border border-red-100 rounded-lg p-4 mb-4 text-red-800">
                <div className="font-medium flex items-center gap-2"><Icons.Alert /> Attestation Failed</div>
                <p className="text-sm mt-1">{attestResult.error}</p>
              </div>
              <BlockerList blockers={blockers} />
              <Button variant="secondary" className="mt-4" onClick={() => setAttestResult(null)}>Try Again</Button>
            </div>
          )
        ) : (
          <div>
            <BlockerList blockers={blockers} />
            {blockers.length === 0 && (
              <div className="mt-4">
                {/* Changes Since Last Attestation */}
                {(() => {
                  const lastAttestation = attestations[0];
                  const lastAttestDate = lastAttestation ? new Date(lastAttestation.attested_at) : null;
                  
                  const newScenarios = lastAttestDate 
                    ? scenarios.filter(s => new Date(s.created_at) > lastAttestDate)
                    : scenarios;
                  const newThresholds = lastAttestDate 
                    ? thresholds.filter(t => new Date(t.created_at) > lastAttestDate)
                    : thresholds;
                  const updatedScenarios = lastAttestDate 
                    ? scenarios.filter(s => s.updated_at && new Date(s.updated_at) > lastAttestDate && new Date(s.created_at) <= lastAttestDate)
                    : [];
                  const updatedThresholds = lastAttestDate 
                    ? thresholds.filter(t => t.updated_at && new Date(t.updated_at) > lastAttestDate && new Date(t.created_at) <= lastAttestDate)
                    : [];
                  
                  const hasChanges = newScenarios.length > 0 || newThresholds.length > 0 || updatedScenarios.length > 0 || updatedThresholds.length > 0;
                  
                  return hasChanges && (
                    <div className={`mb-4 p-4 rounded-xl border-2 ${darkMode ? "bg-blue-900/20 border-blue-700/50" : "bg-blue-50 border-blue-200"}`}>
                      <div className={`text-sm font-semibold mb-3 flex items-center gap-2 ${darkMode ? "text-blue-300" : "text-blue-700"}`}>
                        <Icons.Clock className="w-4 h-4" />
                        Changes Since Last Attestation {lastAttestation && <span className="font-normal text-xs opacity-75">({formatDate(lastAttestation.attested_at)})</span>}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        {newScenarios.length > 0 && (
                          <div className={`p-2 rounded-lg ${darkMode ? "bg-emerald-900/30" : "bg-emerald-100"}`}>
                            <div className="text-lg font-bold text-emerald-600">+{newScenarios.length}</div>
                            <div className={`text-xs ${darkMode ? "text-emerald-300" : "text-emerald-700"}`}>New Scenarios</div>
                          </div>
                        )}
                        {newThresholds.length > 0 && (
                          <div className={`p-2 rounded-lg ${darkMode ? "bg-emerald-900/30" : "bg-emerald-100"}`}>
                            <div className="text-lg font-bold text-emerald-600">+{newThresholds.length}</div>
                            <div className={`text-xs ${darkMode ? "text-emerald-300" : "text-emerald-700"}`}>New Thresholds</div>
                          </div>
                        )}
                        {updatedScenarios.length > 0 && (
                          <div className={`p-2 rounded-lg ${darkMode ? "bg-amber-900/30" : "bg-amber-100"}`}>
                            <div className="text-lg font-bold text-amber-600">{updatedScenarios.length}</div>
                            <div className={`text-xs ${darkMode ? "text-amber-300" : "text-amber-700"}`}>Updated Scenarios</div>
                          </div>
                        )}
                        {updatedThresholds.length > 0 && (
                          <div className={`p-2 rounded-lg ${darkMode ? "bg-amber-900/30" : "bg-amber-100"}`}>
                            <div className="text-lg font-bold text-amber-600">{updatedThresholds.length}</div>
                            <div className={`text-xs ${darkMode ? "text-amber-300" : "text-amber-700"}`}>Updated Thresholds</div>
                          </div>
                        )}
                      </div>
                      
                      {/* List of changes */}
                      <details className="mt-3">
                        <summary className={`text-xs cursor-pointer ${darkMode ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-700"}`}>
                          View all changes ({newScenarios.length + newThresholds.length + updatedScenarios.length + updatedThresholds.length} items)
                        </summary>
                        <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                          {newScenarios.map((s, i) => (
                            <div key={`ns-${i}`} className={`text-xs px-2 py-1 rounded flex items-center gap-2 ${darkMode ? "bg-emerald-900/30" : "bg-emerald-50"}`}>
                              <span className="text-emerald-600 font-medium">NEW</span>
                              <span className={darkMode ? "text-slate-300" : "text-slate-600"}>{s.name}</span>
                            </div>
                          ))}
                          {newThresholds.map((t, i) => (
                            <div key={`nt-${i}`} className={`text-xs px-2 py-1 rounded flex items-center gap-2 ${darkMode ? "bg-emerald-900/30" : "bg-emerald-50"}`}>
                              <span className="text-emerald-600 font-medium">NEW</span>
                              <span className={darkMode ? "text-slate-300" : "text-slate-600"}>{t.name}</span>
                            </div>
                          ))}
                          {updatedScenarios.map((s, i) => (
                            <div key={`us-${i}`} className={`text-xs px-2 py-1 rounded flex items-center gap-2 ${darkMode ? "bg-amber-900/30" : "bg-amber-50"}`}>
                              <span className="text-amber-600 font-medium">UPDATED</span>
                              <span className={darkMode ? "text-slate-300" : "text-slate-600"}>{s.name}</span>
                            </div>
                          ))}
                          {updatedThresholds.map((t, i) => (
                            <div key={`ut-${i}`} className={`text-xs px-2 py-1 rounded flex items-center gap-2 ${darkMode ? "bg-amber-900/30" : "bg-amber-50"}`}>
                              <span className="text-amber-600 font-medium">UPDATED</span>
                              <span className={darkMode ? "text-slate-300" : "text-slate-600"}>{t.name}</span>
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  );
                })()}
                
                {/* Attestation Preview Section */}
                <div className={`mb-6 p-4 rounded-xl border ${darkMode ? "bg-slate-800 border-slate-700" : "bg-slate-50 border-slate-200"}`}>
                  <div className={`text-sm font-semibold mb-3 flex items-center gap-2 ${darkMode ? "text-slate-200" : "text-slate-700"}`}>
                    <Icons.File className="w-4 h-4" />
                    Attestation Summary
                  </div>
                  
                  {/* Counts */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className={`p-3 rounded-lg ${darkMode ? "bg-slate-700" : "bg-white"} text-center`}>
                      <div className={`text-2xl font-bold ${darkMode ? "text-blue-400" : "text-blue-600"}`}>{scenarios.length}</div>
                      <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Scenarios</div>
                    </div>
                    <div className={`p-3 rounded-lg ${darkMode ? "bg-slate-700" : "bg-white"} text-center`}>
                      <div className={`text-2xl font-bold ${darkMode ? "text-emerald-400" : "text-emerald-600"}`}>{thresholds.length}</div>
                      <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Thresholds</div>
                    </div>
                  </div>
                  
                  {/* Quality Score */}
                  {attestationReadiness.overallScore !== undefined && (
                    <div className={`p-3 rounded-lg mb-3 ${
                      attestationReadiness.overallScore >= 85 
                        ? (darkMode ? "bg-emerald-900/30" : "bg-emerald-50")
                        : attestationReadiness.overallScore >= 70
                          ? (darkMode ? "bg-amber-900/30" : "bg-amber-50")
                          : (darkMode ? "bg-red-900/30" : "bg-red-50")
                    }`}>
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Quality Score</span>
                        <span className={`text-lg font-bold ${
                          attestationReadiness.overallScore >= 85 
                            ? "text-emerald-600" 
                            : attestationReadiness.overallScore >= 70 
                              ? "text-amber-600" 
                              : "text-red-600"
                        }`}>
                          {attestationReadiness.overallScore}%
                        </span>
                      </div>
                      <div className={`text-xs mt-1 ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                        {attestationReadiness.overallScore >= 85 
                          ? "Examiner-ready documentation" 
                          : attestationReadiness.overallScore >= 70 
                            ? "Meets minimum threshold" 
                            : "Below required threshold (70%)"}
                      </div>
                    </div>
                  )}
                  
                  {/* Items being attested */}
                  <details className="mt-3">
                    <summary className={`text-sm cursor-pointer ${darkMode ? "text-slate-500 hover:text-slate-300" : "text-slate-500 hover:text-slate-700"}`}>
                      View items being attested ({scenarios.length + thresholds.length} total)
                    </summary>
                    <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                      {scenarios.slice(0, 10).map((s, i) => (
                        <div key={i} className={`text-xs px-2 py-1 rounded flex items-center gap-2 ${darkMode ? "bg-slate-700" : "bg-white"}`}>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${darkMode ? "bg-blue-900 text-blue-300" : "bg-blue-100 text-blue-700"}`}>S</span>
                          <span className={`truncate ${darkMode ? "text-slate-300" : "text-slate-600"}`}>{s.name}</span>
                        </div>
                      ))}
                      {scenarios.length > 10 && (
                        <div className={`text-xs px-2 ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                          ...and {scenarios.length - 10} more scenarios
                        </div>
                      )}
                      {thresholds.slice(0, 10).map((t, i) => (
                        <div key={i} className={`text-xs px-2 py-1 rounded flex items-center gap-2 ${darkMode ? "bg-slate-700" : "bg-white"}`}>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${darkMode ? "bg-emerald-900 text-emerald-300" : "bg-emerald-100 text-emerald-700"}`}>T</span>
                          <span className={`truncate ${darkMode ? "text-slate-300" : "text-slate-600"}`}>{t.name}</span>
                        </div>
                      ))}
                      {thresholds.length > 10 && (
                        <div className={`text-xs px-2 ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                          ...and {thresholds.length - 10} more thresholds
                        </div>
                      )}
                    </div>
                  </details>
                </div>
                
                <p className="text-sm text-slate-600 mb-4">By attesting, you confirm all governance objects meet compliance requirements for this period.</p>
                {attestRigorBlocked && (
                  <div className="bg-red-50 border border-red-100 rounded-lg p-3 text-sm text-red-800 mb-3">
                    Rigor check blocked attestation: {attestRigorBlocked.blocked.length} item(s) scored 1/3. Improve content before attesting.
                  </div>
                )}
                {attestRigorWarning && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 mb-4">
                    <div className="flex items-start gap-3">
                      <Icons.Alert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-medium">Documentation Quality Warning</div>
                        <p className="text-sm mt-1">
                          {attestRigorWarning.warning.length} item(s) scored 2/3 on the rigor check. 
                          These may not fully satisfy examiner scrutiny.
                        </p>
                        <div className="mt-2 text-sm">
                          <strong>Items with warnings:</strong>
                          <ul className="mt-1 ml-4 list-disc">
                            {attestRigorWarning.warning.slice(0, 5).map((item, i) => (
                              <li key={i}>{item.name || item.key}</li>
                            ))}
                            {attestRigorWarning.warning.length > 5 && (
                              <li className="text-amber-600">...and {attestRigorWarning.warning.length - 5} more</li>
                            )}
                          </ul>
                        </div>
                        <div className="mt-3 p-2 bg-amber-100 rounded text-xs">
                          <strong>Recommended:</strong> Click "Review Items" below to improve documentation before attesting.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <Button onClick={handleAttest} disabled={attestRigorLoading || rigorBatchRunning}>
                    <Icons.Check /> {attestRigorLoading ? "Checking Rigor..." : "Confirm Attestation"}
                  </Button>
                  {attestRigorWarning && (
                    <>
                      <Button variant="secondary" onClick={() => { setAttestModal(false); setActiveTab("governance"); }}>
                        Review Items
                      </Button>
                      <Button 
                        variant="danger" 
                        onClick={() => {
                          showConfirmModal({
                            title: "⚠️ Attest with Quality Warnings",
                            message: `You are attesting despite ${attestRigorWarning.warning.length} item(s) with quality warnings. This will be logged in the audit trail. An examiner may ask why you attested with known documentation gaps.`,
                            confirmText: "Proceed with Attestation",
                            confirmVariant: "danger",
                            isDestructive: true,
                            onConfirm: async () => {
                              await performAttest();
                            }
                          });
                        }} 
                        disabled={attestRigorLoading}
                      >
                        Proceed Despite Warnings
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal darkMode={darkMode} open={scenarioModal} onClose={() => { setScenarioModal(false); setScenarioEditingId(null); resetScenarioForm(); }} title={scenarioEditingId ? "Edit Scenario" : "New Scenario"} size="xl" disableBackdropClose={true}>
        <div className="grid md:grid-cols-2 gap-6">
          {/* LEFT COLUMN: Data Primitives (WHO, WHAT, WHEN, WHY) */}
          <div className={`space-y-4 p-4 rounded-lg ${darkMode ? "bg-slate-800/50" : "bg-slate-50"}`}>
            <div className={`pb-3 border-b ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
              <div className="flex items-center gap-2">
                <div className={`text-sm font-bold ${darkMode ? "text-blue-400" : "text-blue-600"}`}>📊 Data Primitives (Required)</div>
                <HelpTooltip 
                  helpKey="dataPrimitives"
                  tooltipContent="The Four Questions that form the core of every governance object: WHO made the decision, WHAT it is, WHEN it was made, and WHY that specific value was chosen. These are required for examiner readiness."
                  darkMode={darkMode}
                />
              </div>
              <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>WHO • WHAT • WHEN • WHY</div>
            </div>
            
            {/* WHAT */}
            <div className={`p-3 rounded-lg border ${darkMode ? "bg-slate-700/50 border-slate-600" : "bg-white border-slate-200"}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${darkMode ? "bg-blue-600 text-white" : "bg-blue-500 text-white"}`}>WHAT</span>
              </div>
              <div className="space-y-3">
                <div>
                  <label htmlFor="scenario-form-name" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Name *</label>
                  <input id="scenario-form-name" aria-label="Scenario name - required field" aria-required="true" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} placeholder="CTR Filing Scenario" value={scenarioForm.name} onChange={e => setScenarioForm(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div>
                  <label htmlFor="scenario-form-description" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Description *</label>
                  <textarea id="scenario-form-description" aria-label="Scenario description - required field" aria-required="true" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} rows={2} placeholder="Monitor cash transactions over $10,000 for CTR filing" value={scenarioForm.description} onChange={e => setScenarioForm(f => ({ ...f, description: e.target.value }))} />
                </div>
              </div>
            </div>

            {/* WHO */}
            <div className={`p-3 rounded-lg border ${darkMode ? "bg-slate-700/50 border-slate-600" : "bg-white border-slate-200"}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${darkMode ? "bg-green-600 text-white" : "bg-green-500 text-white"}`}>WHO</span>
              </div>
              <div>
                <label htmlFor="scenario-form-owner" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Owner Email *</label>
                <input id="scenario-form-owner" aria-label="Owner email address - required field" aria-required="true" type="email" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} placeholder="bsa.officer@bank.com" value={scenarioForm.owner_email} onChange={e => setScenarioForm(f => ({ ...f, owner_email: e.target.value }))} />
              </div>
            </div>

            {/* WHEN */}
            <div className={`p-3 rounded-lg border ${darkMode ? "bg-slate-700/50 border-slate-600" : "bg-white border-slate-200"}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${darkMode ? "bg-purple-600 text-white" : "bg-purple-500 text-white"}`}>WHEN</span>
              </div>
              <div>
                <label htmlFor="scenario-form-review-date" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Next Review Date *</label>
                <input id="scenario-form-review-date" aria-label="Next review date - required field" aria-required="true" type="date" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} value={scenarioForm.next_review_date} onChange={e => setScenarioForm(f => ({ ...f, next_review_date: e.target.value }))} />
              </div>
            </div>

            {/* WHY - The critical field */}
            <div className={`p-3 rounded-lg border-2 ${darkMode ? "bg-amber-900/30 border-amber-600" : "bg-amber-50 border-amber-300"}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${darkMode ? "bg-amber-600 text-white" : "bg-amber-500 text-white"}`}>WHY</span>
                  <span className={`text-xs ${darkMode ? "text-amber-300" : "text-amber-600"}`}>Critical for examiner readiness</span>
                </div>
                <button
                  type="button"
                  onClick={() => setWhyBuilderModal({ open: true, type: "scenario", target: "regulatory_basis" })}
                  className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-all ${
                    darkMode 
                      ? "bg-amber-600 hover:bg-amber-500 text-white" 
                      : "bg-amber-500 hover:bg-amber-600 text-white"
                  }`}
                >
                  🎯 Build WHY
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <label htmlFor="scenario-form-rationale" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Rationale & Data Analysis *</label>
                  <p className={`text-xs mb-1 ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Include your data (e.g., "87% of suspicious activity below $5K") and regulatory citation</p>
                  <textarea id="scenario-form-rationale" aria-label="Rationale and data analysis - required field" aria-required="true" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} rows={3} placeholder="Based on 24-month transaction analysis showing 87% of suspicious activity below $5,000, we set threshold at $3,000 per FinCEN Advisory FIN-2021-A001..." value={scenarioForm.regulatory_basis} onChange={e => setScenarioForm(f => ({ ...f, regulatory_basis: e.target.value }))} />
                </div>
                <div>
                  <label htmlFor="scenario-form-alternatives" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Alternatives Considered *</label>
                  <textarea id="scenario-form-alternatives" aria-label="Alternatives considered - required field" aria-required="true" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} rows={2} placeholder="We considered $5,000 but rejected due to 34% detection gap" value={scenarioForm.alternatives_considered} onChange={e => setScenarioForm(f => ({ ...f, alternatives_considered: e.target.value }))} />
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Extensions (WHERE, AUTHORITY, EVIDENCE, REVIEW) */}
          <div className={`space-y-4 p-4 rounded-lg ${darkMode ? "bg-slate-800/50" : "bg-slate-50"}`}>
            <div className={`pb-3 border-b ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
              <div className="flex items-center gap-2">
                <div className={`text-sm font-bold ${darkMode ? "text-slate-500" : "text-slate-600"}`}>📎 Extensions (Highly Recommended)</div>
                <HelpTooltip 
                  helpKey="extensions"
                  tooltipContent="The Four Extensions provide supporting context: WHERE is the documentation, under what AUTHORITY was this approved, what EVIDENCE proves implementation, and when is the next REVIEW. While not strictly required, these dramatically increase examiner confidence."
                  darkMode={darkMode}
                />
              </div>
              <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>WHERE • AUTHORITY • EVIDENCE • REVIEW</div>
            </div>

            {/* WHERE */}
            <div className={`p-3 rounded-lg border ${darkMode ? "bg-slate-700/50 border-slate-600" : "bg-white border-slate-200"}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${darkMode ? "bg-slate-600 text-white" : "bg-slate-500 text-white"}`}>WHERE</span>
              </div>
              <div>
                <label htmlFor="scenario-form-doc-location" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Documentation Location</label>
                <input id="scenario-form-doc-location" aria-label="Documentation Location" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} placeholder="BSA Policy Manual Section 4.2" value={scenarioForm.documentation_location} onChange={e => setScenarioForm(f => ({ ...f, documentation_location: e.target.value }))} />
              </div>
            </div>

            {/* AUTHORITY */}
            <div className={`p-3 rounded-lg border ${darkMode ? "bg-slate-700/50 border-slate-600" : "bg-white border-slate-200"}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${darkMode ? "bg-slate-600 text-white" : "bg-slate-500 text-white"}`}>AUTHORITY</span>
              </div>
              <div>
                <label htmlFor="scenario-form-authority" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Regulatory Citation</label>
                <input id="scenario-form-authority" aria-label="Regulatory Citation" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} placeholder="31 CFR 1010.311" value={scenarioForm.authority_citation} onChange={e => setScenarioForm(f => ({ ...f, authority_citation: e.target.value }))} />
              </div>
            </div>

            {/* EVIDENCE */}
            <div className={`p-3 rounded-lg border ${darkMode ? "bg-slate-700/50 border-slate-600" : "bg-white border-slate-200"}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${darkMode ? "bg-slate-600 text-white" : "bg-slate-500 text-white"}`}>EVIDENCE</span>
              </div>
              <div>
                <label htmlFor="scenario-form-evidence" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Evidence Summary</label>
                <p className={`text-xs mb-1 ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Supporting documents/analysis</p>
                <textarea id="scenario-form-evidence" aria-label="Evidence Summary" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} rows={2} placeholder="24-month SAR analysis, Board minutes 2024-01-15" value={scenarioForm.evidence_summary} onChange={e => setScenarioForm(f => ({ ...f, evidence_summary: e.target.value }))} />
              </div>
            </div>

            {/* REVIEW */}
            <div className={`p-3 rounded-lg border ${darkMode ? "bg-slate-700/50 border-slate-600" : "bg-white border-slate-200"}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${darkMode ? "bg-slate-600 text-white" : "bg-slate-500 text-white"}`}>REVIEW</span>
              </div>
              <div className="space-y-3">
                <div>
                  <label htmlFor="scenario-form-review-freq" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Review Frequency</label>
                  <input id="scenario-form-review-freq" aria-label="Review Frequency" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} placeholder="Annual" value={scenarioForm.review_frequency} onChange={e => setScenarioForm(f => ({ ...f, review_frequency: e.target.value }))} />
                </div>
                <div>
                  <label htmlFor="scenario-form-risk-level" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Risk Level</label>
                  <select id="scenario-form-risk-level" aria-label="Select scenario risk level" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} value={scenarioForm.risk_level} onChange={e => setScenarioForm(f => ({ ...f, risk_level: e.target.value }))}>
                    {["low", "medium", "high"].map(level => <option key={level} value={level}>{level}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className={`flex items-center justify-end gap-2 mt-6 pt-4 border-t ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
          <Button variant="secondary" onClick={() => { setScenarioModal(false); setScenarioEditingId(null); resetScenarioForm(); }}>Cancel</Button>
          <Button onClick={saveScenario} disabled={scenarioSaving}>{scenarioSaving ? "Saving..." : scenarioEditingId ? "Save Changes" : "Create Scenario"}</Button>
        </div>
      </Modal>

      <Modal darkMode={darkMode} open={thresholdModal} onClose={() => { setThresholdModal(false); setThresholdEditingId(null); resetThresholdForm(); }} title={thresholdEditingId ? "Edit Threshold" : "New Threshold"} size="xl" disableBackdropClose={true}>
        <div className="grid md:grid-cols-2 gap-6">
          {/* LEFT COLUMN: Data Primitives (WHO, WHAT, WHEN, WHY) */}
          <div className={`space-y-4 p-4 rounded-lg ${darkMode ? "bg-slate-800/50" : "bg-slate-50"}`}>
            <div className={`pb-3 border-b ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
              <div className="flex items-center gap-2">
                <div className={`text-sm font-bold ${darkMode ? "text-blue-400" : "text-blue-600"}`}>📊 Data Primitives (Required)</div>
                <HelpTooltip 
                  helpKey="dataPrimitives"
                  tooltipContent="The Four Questions that form the core of every governance object: WHO made the decision, WHAT it is, WHEN it was made, and WHY that specific value was chosen. These are required for examiner readiness."
                  darkMode={darkMode}
                />
              </div>
              <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>WHO • WHAT • WHEN • WHY</div>
            </div>
            
            {/* WHAT */}
            <div className={`p-3 rounded-lg border ${darkMode ? "bg-slate-700/50 border-slate-600" : "bg-white border-slate-200"}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${darkMode ? "bg-blue-600 text-white" : "bg-blue-500 text-white"}`}>WHAT</span>
              </div>
              <div className="space-y-3">
                <div>
                  <label htmlFor="threshold-form-name" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Name *</label>
                  <input id="threshold-form-name" aria-label="Threshold name - required field" aria-required="true" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} placeholder="Enhanced Review Threshold" value={thresholdForm.name} onChange={e => setThresholdForm(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div>
                  <label htmlFor="threshold-form-description" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Description *</label>
                  <textarea id="threshold-form-description" aria-label="Threshold description - required field" aria-required="true" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} rows={2} placeholder="Dollar amount triggering enhanced review" value={thresholdForm.description} onChange={e => setThresholdForm(f => ({ ...f, description: e.target.value }))} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="threshold-form-value" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Value *</label>
                    <input id="threshold-form-value" aria-label="Threshold value - required field" aria-required="true" type="number" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} placeholder="3000" value={thresholdForm.current_value} onChange={e => setThresholdForm(f => ({ ...f, current_value: e.target.value }))} />
                  </div>
                  <div>
                    <label htmlFor="threshold-form-unit" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Unit</label>
                    <input id="threshold-form-unit" aria-label="Unit of measurement" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} placeholder="USD" value={thresholdForm.unit} onChange={e => setThresholdForm(f => ({ ...f, unit: e.target.value }))} />
                  </div>
                </div>
              </div>
            </div>

            {/* WHO */}
            <div className={`p-3 rounded-lg border ${darkMode ? "bg-slate-700/50 border-slate-600" : "bg-white border-slate-200"}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${darkMode ? "bg-green-600 text-white" : "bg-green-500 text-white"}`}>WHO</span>
              </div>
              <div>
                <label htmlFor="threshold-form-owner" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Owner Email *</label>
                <input id="threshold-form-owner" aria-label="Owner email address - required field" aria-required="true" type="email" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} placeholder="bsa.officer@bank.com" value={thresholdForm.owner_email} onChange={e => setThresholdForm(f => ({ ...f, owner_email: e.target.value }))} />
              </div>
            </div>

            {/* WHEN */}
            <div className={`p-3 rounded-lg border ${darkMode ? "bg-slate-700/50 border-slate-600" : "bg-white border-slate-200"}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${darkMode ? "bg-purple-600 text-white" : "bg-purple-500 text-white"}`}>WHEN</span>
              </div>
              <div>
                <label htmlFor="threshold-form-review-date" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Next Review Date *</label>
                <input id="threshold-form-review-date" aria-label="Next review date - required field" aria-required="true" type="date" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} value={thresholdForm.next_review_date} onChange={e => setThresholdForm(f => ({ ...f, next_review_date: e.target.value }))} />
              </div>
            </div>

            {/* WHY - The critical field */}
            <div className={`p-3 rounded-lg border-2 ${darkMode ? "bg-amber-900/30 border-amber-600" : "bg-amber-50 border-amber-300"}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${darkMode ? "bg-amber-600 text-white" : "bg-amber-500 text-white"}`}>WHY</span>
                  <span className={`text-xs ${darkMode ? "text-amber-300" : "text-amber-600"}`}>Critical for examiner readiness</span>
                </div>
                <button
                  type="button"
                  onClick={() => setWhyBuilderModal({ open: true, type: "threshold", target: "regulatory_basis" })}
                  className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-all ${
                    darkMode 
                      ? "bg-amber-600 hover:bg-amber-500 text-white" 
                      : "bg-amber-500 hover:bg-amber-600 text-white"
                  }`}
                >
                  🎯 Build WHY
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <label htmlFor="threshold-form-rationale" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Rationale & Data Analysis *</label>
                  <p className={`text-xs mb-1 ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Include your data (e.g., "87% of activity below $5K") and regulatory citation</p>
                  <textarea id="threshold-form-rationale" aria-label="Rationale and data analysis - required field" aria-required="true" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} rows={3} placeholder="Based on 24-month transaction analysis showing 87% of suspicious activity below $5,000, we set threshold at $3,000 per FinCEN Advisory FIN-2021-A001..." value={thresholdForm.regulatory_basis} onChange={e => setThresholdForm(f => ({ ...f, regulatory_basis: e.target.value }))} />
                </div>
                <div>
                  <label htmlFor="threshold-form-alternatives" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Alternatives Considered *</label>
                  <textarea id="threshold-form-alternatives" aria-label="Alternatives considered - required field" aria-required="true" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} rows={2} placeholder="We considered $5,000 but rejected due to 34% detection gap" value={thresholdForm.alternatives_considered} onChange={e => setThresholdForm(f => ({ ...f, alternatives_considered: e.target.value }))} />
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Extensions (WHERE, AUTHORITY, EVIDENCE, REVIEW) */}
          <div className={`space-y-4 p-4 rounded-lg ${darkMode ? "bg-slate-800/50" : "bg-slate-50"}`}>
            <div className={`pb-3 border-b ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
              <div className="flex items-center gap-2">
                <div className={`text-sm font-bold ${darkMode ? "text-slate-500" : "text-slate-600"}`}>📎 Extensions (Highly Recommended)</div>
                <HelpTooltip 
                  helpKey="extensions"
                  tooltipContent="The Four Extensions provide supporting context: WHERE is the documentation, under what AUTHORITY was this approved, what EVIDENCE proves implementation, and when is the next REVIEW. While not strictly required, these dramatically increase examiner confidence."
                  darkMode={darkMode}
                />
              </div>
              <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>WHERE • AUTHORITY • EVIDENCE • REVIEW</div>
            </div>

            {/* WHERE */}
            <div className={`p-3 rounded-lg border ${darkMode ? "bg-slate-700/50 border-slate-600" : "bg-white border-slate-200"}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${darkMode ? "bg-slate-600 text-white" : "bg-slate-500 text-white"}`}>WHERE</span>
              </div>
              <div>
                <label htmlFor="threshold-form-doc-location" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Documentation Location</label>
                <input id="threshold-form-doc-location" aria-label="Documentation Location" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} placeholder="BSA Policy Manual Section 4.2" value={thresholdForm.documentation_location} onChange={e => setThresholdForm(f => ({ ...f, documentation_location: e.target.value }))} />
              </div>
            </div>

            {/* AUTHORITY */}
            <div className={`p-3 rounded-lg border ${darkMode ? "bg-slate-700/50 border-slate-600" : "bg-white border-slate-200"}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${darkMode ? "bg-slate-600 text-white" : "bg-slate-500 text-white"}`}>AUTHORITY</span>
              </div>
              <div>
                <label htmlFor="threshold-form-authority" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Regulatory Citation</label>
                <input id="threshold-form-authority" aria-label="Regulatory Citation" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} placeholder="31 CFR 1010.311" value={thresholdForm.authority_citation} onChange={e => setThresholdForm(f => ({ ...f, authority_citation: e.target.value }))} />
              </div>
            </div>

            {/* EVIDENCE */}
            <div className={`p-3 rounded-lg border ${darkMode ? "bg-slate-700/50 border-slate-600" : "bg-white border-slate-200"}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${darkMode ? "bg-slate-600 text-white" : "bg-slate-500 text-white"}`}>EVIDENCE</span>
              </div>
              <div>
                <label htmlFor="threshold-form-evidence" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Evidence Summary</label>
                <p className={`text-xs mb-1 ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Supporting documents/analysis</p>
                <textarea id="threshold-form-evidence" aria-label="Evidence Summary" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} rows={2} placeholder="Threshold calibration spreadsheet, Board minutes" value={thresholdForm.evidence_summary} onChange={e => setThresholdForm(f => ({ ...f, evidence_summary: e.target.value }))} />
              </div>
            </div>

            {/* REVIEW */}
            <div className={`p-3 rounded-lg border ${darkMode ? "bg-slate-700/50 border-slate-600" : "bg-white border-slate-200"}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${darkMode ? "bg-slate-600 text-white" : "bg-slate-500 text-white"}`}>REVIEW</span>
              </div>
              <div className="space-y-3">
                <div>
                  <label htmlFor="threshold-form-review-freq" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Review Frequency</label>
                  <input id="threshold-form-review-freq" aria-label="Review Frequency" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} placeholder="Annual" value={thresholdForm.review_frequency} onChange={e => setThresholdForm(f => ({ ...f, review_frequency: e.target.value }))} />
                </div>
                <div>
                  <label htmlFor="threshold-form-risk-level" className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Risk Level</label>
                  <select id="threshold-form-risk-level" aria-label="Select threshold risk level" className={`mt-1 w-full border rounded-xl px-4 py-2.5 text-sm transition-all duration-200 focus:ring-2 focus:ring-offset-0 ${darkMode ? "bg-slate-700 border-slate-600 text-white focus:border-blue-500 focus:ring-blue-500/20" : "bg-white border-slate-200 text-slate-900 focus:border-blue-500 focus:ring-blue-500/20 hover:border-slate-300"}`} value={thresholdForm.risk_level} onChange={e => setThresholdForm(f => ({ ...f, risk_level: e.target.value }))}>
                    {["low", "medium", "high"].map(level => <option key={level} value={level}>{level}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className={`flex items-center justify-end gap-2 mt-6 pt-4 border-t ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
          <Button variant="secondary" onClick={() => { setThresholdModal(false); setThresholdEditingId(null); resetThresholdForm(); }}>Cancel</Button>
          <Button onClick={saveThreshold} disabled={thresholdSaving}>{thresholdSaving ? "Saving..." : thresholdEditingId ? "Save Changes" : "Create Threshold"}</Button>
        </div>
      </Modal>

      <Modal darkMode={darkMode} open={rigorModal.open} onClose={() => setRigorModal({ open: false, key: null, name: "", result: null })} title="AI Rigor Check" size="lg">
        {(() => {
          const result = rigorModal.result || rigorResults[rigorModal.key];
          const fieldScores = result?.field_scores || result?.fields || {};
          const concerns = result?.examiner_concerns || [];
          const suggestions = result?.suggestions || [];
          const score = result?.overall_score || 0;
          const issueFields = Object.entries(fieldScores).filter(([, v]) => v?.score && v.score < 3);
          
          // Score descriptions
          const scoreLabel = score === 3 ? "Exam Ready" : score === 2 ? "Needs Work" : "Fails Review";
          const scoreColor = score === 3 ? "emerald" : score === 2 ? "amber" : "red";
          const scoreBgClass = score === 3 
            ? (darkMode ? "bg-emerald-900/30 border-emerald-700" : "bg-emerald-50 border-emerald-200")
            : score === 2 
            ? (darkMode ? "bg-amber-900/30 border-amber-700" : "bg-amber-50 border-amber-200")
            : (darkMode ? "bg-red-900/30 border-red-700" : "bg-red-50 border-red-200");
          
          return (
            <div className="space-y-4">
              {/* Overall Score */}
              <div className={`flex items-center justify-between p-4 rounded-lg border ${scoreBgClass}`}>
                <div>
                  <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Item</div>
                  <div className={`font-semibold ${darkMode ? "text-slate-100" : "text-slate-900"}`}>{rigorModal.name}</div>
                </div>
                <div className="text-right">
                  <div className={`text-xl font-bold ${scoreColor === "emerald" ? "text-emerald-500" : scoreColor === "amber" ? "text-amber-500" : "text-red-500"}`}>
                    {scoreLabel}
                  </div>
                  <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                    AI Assessment
                  </div>
                </div>
              </div>

              <div>
                <div className={`text-sm font-medium mb-2 ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Per-field Issues</div>
                {issueFields.length === 0 ? (
                  <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-500"}`}>No issues detected. All fields look examiner-ready.</div>
                ) : (
                  <div className="space-y-2">
                    {issueFields.map(([field, detail]) => {
                      const fieldLabel = detail.score === 2 ? "Needs Work" : "Fails";
                      return (
                        <div key={field} className={`rounded-lg p-3 text-sm border ${darkMode ? "bg-slate-800 border-slate-700" : "bg-slate-50 border-slate-100"}`}>
                          <div className="flex items-center justify-between">
                            <span className={`font-medium capitalize ${darkMode ? "text-slate-200" : "text-slate-800"}`}>{field.replace(/_/g, " ")}</span>
                            <Badge color={detail.score === 2 ? "amber" : "red"}>{fieldLabel}</Badge>
                          </div>
                          {detail.issue && <div className={`mt-1 ${darkMode ? "text-slate-500" : "text-slate-600"}`}>{detail.issue}</div>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div>
                <div className={`text-sm font-medium mb-2 ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Examiner Concerns</div>
                {concerns.length === 0 ? (
                  <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-500"}`}>No concerns flagged.</div>
                ) : (
                  <ul className={`list-disc list-inside text-sm ${darkMode ? "text-slate-300" : "text-slate-600"}`}>
                    {concerns.map((c, i) => <li key={i}>{c}</li>)}
                  </ul>
                )}
              </div>

              <div>
                <div className={`text-sm font-medium mb-2 ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Suggestions to Improve</div>
                {suggestions.length === 0 ? (
                  <div className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-500"}`}>No suggestions provided.</div>
                ) : (
                  <ul className={`list-disc list-inside text-sm ${darkMode ? "text-slate-300" : "text-slate-600"}`}>
                    {suggestions.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                )}
              </div>
            </div>
          );
        })()}
      </Modal>

      <Modal
        open={examinerModal.open}
        onClose={() => setExaminerModal(m => ({ ...m, open: false }))}
        title="Examiner Package Generator"
        size="xl"
      >
        <div className="bg-slate-900 text-slate-200 rounded-xl p-6 border border-slate-700">
          <div className="mb-6">
            <div className="text-2xl font-bold text-slate-100">Examiner Package</div>
            <div className="text-sm text-slate-500">Generate proof packs for regulatory examination</div>
          </div>

          {examinerModal.proofPack && examinerModal.humanPack && (
            <div className="bg-emerald-900/30 border border-emerald-600/40 rounded-xl p-4 mb-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-700/30 rounded-lg flex items-center justify-center text-emerald-400">
                <Icons.Check className="w-6 h-6" />
              </div>
              <div>
                <div className="text-lg font-semibold text-emerald-300">PACKAGE READY</div>
                <div className="text-sm text-emerald-200/80">All components generated successfully. Download your examiner package below.</div>
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
              <div className="text-sm font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <Icons.Shield className="w-4 h-4" /> Package Configuration
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3">
                  <span className="w-24 text-slate-500">As-of Date</span>
                  <input
                    type="date"
                    className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-200"
                    value={examinerModal.asOf}
                    onChange={e => setExaminerModal(m => ({ ...m, asOf: e.target.value }))}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-24 text-slate-500">Scope</span>
                  <div className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-200">
                    All Governance Objects ({scenarios.length} scenarios, {thresholds.length} thresholds)
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <div className="text-sm font-semibold text-slate-100 mb-3">Generation Status</div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 bg-emerald-900/20 border border-emerald-600/30 rounded-xl p-3">
                    <div className="w-10 h-10 bg-emerald-700/30 rounded-lg flex items-center justify-center text-emerald-300">
                      <Icons.Check className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-slate-100">Phase A — Proof Pack</div>
                      <div className="text-xs text-emerald-300">{examinerModal.proofPack ? "Complete" : "Not generated"}</div>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={examinerModal.generating}
                      onClick={() => generateExaminerPack("proof")}
                    >
                      {examinerModal.generating && examinerModal.phase === "proof" ? "Generating..." : "Run"}
                    </Button>
                  </div>

                  <div className="flex items-center gap-3 bg-emerald-900/20 border border-emerald-600/30 rounded-xl p-3">
                    <div className="w-10 h-10 bg-emerald-700/30 rounded-lg flex items-center justify-center text-emerald-300">
                      <Icons.Check className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-slate-100">Phase B — Human Pack</div>
                      <div className="text-xs text-emerald-300">{examinerModal.humanPack ? "Complete" : "Not generated"}</div>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={examinerModal.generating}
                      onClick={() => generateExaminerPack("full")}
                    >
                      {examinerModal.generating && examinerModal.phase === "full" ? "Generating..." : "Run"}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <div className="text-sm font-semibold text-slate-100 mb-3">Included Components</div>
                <div className="space-y-2 text-sm">
                  {[
                    "Manifest (metadata + integrity)",
                    `Governance State (${scenarios.length + thresholds.length} objects as-of date)`,
                    "Change History (placeholder)",
                    `Attestation Records (${attestations.length})`,
                    `Findings Tracker (${importedRules.length})`,
                    "Hash List / Integrity References",
                    `Evidence Bundle (${documents.length} documents)`,
                    "PDF Narrative (Executive Summary)",
                  ].map((label, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2">
                      <span className="w-5 h-5 bg-emerald-700/40 rounded-md flex items-center justify-center text-emerald-300 text-xs">✓</span>
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
                <div className="text-sm font-semibold text-slate-100 mb-4">Download Package</div>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-xl p-4 text-left hover:border-blue-500 transition"
                    onClick={() => examinerModal.proofPack?.signed_url && window.open(normalizeSupabaseSignedUrl(examinerModal.proofPack.signed_url), "_blank")}
                  >
                    <div className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center text-blue-400 mb-3">
                      <Icons.Download className="w-6 h-6" />
                    </div>
                    <div className="text-sm font-semibold text-slate-100">Proof Pack</div>
                    <div className="text-xs text-slate-500">ZIP • hashes + state</div>
                  </button>
                  <button
                    className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-xl p-4 text-left hover:border-blue-500 transition"
                    onClick={() => examinerModal.humanPack?.pdf_signed_url && window.open(normalizeSupabaseSignedUrl(examinerModal.humanPack.pdf_signed_url), "_blank")}
                  >
                    <div className="w-12 h-12 bg-blue-600/20 rounded-lg flex items-center justify-center text-blue-400 mb-3">
                      <Icons.Download className="w-6 h-6" />
                    </div>
                    <div className="text-sm font-semibold text-slate-100">Human Pack</div>
                    <div className="text-xs text-slate-500">PDF + Evidence ZIP</div>
                  </button>
                </div>

                <button
                  className="mt-4 w-full text-blue-400 text-sm font-medium hover:bg-blue-600/10 rounded-lg py-2"
                  onClick={() => {
                    if (examinerModal.proofPack?.merkle_root) {
                      navigator.clipboard?.writeText(examinerModal.proofPack.merkle_root);
                      setToast({ type: "success", message: "Merkle root copied" });
                      setTimeout(() => setToast(null), 2000);
                    }
                  }}
                >
                  Verify Package Integrity
                </button>

                <div className="mt-4 bg-slate-900 border border-slate-700 rounded-lg p-4 text-xs text-slate-500 whitespace-pre-wrap">
{JSON.stringify(examinerModal.meta || {
  generated_at: "pending",
  as_of_date: examinerModal.asOf,
  package_version: "v1",
  reconstruction_method: "versioned_snapshots",
  organization_id: orgId,
  scope: "all",
  hash_algorithm: "SHA-256",
  merkle_root: examinerModal.proofPack?.merkle_root || "pending",
}, null, 2)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Nine Questions Detail Modal - THE BRAND PANEL */}
      <Modal 
        open={eqDetailModal.open} 
        onClose={() => setEqDetailModal({ open: false, item: null, type: null })} 
        title={eqDetailModal.item?.name || "Nine Questions"} 
        size="xl"
      >
        {eqDetailModal.item && (
          <div className="space-y-4">
            {/* Type badge and metadata */}
            <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
              <Badge color={eqDetailModal.type === "scenario" ? "blue" : "amber"}>
                {eqDetailModal.type === "scenario" ? "Scenario" : "Threshold"}
              </Badge>
              {eqDetailModal.type === "threshold" && eqDetailModal.item.current_value && (
                <span className="text-sm text-slate-600">
                  Value: <span className="font-medium text-slate-900">{eqDetailModal.item.current_value} {eqDetailModal.item.unit || ""}</span>
                </span>
              )}
              <span className="text-sm text-slate-500">
                Risk: <span className={`font-medium ${
                  eqDetailModal.item.risk_level === "high" ? "text-red-600" : 
                  eqDetailModal.item.risk_level === "low" ? "text-emerald-600" : "text-amber-600"
                }`}>{eqDetailModal.item.risk_level || "medium"}</span>
              </span>
            </div>
            
            {/* Nine Questions Panel */}
            <NineQuestionsPanel 
              item={eqDetailModal.item}
              onEdit={(field) => {
                // Close detail modal and open edit modal
                setEqDetailModal({ open: false, item: null, type: null });
                if (eqDetailModal.type === "scenario") {
                  openScenarioEdit(eqDetailModal.item);
                } else {
                  openThresholdEdit(eqDetailModal.item);
                }
              }}
            />
            
            {/* Action buttons */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2">
                {(() => {
                  const key = getRigorKey(eqDetailModal.type, eqDetailModal.item.id);
                  const result = rigorResults[key];
                  return (
                    <>
                      <Button 
                        variant="secondary" 
                        size="sm"
                        onClick={() => {
                          runRigorCheck(eqDetailModal.type, eqDetailModal.item);
                        }}
                        disabled={rigorCheckingKey === key}
                      >
                        {rigorCheckingKey === key ? "Checking..." : "Check Rigor"}
                      </Button>
                      {result && (
                        <span className="text-sm text-slate-500">
                          Rigor: {getRigorBadge(result)}
                        </span>
                      )}
                    </>
                  );
                })()}
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="secondary"
                  onClick={() => setEqDetailModal({ open: false, item: null, type: null })}
                >
                  Close
                </Button>
                <Button 
                  variant="primary"
                  onClick={() => {
                    setEqDetailModal({ open: false, item: null, type: null });
                    if (eqDetailModal.type === "scenario") {
                      openScenarioEdit(eqDetailModal.item);
                    } else {
                      openThresholdEdit(eqDetailModal.item);
                    }
                  }}
                >
                  Edit {eqDetailModal.type === "scenario" ? "Scenario" : "Threshold"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Add Regulatory Feed Modal */}
      <Modal darkMode={darkMode} open={addFeedModal} onClose={() => setAddFeedModal(false)} title="Add Regulatory Feed" size="md">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Feed Name</label>
            <input
              type="text"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              placeholder="e.g., FinCEN Advisories"
              value={newFeedForm.name}
              onChange={e => setNewFeedForm({ ...newFeedForm, name: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Feed URL</label>
            <input
              type="url"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              placeholder="https://example.gov/rss/feed.xml"
              value={newFeedForm.url}
              onChange={e => setNewFeedForm({ ...newFeedForm, url: e.target.value })}
            />
          </div>
          <div className="text-xs text-slate-500">
            The feed will be polled automatically for new regulatory updates. Only HTTPS URLs are allowed.
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => { setAddFeedModal(false); setNewFeedForm({ name: "", url: "" }); }}>Cancel</Button>
            <Button variant="primary" onClick={addRegulatoryFeed} disabled={addingFeed || !newFeedForm.name || !newFeedForm.url}>
              {addingFeed ? "Adding..." : "Add Feed"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal darkMode={darkMode} open={deleteModal.open} onClose={() => setDeleteModal({ open: false, type: null, id: null, name: "" })} title={`Delete ${deleteModal.type ? deleteModal.type.charAt(0).toUpperCase() + deleteModal.type.slice(1) : "Item"}`} size="sm">
        <div className="space-y-4">
          <p className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-600"}`}>
            {deleteModal.type === "webhook" 
              ? <>Are you sure you want to delete <span className={`font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>{deleteModal.name}</span>? This action cannot be undone.</>
              : <>This will archive <span className={`font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>{deleteModal.name}</span> and remove it from active governance items.</>
            }
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteModal({ open: false, type: null, id: null, name: "" })}>Cancel</Button>
            <Button variant="danger" onClick={confirmDelete} disabled={deletingItem}>{deletingItem ? "Deleting..." : "Delete"}</Button>
          </div>
        </div>
      </Modal>

      {/* Generic Confirmation Modal (replaces browser confirm()) */}
      <Modal 
        darkMode={darkMode} 
        open={confirmModal.open} 
        onClose={() => setConfirmModal({ open: false, title: "", message: "", confirmText: "Confirm", confirmVariant: "primary", onConfirm: null, isDestructive: false })} 
        title={confirmModal.title} 
        size="sm"
      >
        <div className="space-y-4">
          {confirmModal.isDestructive && (
            <div className={`flex items-start gap-3 p-3 rounded-xl ${darkMode ? "bg-red-900/20 border border-red-800/50" : "bg-red-50 border border-red-200"}`}>
              <Icons.Alert className={`w-5 h-5 flex-shrink-0 mt-0.5 ${darkMode ? "text-red-400" : "text-red-500"}`} />
              <div className={`text-sm ${darkMode ? "text-red-300" : "text-red-700"}`}>
                This action cannot be undone. Please make sure you want to proceed.
              </div>
            </div>
          )}
          <p className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-600"}`}>
            {confirmModal.message}
          </p>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button 
              variant="secondary" 
              onClick={() => setConfirmModal({ open: false, title: "", message: "", confirmText: "Confirm", confirmVariant: "primary", onConfirm: null, isDestructive: false })}
            >
              Cancel
            </Button>
            <Button 
              variant={confirmModal.confirmVariant} 
              onClick={handleConfirmModalConfirm} 
              disabled={confirmLoading}
            >
              {confirmLoading ? "Processing..." : confirmModal.confirmText}
            </Button>
          </div>
        </div>
      </Modal>
      
      {/* Regulatory Review Modal - Human-in-the-Loop Workflow */}
      <Modal 
        open={regReviewModal.open} 
        onClose={() => setRegReviewModal({ open: false, update: null })} 
        title="Review Regulatory Update" 
        size="xl"
      >
        {regReviewModal.update && (
          <div className="space-y-6">
            {/* Status Banner */}
            <div className={`p-3 rounded-lg ${
              regReviewModal.update.review_status === "pending" ? "bg-amber-50 border border-amber-200" :
              regReviewModal.update.review_status === "snoozed" ? "bg-blue-50 border border-blue-200" :
              "bg-emerald-50 border border-emerald-200"
            }`}>
              <div className="flex items-center justify-between">
                <Badge color={
                  regReviewModal.update.review_status === "pending" ? "amber" :
                  regReviewModal.update.review_status === "snoozed" ? "blue" : "emerald"
                }>
                  {regReviewModal.update.review_status === "pending" ? "Pending Review" : 
                   regReviewModal.update.review_status === "snoozed" ? "Snoozed" : "Reviewed"}
                </Badge>
                {regReviewModal.update.snooze_until && (
                  <span className="text-sm text-slate-500">
                    Returns: {formatDate(regReviewModal.update.snooze_until)}
                  </span>
                )}
              </div>
            </div>
            
            {/* Title and Source */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900">{regReviewModal.update.title}</h3>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <Badge color="slate">{regReviewModal.update.source || "Unknown Source"}</Badge>
                <span className="text-sm text-slate-500">{formatDate(regReviewModal.update.published_at)}</span>
                {regReviewModal.update.impact_guess && (
                  <Badge color={
                    regReviewModal.update.impact_guess === "high" ? "red" :
                    regReviewModal.update.impact_guess === "medium" ? "amber" : "slate"
                  }>
                    {regReviewModal.update.impact_guess} impact
                  </Badge>
                )}
              </div>
            </div>
            
            {/* AI Summary Section - Modern gradient design */}
            <div className="bg-gradient-to-br from-slate-50 via-indigo-50/50 to-blue-50/30 border border-indigo-100 rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                  <Icons.Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">AI Analysis</h4>
                  <p className="text-xs text-slate-500">Powered by Steadfastly Intelligence</p>
                </div>
                {regReviewLoading && <Icons.Clock className="w-4 h-4 animate-spin text-indigo-500 ml-auto" />}
              </div>
              
              {regReviewLoading ? (
                <div className="flex items-center gap-3 py-4">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: "0.1s" }} />
                  <div className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }} />
                  <span className="text-sm text-slate-600 ml-2">Analyzing regulatory update...</span>
                </div>
              ) : regAISummary ? (
                <div className="space-y-4">
                  <div>
                    <div className="text-xs font-semibold text-indigo-600 uppercase tracking-wide mb-2">Plain English Summary</div>
                    <p className="text-sm text-slate-700 leading-relaxed">{regAISummary.summary}</p>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-slate-200/50 shadow-sm">
                    <div className="text-xs font-semibold text-indigo-600 uppercase tracking-wide mb-2">AI Recommendation</div>
                    <p className="text-sm text-slate-900 font-medium leading-relaxed">{regAISummary.recommendation}</p>
                  </div>
                  {regAISummary.affected_domains?.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-indigo-600 uppercase tracking-wide mb-2">Affected Domains</div>
                      <div className="flex flex-wrap gap-2">
                        {regAISummary.affected_domains.map((domain, i) => (
                          <Badge key={i} color="indigo">{domain}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {regAISummary.key_takeaways?.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-indigo-600 uppercase tracking-wide mb-2">Key Takeaways</div>
                      <ul className="space-y-2">
                        {regAISummary.key_takeaways.map((takeaway, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                            <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-0.5">{i + 1}</span>
                            {takeaway}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {regAISummary.urgency_level && (
                    <div className="flex items-center gap-2 pt-2 border-t border-slate-200/50">
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Urgency:</span>
                      <Badge color={
                        regAISummary.urgency_level === "high" ? "red" :
                        regAISummary.urgency_level === "medium" ? "amber" : "slate"
                      }>
                        {regAISummary.urgency_level}
                      </Badge>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-slate-500 py-4 text-center">AI analysis will appear after processing</div>
              )}
            </div>
            
            {/* Rule Extraction Section - PROMINENT */}
            <div className="border border-blue-200 rounded-2xl p-5 bg-gradient-to-br from-blue-50 via-indigo-50/30 to-white shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-lg shadow-blue-500/20">
                    <Icons.Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-lg">Extract Actionable Rules</h4>
                    <p className="text-xs text-slate-500">AI-powered policy extraction</p>
                  </div>
                </div>
                <button 
                  onClick={extractRulesFromUpdate}
                  disabled={extractingRules}
                  className={`px-5 py-2.5 rounded-xl font-semibold text-white transition-all flex items-center gap-2 ${
                    extractingRules 
                      ? "bg-blue-400 cursor-wait" 
                      : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30"
                  }`}
                >
                  {extractingRules ? (
                    <><Icons.Clock className="w-5 h-5 animate-spin" /> Extracting...</>
                  ) : (
                    <><Icons.Sparkles className="w-5 h-5" /> Extract Rules</>
                  )}
                </button>
              </div>
              
              {/* AI Extracted Rules - HITL Editable Cards */}
              {extractedRules.length > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-indigo-800 font-medium">
                      🤖 AI found {extractedRules.length} rule(s). Add more if needed.
                    </p>
                    <span className="text-xs text-slate-500">
                      {extractedRules.filter(r => r.isSelected).length} selected
                    </span>
                  </div>
                  
                  {/* Nudge: AI is a starting point */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <p className="text-xs text-amber-800">
                      💡 <strong>Copy and paste from the original source</strong> if AI missed important details. 
                      AI doesn't always find everything.
                    </p>
                  </div>
                  
                  <div className="max-h-80 overflow-y-auto space-y-3">
                    {extractedRules.map(rule => (
                      <div key={rule.id} className={`bg-white rounded-lg border-2 ${rule.isSelected ? "border-indigo-400 shadow-sm" : "border-slate-200"} p-4`}>
                        {/* Header: AI Badge + Type + Confidence */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {!rule.isEdited && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                🤖 AI Extracted
                              </span>
                            )}
                            {rule.isEdited && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                ✓ Human Edited
                              </span>
                            )}
                            <Badge color={
                              rule.rule_type === "requirement" ? "blue" :
                              rule.rule_type === "deadline" ? "red" :
                              rule.rule_type === "threshold" ? "amber" :
                              rule.rule_type === "prohibition" ? "red" : "slate"
                            }>
                              {rule.rule_type || "requirement"}
                            </Badge>
                          </div>
                          {rule.confidence_score && (
                            <span className="text-xs text-slate-500">
                              {Math.round(rule.confidence_score * 100)}% confidence
                            </span>
                          )}
                        </div>
                        
                        {/* EDITABLE Text Field - Always shown as textarea */}
                        <div className="mb-3">
                          <textarea
                            value={rule.rule_text}
                            onChange={e => setExtractedRules(prev => prev.map(r => 
                              r.id === rule.id ? { ...r, rule_text: e.target.value, isEdited: true } : r
                            ))}
                            className="w-full border border-slate-300 rounded-lg p-3 text-sm text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                            rows={3}
                            placeholder="Type or paste the rule text here..."
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            💡 Edit directly above or paste from your source document
                          </p>
                        </div>
                        
                        {/* Citation field (editable) */}
                        <div className="mb-3">
                          <label className="block text-xs font-medium text-slate-500 mb-1">Citation / Reference</label>
                          <input
                            type="text"
                            value={rule.citation || ""}
                            onChange={e => setExtractedRules(prev => prev.map(r => 
                              r.id === rule.id ? { ...r, citation: e.target.value, isEdited: true } : r
                            ))}
                            className="w-full border border-slate-200 rounded px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="e.g., 31 CFR 1010.311, FinCEN FIN-2024-A001"
                          />
                        </div>
                        
                        {/* Accept / Reject Buttons */}
                        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={rule.isSelected}
                              onChange={() => toggleRuleSelection(rule.id)}
                              className="rounded text-indigo-500 focus:ring-indigo-500"
                            />
                            <span className="text-sm text-slate-600">Include in adoption</span>
                          </label>
                          <button
                            onClick={() => setExtractedRules(prev => prev.filter(r => r.id !== rule.id))}
                            className="text-xs text-red-500 hover:text-red-700"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Add Rule Manually Button */}
                  <button
                    onClick={() => setExtractedRules(prev => [...prev, {
                      id: `manual-${Date.now()}`,
                      rule_text: "",
                      rule_type: "requirement",
                      citation: "",
                      confidence_score: null,
                      isSelected: true,
                      isEdited: true,
                      isManual: true,
                    }])}
                    className="w-full border-2 border-dashed border-indigo-300 rounded-lg p-4 text-center hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
                  >
                    <span className="text-indigo-600 font-medium">+ Add Rule Manually</span>
                    <p className="text-xs text-slate-500 mt-1">
                      Copy and paste from the original source document to add rules the AI missed
                    </p>
                  </button>
                  
                  {/* Adopt Button */}
                  <div className="flex items-center justify-between pt-3 border-t border-indigo-200">
                    <p className="text-xs text-slate-500">
                      Review the original document for rules AI may have missed
                    </p>
                    <Button 
                      variant="success" 
                      size="sm"
                      onClick={adoptSelectedRules}
                      disabled={adoptingRules || extractedRules.filter(r => r.isSelected && r.rule_text.trim()).length === 0}
                    >
                      {adoptingRules ? (
                        <><Icons.Clock className="w-4 h-4 animate-spin" /> Adopting...</>
                      ) : (
                        <><Icons.Check className="w-4 h-4" /> Accept {extractedRules.filter(r => r.isSelected && r.rule_text.trim()).length} Rule(s)</>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  <p className="text-blue-800 font-medium">
                    👆 Click <span className="font-bold text-blue-600">"Extract Rules"</span> to let AI find compliance requirements.
                  </p>
                  <p className="text-sm text-blue-600 mt-2">
                    AI will suggest rules you can <strong>edit, override, or replace</strong> with your own text. 
                    You can also add rules manually that AI missed.
                  </p>
                </div>
              )}
            </div>
            
            {/* Original Source - Always Accessible */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition"
                onClick={() => setRegShowOriginal(!regShowOriginal)}
              >
                <div className="flex items-center gap-2">
                  <Icons.Document className="w-4 h-4 text-slate-500" />
                  <span className="font-medium text-slate-700">Original Source Text</span>
                </div>
                <Icons.ChevronDown className={`w-4 h-4 text-slate-500 transition ${regShowOriginal ? "rotate-180" : ""}`} />
              </button>
              {regShowOriginal && (
                <div className="p-4 border-t border-slate-200">
                  <p className="text-sm text-slate-600 whitespace-pre-wrap">
                    {regReviewModal.update.summary || "No summary available. Click the link below to view the full document."}
                  </p>
                </div>
              )}
              <div className="p-3 border-t border-slate-200 bg-blue-50">
                <a 
                  href={regReviewModal.update.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium text-sm"
                >
                  <Icons.Link className="w-4 h-4" />
                  Open Full Document in New Tab
                </a>
              </div>
            </div>
            
            {/* Affected Governance Objects (for Adopt action) */}
            <div className="border border-slate-200 rounded-lg p-4">
              <h4 className="font-semibold text-slate-900 mb-3">Affected Governance Objects</h4>
              <p className="text-sm text-slate-500 mb-3">
                Select which scenarios or thresholds should be updated based on this regulatory change:
              </p>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {scenarios.map(s => (
                  <label key={s.scenario_id} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={regSelectedObjects.some(o => o.id === s.scenario_id && o.type === "scenario")}
                      onChange={e => {
                        if (e.target.checked) {
                          setRegSelectedObjects([...regSelectedObjects, { id: s.scenario_id, type: "scenario", name: s.name }]);
                        } else {
                          setRegSelectedObjects(regSelectedObjects.filter(o => !(o.id === s.scenario_id && o.type === "scenario")));
                        }
                      }}
                      className="rounded text-blue-500"
                    />
                    <Badge color="blue">Scenario</Badge>
                    <span className="text-sm text-slate-700">{s.name}</span>
                  </label>
                ))}
                {thresholds.map(t => (
                  <label key={t.threshold_id} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={regSelectedObjects.some(o => o.id === t.threshold_id && o.type === "threshold")}
                      onChange={e => {
                        if (e.target.checked) {
                          setRegSelectedObjects([...regSelectedObjects, { id: t.threshold_id, type: "threshold", name: t.name }]);
                        } else {
                          setRegSelectedObjects(regSelectedObjects.filter(o => !(o.id === t.threshold_id && o.type === "threshold")));
                        }
                      }}
                      className="rounded text-emerald-500"
                    />
                    <Badge color="emerald">Threshold</Badge>
                    <span className="text-sm text-slate-700">{t.name}</span>
                  </label>
                ))}
              </div>
              {regSelectedObjects.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <span className="text-sm text-slate-500">Selected: </span>
                  <span className="text-sm font-medium text-slate-700">{regSelectedObjects.length} object(s)</span>
                </div>
              )}
            </div>
            
            {/* Decline Reason (for Decline action) */}
            <div className="border border-slate-200 rounded-lg p-4">
              <h4 className="font-semibold text-slate-900 mb-2">Decline Reason</h4>
              <p className="text-sm text-slate-500 mb-3">
                If declining, document why this update does not apply to your compliance program:
              </p>
              <textarea
                value={regDeclineReason}
                onChange={e => setRegDeclineReason(e.target.value)}
                placeholder="e.g., This guidance applies to broker-dealers, not community banks. Our institution does not engage in the activities covered by this update."
                className="w-full border border-slate-200 rounded-lg p-3 text-sm"
                rows={3}
              />
            </div>
            
            {/* Notes Section - Sprint 2 Feature */}
            <div className="border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Icons.Comment className="w-4 h-4 text-slate-500" />
                  Notes & Comments
                </h4>
                <span className="text-xs text-slate-500">
                  {getNotesForObject("regulatory_update", regReviewModal.update?.id).length} note(s)
                </span>
              </div>
              {/* Existing Notes */}
              {getNotesForObject("regulatory_update", regReviewModal.update?.id).length > 0 && (
                <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
                  {getNotesForObject("regulatory_update", regReviewModal.update?.id).map(note => (
                    <div key={note.id} className="bg-slate-50 rounded p-2 text-sm">
                      <p className="text-slate-700">{note.note}</p>
                      <div className="text-xs text-slate-500 mt-1">
                        {note.author_email} • {formatDateTime(note.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Add Note */}
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Add a note..."
                  className="flex-1 border border-slate-200 rounded px-3 py-2 text-sm"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.target.value.trim()) {
                      addNote("regulatory_update", regReviewModal.update?.id, e.target.value);
                      e.target.value = "";
                    }
                  }}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={(e) => {
                    const input = e.target.previousSibling;
                    if (input.value.trim()) {
                      addNote("regulatory_update", regReviewModal.update?.id, input.value);
                      input.value = "";
                    }
                  }}
                >
                  Add
                </Button>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-slate-200">
              <Button 
                variant="secondary" 
                onClick={() => setRegReviewModal({ open: false, update: null })}
              >
                Cancel
              </Button>
              <div className="flex flex-wrap items-center gap-2">
                <Button 
                  variant="secondary"
                  onClick={() => recordRegDecision("snoozed", 30)}
                  disabled={regReviewLoading}
                >
                  <Icons.Clock className="w-4 h-4 mr-1" />
                  Snooze 30 Days
                </Button>
                <Button 
                  variant="danger"
                  onClick={() => recordRegDecision("declined")}
                  disabled={regReviewLoading || !regDeclineReason.trim()}
                  title={!regDeclineReason.trim() ? "Decline reason required" : ""}
                >
                  <Icons.X className="w-4 h-4 mr-1" />
                  Decline
                </Button>
                <Button 
                  variant="primary"
                  onClick={() => recordRegDecision("adopted")}
                  disabled={regReviewLoading || regSelectedObjects.length === 0}
                  title={regSelectedObjects.length === 0 ? "Select objects to update" : ""}
                >
                  <Icons.Check className="w-4 h-4 mr-1" />
                  Adopt ({regSelectedObjects.length})
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>
      
      {/* Vertical Selector Modal - Shows on first login if no vertical set */}
      {showVerticalSelector && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8 border-b border-slate-100">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 text-white rounded-2xl mb-4 text-2xl font-bold">S</div>
                <h1 className="text-2xl font-bold text-slate-900">Welcome to Steadfastly</h1>
                <p className="text-slate-500 mt-2">Select your industry to customize your compliance experience</p>
              </div>
            </div>
            <div className="p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {VERTICAL_OPTIONS.map(v => {
                  const config = VERTICAL_CONFIG[v];
                  return (
                    <button
                      key={v}
                      onClick={() => saveVerticalSelection(v)}
                      className="p-6 border-2 border-slate-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition group text-left"
                    >
                      <div className="text-4xl mb-3">{config?.icon || "📋"}</div>
                      <div className="font-semibold text-slate-900 group-hover:text-blue-700">{config?.displayName || v}</div>
                      <div className="text-sm text-slate-500 mt-1">{config?.description || ""}</div>
                      <div className="text-xs text-slate-500 mt-3">
                        {config?.officerTitle || "Compliance Officer"}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="mt-6 text-center text-sm text-slate-500">
                This determines your regulatory sources, terminology, and templates. You can change it later in Settings.
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Webhook Add/Edit Modal */}
      {showWebhookModal && (
        <Modal
          open={showWebhookModal}
          onClose={() => { setShowWebhookModal(false); setEditingWebhook(null); }}
          title={editingWebhook ? "Edit Webhook" : "Add Webhook Integration"}
          size="lg"
        >
          <div className="space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
              <input
                type="text"
                id="webhook-name"
                defaultValue={editingWebhook?.name || ""}
                placeholder="Slack - #compliance-alerts"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            
            {/* Provider */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Provider</label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { id: "slack", name: "Slack", icon: "🟢" },
                  { id: "teams", name: "Teams", icon: "🔵" },
                  { id: "servicenow", name: "ServiceNow", icon: "⚪" },
                  { id: "generic", name: "Generic", icon: "🔗" },
                ].map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => document.getElementById("webhook-provider").value = p.id}
                    className={`p-3 border rounded-lg text-center hover:border-blue-500 hover:bg-blue-50 transition ${editingWebhook?.provider === p.id ? "border-blue-500 bg-blue-50" : "border-slate-200"}`}
                  >
                    <div className="text-xl">{p.icon}</div>
                    <div className="text-xs mt-1">{p.name}</div>
                  </button>
                ))}
              </div>
              <input type="hidden" id="webhook-provider" defaultValue={editingWebhook?.provider || "slack"} />
            </div>
            
            {/* Webhook URL */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Webhook URL</label>
              <input
                type="url"
                id="webhook-url"
                defaultValue={editingWebhook?.webhook_url || ""}
                placeholder="https://hooks.slack.com/services/T00000/B000..."
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono"
              />
              <p className="text-xs text-slate-500 mt-1">
                For Slack: Apps → Incoming Webhooks → Add to Slack
              </p>
            </div>
            
            {/* Events */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Events to Send</label>
              <div className="grid grid-cols-2 gap-2">
                {WEBHOOK_EVENTS.map(event => (
                  <label key={event.id} className="flex items-center gap-2 text-sm p-2 rounded hover:bg-slate-50 cursor-pointer">
                    <input
                      type="checkbox"
                      name="webhook-events"
                      value={event.id}
                      defaultChecked={editingWebhook?.events?.includes(event.id) || ["regulatory_critical", "attestation_due"].includes(event.id)}
                      className="rounded"
                    />
                    <span>{event.icon}</span>
                    <span>{event.label}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-200">
              <Button variant="secondary" onClick={() => { setShowWebhookModal(false); setEditingWebhook(null); }}>
                Cancel
              </Button>
              <div className="flex items-center gap-2">
                {editingWebhook && (
                  <Button 
                    variant="secondary"
                    onClick={() => testWebhook(editingWebhook)}
                  >
                    Test Webhook
                  </Button>
                )}
                <Button
                  variant="primary"
                  onClick={() => {
                    const name = document.getElementById("webhook-name").value;
                    const provider = document.getElementById("webhook-provider").value;
                    const webhook_url = document.getElementById("webhook-url").value;
                    const events = Array.from(document.querySelectorAll('input[name="webhook-events"]:checked')).map(e => e.value);
                    
                    if (!name || !webhook_url) {
                      setToast({ type: "error", message: "Name and URL are required" });
                      setTimeout(() => setToast(null), 3000);
                      return;
                    }
                    
                    if (editingWebhook) {
                      updateWebhook(editingWebhook.id, { name, provider, webhook_url, events });
                      setToast({ type: "success", message: "Webhook updated" });
                    } else {
                      addWebhook({ name, provider, webhook_url, events });
                    }
                    setShowWebhookModal(false);
                    setEditingWebhook(null);
                    setTimeout(() => setToast(null), 3000);
                  }}
                >
                  {editingWebhook ? "Update" : "Save"} Webhook
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
      
      {/* ========================================================================== */}
      {/* GRC MODALS - Vendor, Control, Risk, Policy, Incident (Sprint 2-3) */}
      {/* ========================================================================== */}
      
      {/* Vendor Modal */}
      {vendorModal.open && (
        <Modal darkMode={darkMode} open={vendorModal.open} onClose={() => { setVendorModal({ open: false, vendor: null }); setVendorEditingId(null); resetVendorForm(); }} title={vendorEditingId ? "Edit Vendor" : "New Vendor"} size="lg">
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Vendor Name *</label>
                <input className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} placeholder="Acme Corp" value={vendorForm.name} onChange={e => setVendorForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Vendor Type</label>
                <select className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} value={vendorForm.vendor_type} onChange={e => setVendorForm(f => ({ ...f, vendor_type: e.target.value }))}>
                  <option value="critical">Critical</option>
                  <option value="significant">Significant</option>
                  <option value="standard">Standard</option>
                </select>
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Risk Tier</label>
                <select className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} value={vendorForm.risk_tier} onChange={e => setVendorForm(f => ({ ...f, risk_tier: e.target.value }))}>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Status</label>
                <select className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} value={vendorForm.status} onChange={e => setVendorForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="under_review">Under Review</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div>
              <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Description</label>
              <textarea className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} rows={2} placeholder="Services provided by this vendor..." value={vendorForm.description} onChange={e => setVendorForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Contact Name</label>
                <input className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} placeholder="John Smith" value={vendorForm.contact_name} onChange={e => setVendorForm(f => ({ ...f, contact_name: e.target.value }))} />
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Contact Email</label>
                <input type="email" className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} placeholder="john@acme.com" value={vendorForm.contact_email} onChange={e => setVendorForm(f => ({ ...f, contact_email: e.target.value }))} />
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Owner Email</label>
                <input type="email" className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} placeholder="owner@bank.com" value={vendorForm.owner_email} onChange={e => setVendorForm(f => ({ ...f, owner_email: e.target.value }))} />
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Next Review Date</label>
                <input type="date" className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} value={vendorForm.next_review_date} onChange={e => setVendorForm(f => ({ ...f, next_review_date: e.target.value }))} />
              </div>
            </div>
            <div className={`flex items-center justify-end gap-2 pt-4 border-t ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
              <Button variant="secondary" onClick={() => { setVendorModal({ open: false, vendor: null }); setVendorEditingId(null); resetVendorForm(); }}>Cancel</Button>
              <Button onClick={saveVendor} disabled={vendorSaving}>{vendorSaving ? "Saving..." : vendorEditingId ? "Save Changes" : "Create Vendor"}</Button>
            </div>
          </div>
        </Modal>
      )}
      
      {/* Control Modal */}
      {controlModal.open && (
        <Modal darkMode={darkMode} open={controlModal.open} onClose={() => { setControlModal({ open: false, control: null }); setControlEditingId(null); resetControlForm(); }} title={controlEditingId ? "Edit Control" : "New Control"} size="lg">
          <div className="space-y-4">
            <div>
              <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Control Name *</label>
              <input className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} placeholder="Transaction Monitoring Control" value={controlForm.name} onChange={e => setControlForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Control Type</label>
                <select className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} value={controlForm.control_type} onChange={e => setControlForm(f => ({ ...f, control_type: e.target.value }))}>
                  <option value="preventive">Preventive</option>
                  <option value="detective">Detective</option>
                  <option value="corrective">Corrective</option>
                </select>
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Category</label>
                <select className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} value={controlForm.control_category} onChange={e => setControlForm(f => ({ ...f, control_category: e.target.value }))}>
                  <option value="operational">Operational</option>
                  <option value="technical">Technical</option>
                  <option value="administrative">Administrative</option>
                </select>
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Frequency</label>
                <select className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} value={controlForm.frequency} onChange={e => setControlForm(f => ({ ...f, frequency: e.target.value }))}>
                  <option value="continuous">Continuous</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annually">Annually</option>
                </select>
              </div>
            </div>
            <div>
              <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Description</label>
              <textarea className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} rows={2} placeholder="Describe what this control does..." value={controlForm.description} onChange={e => setControlForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Owner Email</label>
                <input type="email" className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} placeholder="owner@bank.com" value={controlForm.owner_email} onChange={e => setControlForm(f => ({ ...f, owner_email: e.target.value }))} />
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Next Review Date</label>
                <input type="date" className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} value={controlForm.next_review_date} onChange={e => setControlForm(f => ({ ...f, next_review_date: e.target.value }))} />
              </div>
            </div>
            <div className={`flex items-center justify-end gap-2 pt-4 border-t ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
              <Button variant="secondary" onClick={() => { setControlModal({ open: false, control: null }); setControlEditingId(null); resetControlForm(); }}>Cancel</Button>
              <Button onClick={saveControl} disabled={controlSaving}>{controlSaving ? "Saving..." : controlEditingId ? "Save Changes" : "Create Control"}</Button>
            </div>
          </div>
        </Modal>
      )}
      
      {/* Risk Modal */}
      {riskModal.open && (
        <Modal darkMode={darkMode} open={riskModal.open} onClose={() => { setRiskModal({ open: false, risk: null }); setRiskEditingId(null); resetRiskForm(); }} title={riskEditingId ? "Edit Risk" : "New Risk"} size="lg">
          <div className="space-y-4">
            <div>
              <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Risk Name *</label>
              <input className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} placeholder="Regulatory Non-Compliance Risk" value={riskForm.name} onChange={e => setRiskForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Risk Category</label>
                <select className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} value={riskForm.risk_category} onChange={e => setRiskForm(f => ({ ...f, risk_category: e.target.value }))}>
                  <option value="operational">Operational</option>
                  <option value="compliance">Compliance</option>
                  <option value="financial">Financial</option>
                  <option value="strategic">Strategic</option>
                  <option value="reputational">Reputational</option>
                </select>
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Risk Treatment</label>
                <select className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} value={riskForm.risk_treatment} onChange={e => setRiskForm(f => ({ ...f, risk_treatment: e.target.value }))}>
                  <option value="mitigate">Mitigate</option>
                  <option value="accept">Accept</option>
                  <option value="transfer">Transfer</option>
                  <option value="avoid">Avoid</option>
                </select>
              </div>
            </div>
            <div>
              <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Description</label>
              <textarea className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} rows={2} placeholder="Describe the risk..." value={riskForm.description} onChange={e => setRiskForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className={`p-3 rounded-lg ${darkMode ? "bg-slate-700/50" : "bg-slate-50"}`}>
              <div className={`text-sm font-medium mb-3 ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Risk Scoring</div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <div className={`text-xs mb-2 ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Inherent Risk (before controls)</div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className={`text-xs ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Likelihood</label>
                      <input type="range" min="1" max="5" className="w-full" value={riskForm.inherent_likelihood} onChange={e => setRiskForm(f => ({ ...f, inherent_likelihood: Number(e.target.value) }))} />
                      <div className="text-center text-xs">{riskForm.inherent_likelihood}</div>
                    </div>
                    <div className="flex-1">
                      <label className={`text-xs ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Impact</label>
                      <input type="range" min="1" max="5" className="w-full" value={riskForm.inherent_impact} onChange={e => setRiskForm(f => ({ ...f, inherent_impact: Number(e.target.value) }))} />
                      <div className="text-center text-xs">{riskForm.inherent_impact}</div>
                    </div>
                  </div>
                </div>
                <div>
                  <div className={`text-xs mb-2 ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Residual Risk (after controls)</div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className={`text-xs ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Likelihood</label>
                      <input type="range" min="1" max="5" className="w-full" value={riskForm.residual_likelihood} onChange={e => setRiskForm(f => ({ ...f, residual_likelihood: Number(e.target.value) }))} />
                      <div className="text-center text-xs">{riskForm.residual_likelihood}</div>
                    </div>
                    <div className="flex-1">
                      <label className={`text-xs ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Impact</label>
                      <input type="range" min="1" max="5" className="w-full" value={riskForm.residual_impact} onChange={e => setRiskForm(f => ({ ...f, residual_impact: Number(e.target.value) }))} />
                      <div className="text-center text-xs">{riskForm.residual_impact}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Owner Email</label>
                <input type="email" className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} placeholder="owner@bank.com" value={riskForm.owner_email} onChange={e => setRiskForm(f => ({ ...f, owner_email: e.target.value }))} />
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Next Review Date</label>
                <input type="date" className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} value={riskForm.next_review_date} onChange={e => setRiskForm(f => ({ ...f, next_review_date: e.target.value }))} />
              </div>
            </div>
            <div className={`flex items-center justify-end gap-2 pt-4 border-t ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
              <Button variant="secondary" onClick={() => { setRiskModal({ open: false, risk: null }); setRiskEditingId(null); resetRiskForm(); }}>Cancel</Button>
              <Button onClick={saveRisk} disabled={riskSaving}>{riskSaving ? "Saving..." : riskEditingId ? "Save Changes" : "Create Risk"}</Button>
            </div>
          </div>
        </Modal>
      )}
      
      {/* Policy Modal */}
      {policyModal.open && (
        <Modal darkMode={darkMode} open={policyModal.open} onClose={() => { setPolicyModal({ open: false, policy: null }); setPolicyEditingId(null); resetPolicyForm(); }} title={policyEditingId ? "Edit Policy" : "New Policy"} size="lg">
          <div className="space-y-4">
            <div>
              <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Policy Name *</label>
              <input className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} placeholder="AML/BSA Compliance Policy" value={policyForm.name} onChange={e => setPolicyForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Policy Type</label>
                <select className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} value={policyForm.policy_type} onChange={e => setPolicyForm(f => ({ ...f, policy_type: e.target.value }))}>
                  <option value="policy">Policy</option>
                  <option value="standard">Standard</option>
                  <option value="procedure">Procedure</option>
                  <option value="guideline">Guideline</option>
                </select>
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Category</label>
                <select className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} value={policyForm.category} onChange={e => setPolicyForm(f => ({ ...f, category: e.target.value }))}>
                  <option value="operational">Operational</option>
                  <option value="compliance">Compliance</option>
                  <option value="security">Security</option>
                  <option value="hr">HR</option>
                </select>
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Status</label>
                <select className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} value={policyForm.status} onChange={e => setPolicyForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="draft">Draft</option>
                  <option value="pending_review">Pending Review</option>
                  <option value="pending_approval">Pending Approval</option>
                  <option value="approved">Approved</option>
                  <option value="published">Published</option>
                </select>
              </div>
            </div>
            <div>
              <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Description</label>
              <textarea className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} rows={2} placeholder="Policy scope and objectives..." value={policyForm.description} onChange={e => setPolicyForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Version</label>
                <input className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} placeholder="1.0" value={policyForm.version} onChange={e => setPolicyForm(f => ({ ...f, version: e.target.value }))} />
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Owner Email</label>
                <input type="email" className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} placeholder="owner@bank.com" value={policyForm.owner_email} onChange={e => setPolicyForm(f => ({ ...f, owner_email: e.target.value }))} />
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Next Review Date</label>
                <input type="date" className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} value={policyForm.next_review_date} onChange={e => setPolicyForm(f => ({ ...f, next_review_date: e.target.value }))} />
              </div>
            </div>
            <div className={`flex items-center justify-end gap-2 pt-4 border-t ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
              <Button variant="secondary" onClick={() => { setPolicyModal({ open: false, policy: null }); setPolicyEditingId(null); resetPolicyForm(); }}>Cancel</Button>
              <Button onClick={savePolicy} disabled={policySaving}>{policySaving ? "Saving..." : policyEditingId ? "Save Changes" : "Create Policy"}</Button>
            </div>
          </div>
        </Modal>
      )}
      
      {/* Incident Modal */}
      {incidentModal.open && (
        <Modal darkMode={darkMode} open={incidentModal.open} onClose={() => { setIncidentModal({ open: false, incident: null }); setIncidentEditingId(null); resetIncidentForm(); }} title={incidentEditingId ? "Edit Incident" : "Report Incident"} size="lg">
          <div className="space-y-4">
            <div>
              <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Incident Title *</label>
              <input className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} placeholder="Security incident title" value={incidentForm.title} onChange={e => setIncidentForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Incident Type</label>
                <select className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} value={incidentForm.incident_type} onChange={e => setIncidentForm(f => ({ ...f, incident_type: e.target.value }))}>
                  <option value="security">Security</option>
                  <option value="compliance">Compliance</option>
                  <option value="operational">Operational</option>
                  <option value="data_breach">Data Breach</option>
                  <option value="fraud">Fraud</option>
                </select>
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Severity</label>
                <select className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} value={incidentForm.severity} onChange={e => setIncidentForm(f => ({ ...f, severity: e.target.value }))}>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Status</label>
                <select className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} value={incidentForm.status} onChange={e => setIncidentForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="open">Open</option>
                  <option value="investigating">Investigating</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>
            <div>
              <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Description</label>
              <textarea className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} rows={3} placeholder="Describe the incident in detail..." value={incidentForm.description} onChange={e => setIncidentForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Reporter Email</label>
                <input type="email" className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} placeholder="reporter@bank.com" value={incidentForm.reporter_email} onChange={e => setIncidentForm(f => ({ ...f, reporter_email: e.target.value }))} />
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Owner Email</label>
                <input type="email" className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} placeholder="owner@bank.com" value={incidentForm.owner_email} onChange={e => setIncidentForm(f => ({ ...f, owner_email: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Root Cause Analysis</label>
              <textarea className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} rows={2} placeholder="What caused this incident?" value={incidentForm.root_cause} onChange={e => setIncidentForm(f => ({ ...f, root_cause: e.target.value }))} />
            </div>
            <div>
              <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Remediation Steps</label>
              <textarea className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} rows={2} placeholder="Steps taken or planned to resolve..." value={incidentForm.remediation} onChange={e => setIncidentForm(f => ({ ...f, remediation: e.target.value }))} />
            </div>
            <div className={`flex items-center justify-end gap-2 pt-4 border-t ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
              <Button variant="secondary" onClick={() => { setIncidentModal({ open: false, incident: null }); setIncidentEditingId(null); resetIncidentForm(); }}>Cancel</Button>
              <Button onClick={saveIncident} disabled={incidentSaving}>{incidentSaving ? "Saving..." : incidentEditingId ? "Save Changes" : "Report Incident"}</Button>
            </div>
          </div>
        </Modal>
      )}
      
      {/* ========================================================================== */}
      {/* DETAIL MODALS - View existing records (read-only) */}
      {/* ========================================================================== */}
      
      {/* Vendor Detail Modal */}
      {vendorDetailModal.open && vendorDetailModal.vendor && (
        <Modal darkMode={darkMode} open={vendorDetailModal.open} onClose={() => setVendorDetailModal({ open: false, vendor: null })} title="Vendor Details" size="lg">
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Vendor Name</div>
                <div className={`text-sm font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>{vendorDetailModal.vendor.name}</div>
              </div>
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Type</div>
                <Badge variant={vendorDetailModal.vendor.vendor_type === 'critical' ? 'critical' : 'medium'}>{vendorDetailModal.vendor.vendor_type}</Badge>
              </div>
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Risk Tier</div>
                <Badge variant={vendorDetailModal.vendor.risk_tier === 'critical' ? 'critical' : vendorDetailModal.vendor.risk_tier === 'high' ? 'high' : 'medium'}>{vendorDetailModal.vendor.risk_tier || 'Not Assessed'}</Badge>
              </div>
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Status</div>
                <div className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`}>{vendorDetailModal.vendor.status}</div>
              </div>
            </div>
            {vendorDetailModal.vendor.description && (
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Description</div>
                <div className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`}>{vendorDetailModal.vendor.description}</div>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Contact</div>
                <div className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`}>{vendorDetailModal.vendor.contact_name || 'N/A'}</div>
                <div className={`text-xs ${darkMode ? "text-slate-400" : "text-slate-500"}`}>{vendorDetailModal.vendor.contact_email || ''}</div>
              </div>
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Owner</div>
                <div className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`}>{vendorDetailModal.vendor.owner_email || 'N/A'}</div>
              </div>
            </div>
            <div className={`flex justify-end pt-4 border-t ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
              <Button variant="secondary" onClick={() => setVendorDetailModal({ open: false, vendor: null })}>Close</Button>
            </div>
          </div>
        </Modal>
      )}
      
      {/* Control Detail Modal */}
      {controlDetailModal.open && controlDetailModal.control && (
        <Modal darkMode={darkMode} open={controlDetailModal.open} onClose={() => setControlDetailModal({ open: false, control: null })} title="Control Details" size="lg">
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Control Name</div>
                <div className={`text-sm font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>{controlDetailModal.control.name}</div>
              </div>
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Type</div>
                <Badge>{controlDetailModal.control.control_type}</Badge>
              </div>
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Category</div>
                <div className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`}>{controlDetailModal.control.control_category}</div>
              </div>
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Frequency</div>
                <div className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`}>{controlDetailModal.control.frequency}</div>
              </div>
            </div>
            {controlDetailModal.control.description && (
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Description</div>
                <div className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`}>{controlDetailModal.control.description}</div>
              </div>
            )}
            <div>
              <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Owner</div>
              <div className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`}>{controlDetailModal.control.owner_email || 'N/A'}</div>
            </div>
            <div className={`flex justify-end pt-4 border-t ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
              <Button variant="secondary" onClick={() => setControlDetailModal({ open: false, control: null })}>Close</Button>
            </div>
          </div>
        </Modal>
      )}
      
      {/* Control Test Modal */}
      {controlTestModal.open && controlTestModal.control && (
        <Modal darkMode={darkMode} open={controlTestModal.open} onClose={() => setControlTestModal({ open: false, control: null })} title="Test Control" size="md">
          <div className="space-y-4">
            <div className={`p-3 rounded-lg ${darkMode ? "bg-slate-700/50" : "bg-slate-50"}`}>
              <div className={`text-sm font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>{controlTestModal.control.name}</div>
              <div className={`text-xs ${darkMode ? "text-slate-400" : "text-slate-500"}`}>{controlTestModal.control.control_type} • {controlTestModal.control.frequency}</div>
            </div>
            <div>
              <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Test Result</label>
              <select className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`}>
                <option value="passed">Passed</option>
                <option value="failed">Failed</option>
                <option value="partial">Partially Effective</option>
              </select>
            </div>
            <div>
              <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Test Notes</label>
              <textarea className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} rows={3} placeholder="Document test methodology and findings..." />
            </div>
            <div>
              <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Evidence</label>
              <input type="file" className={`mt-1 w-full text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`} />
            </div>
            <div className={`flex justify-end gap-2 pt-4 border-t ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
              <Button variant="secondary" onClick={() => setControlTestModal({ open: false, control: null })}>Cancel</Button>
              <Button onClick={() => { setToast({ type: 'success', message: 'Control test recorded' }); setControlTestModal({ open: false, control: null }); setTimeout(() => setToast(null), 3000); }}>Save Test</Button>
            </div>
          </div>
        </Modal>
      )}
      
      {/* Risk Detail Modal */}
      {riskDetailModal.open && riskDetailModal.risk && (
        <Modal darkMode={darkMode} open={riskDetailModal.open} onClose={() => setRiskDetailModal({ open: false, risk: null })} title="Risk Details" size="lg">
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Risk Name</div>
                <div className={`text-sm font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>{riskDetailModal.risk.name}</div>
              </div>
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Category</div>
                <Badge>{riskDetailModal.risk.risk_category}</Badge>
              </div>
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Treatment</div>
                <Badge variant={riskDetailModal.risk.risk_treatment === 'mitigate' ? 'info' : riskDetailModal.risk.risk_treatment === 'accept' ? 'warning' : 'medium'}>{riskDetailModal.risk.risk_treatment}</Badge>
              </div>
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Owner</div>
                <div className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`}>{riskDetailModal.risk.owner_email || 'N/A'}</div>
              </div>
            </div>
            {riskDetailModal.risk.description && (
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Description</div>
                <div className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`}>{riskDetailModal.risk.description}</div>
              </div>
            )}
            <div className={`p-3 rounded-lg ${darkMode ? "bg-slate-700/50" : "bg-slate-50"}`}>
              <div className={`text-xs font-medium mb-2 ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Risk Scores</div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-400"}`}>Inherent</div>
                  <div className={`text-lg font-bold ${darkMode ? "text-white" : "text-slate-900"}`}>{(riskDetailModal.risk.inherent_likelihood || 3) * (riskDetailModal.risk.inherent_impact || 3)}</div>
                  <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-400"}`}>L:{riskDetailModal.risk.inherent_likelihood || 3} × I:{riskDetailModal.risk.inherent_impact || 3}</div>
                </div>
                <div>
                  <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-400"}`}>Residual</div>
                  <div className={`text-lg font-bold ${darkMode ? "text-emerald-400" : "text-emerald-600"}`}>{(riskDetailModal.risk.residual_likelihood || 2) * (riskDetailModal.risk.residual_impact || 2)}</div>
                  <div className={`text-xs ${darkMode ? "text-slate-500" : "text-slate-400"}`}>L:{riskDetailModal.risk.residual_likelihood || 2} × I:{riskDetailModal.risk.residual_impact || 2}</div>
                </div>
              </div>
            </div>
            <div className={`flex justify-end pt-4 border-t ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
              <Button variant="secondary" onClick={() => setRiskDetailModal({ open: false, risk: null })}>Close</Button>
            </div>
          </div>
        </Modal>
      )}
      
      {/* Risk Quantification Modal */}
      {riskQuantModal.open && riskQuantModal.risk && (
        <Modal darkMode={darkMode} open={riskQuantModal.open} onClose={() => setRiskQuantModal({ open: false, risk: null })} title="Risk Quantification" size="md">
          <div className="space-y-4">
            <div className={`p-3 rounded-lg ${darkMode ? "bg-slate-700/50" : "bg-slate-50"}`}>
              <div className={`text-sm font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>{riskQuantModal.risk.name}</div>
              <div className={`text-xs ${darkMode ? "text-slate-400" : "text-slate-500"}`}>{riskQuantModal.risk.risk_category} • {riskQuantModal.risk.risk_treatment}</div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Annual Loss Expectancy ($)</label>
                <input type="number" className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} placeholder="50000" />
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Single Loss Expectancy ($)</label>
                <input type="number" className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} placeholder="10000" />
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Annual Rate of Occurrence</label>
                <input type="number" step="0.1" className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} placeholder="5.0" />
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Control Cost ($)</label>
                <input type="number" className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} placeholder="15000" />
              </div>
            </div>
            <div className={`p-3 rounded-lg border-2 ${darkMode ? "border-emerald-600 bg-emerald-900/20" : "border-emerald-300 bg-emerald-50"}`}>
              <div className={`text-xs font-medium ${darkMode ? "text-emerald-400" : "text-emerald-600"}`}>Risk/Return Analysis</div>
              <div className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Enter values above to calculate ROI on risk mitigation</div>
            </div>
            <div className={`flex justify-end gap-2 pt-4 border-t ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
              <Button variant="secondary" onClick={() => setRiskQuantModal({ open: false, risk: null })}>Cancel</Button>
              <Button onClick={() => { setToast({ type: 'success', message: 'Risk quantification saved' }); setRiskQuantModal({ open: false, risk: null }); setTimeout(() => setToast(null), 3000); }}>Save</Button>
            </div>
          </div>
        </Modal>
      )}
      
      {/* Policy Detail Modal */}
      {policyDetailModal.open && policyDetailModal.policy && (
        <Modal darkMode={darkMode} open={policyDetailModal.open} onClose={() => setPolicyDetailModal({ open: false, policy: null })} title="Policy Details" size="lg">
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Policy Name</div>
                <div className={`text-sm font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>{policyDetailModal.policy.name}</div>
              </div>
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Type</div>
                <Badge>{policyDetailModal.policy.policy_type}</Badge>
              </div>
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Category</div>
                <div className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`}>{policyDetailModal.policy.category}</div>
              </div>
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Status</div>
                <Badge variant={policyDetailModal.policy.status === 'published' ? 'success' : policyDetailModal.policy.status === 'approved' ? 'success' : 'warning'}>{policyDetailModal.policy.status?.replace('_', ' ')}</Badge>
              </div>
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Version</div>
                <div className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`}>{policyDetailModal.policy.version || '1.0'}</div>
              </div>
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Owner</div>
                <div className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`}>{policyDetailModal.policy.owner_email || 'N/A'}</div>
              </div>
            </div>
            {policyDetailModal.policy.description && (
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Description</div>
                <div className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`}>{policyDetailModal.policy.description}</div>
              </div>
            )}
            <div className={`flex justify-end pt-4 border-t ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
              <Button variant="secondary" onClick={() => setPolicyDetailModal({ open: false, policy: null })}>Close</Button>
            </div>
          </div>
        </Modal>
      )}
      
      {/* Incident Detail Modal */}
      {incidentDetailModal.open && incidentDetailModal.incident && (
        <Modal darkMode={darkMode} open={incidentDetailModal.open} onClose={() => setIncidentDetailModal({ open: false, incident: null })} title="Incident Details" size="lg">
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Incident Title</div>
                <div className={`text-sm font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>{incidentDetailModal.incident.title}</div>
              </div>
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Type</div>
                <Badge>{incidentDetailModal.incident.incident_type?.replace('_', ' ')}</Badge>
              </div>
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Severity</div>
                <Badge variant={incidentDetailModal.incident.severity}>{incidentDetailModal.incident.severity}</Badge>
              </div>
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Status</div>
                <Badge variant={incidentDetailModal.incident.status === 'closed' ? 'success' : incidentDetailModal.incident.status === 'resolved' ? 'info' : 'warning'}>{incidentDetailModal.incident.status}</Badge>
              </div>
            </div>
            {incidentDetailModal.incident.description && (
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Description</div>
                <div className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`}>{incidentDetailModal.incident.description}</div>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Reporter</div>
                <div className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`}>{incidentDetailModal.incident.reporter_email || 'N/A'}</div>
              </div>
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Owner</div>
                <div className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`}>{incidentDetailModal.incident.owner_email || 'N/A'}</div>
              </div>
            </div>
            {incidentDetailModal.incident.root_cause && (
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Root Cause</div>
                <div className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`}>{incidentDetailModal.incident.root_cause}</div>
              </div>
            )}
            {incidentDetailModal.incident.remediation && (
              <div>
                <div className={`text-xs font-medium ${darkMode ? "text-slate-400" : "text-slate-500"}`}>Remediation</div>
                <div className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-700"}`}>{incidentDetailModal.incident.remediation}</div>
              </div>
            )}
            <div className={`flex justify-end pt-4 border-t ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
              <Button variant="secondary" onClick={() => setIncidentDetailModal({ open: false, incident: null })}>Close</Button>
            </div>
          </div>
        </Modal>
      )}
      
      {/* Vendor Assessment Modal */}
      {assessmentModal.open && assessmentModal.vendor && (
        <Modal darkMode={darkMode} open={assessmentModal.open} onClose={() => setAssessmentModal({ open: false, vendor: null, assessment: null })} title="Vendor Assessment" size="lg">
          <div className="space-y-4">
            <div className={`p-3 rounded-lg ${darkMode ? "bg-slate-700/50" : "bg-slate-50"}`}>
              <div className={`text-sm font-medium ${darkMode ? "text-white" : "text-slate-900"}`}>{assessmentModal.vendor.name}</div>
              <div className={`text-xs ${darkMode ? "text-slate-400" : "text-slate-500"}`}>{assessmentModal.vendor.vendor_type} • {assessmentModal.vendor.risk_tier || 'Unrated'}</div>
            </div>
            <div>
              <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Assessment Type</label>
              <select className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`}>
                <option value="initial">Initial Assessment</option>
                <option value="periodic">Periodic Review</option>
                <option value="triggered">Triggered Assessment</option>
              </select>
            </div>
            <div>
              <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Assessment Questionnaire</label>
              <select className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`}>
                <option value="standard">Standard TPRM Questionnaire</option>
                <option value="critical">Critical Vendor Deep Dive</option>
                <option value="security">Security Assessment (SOC2)</option>
              </select>
            </div>
            <div>
              <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Due Date</label>
              <input type="date" className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} />
            </div>
            <div>
              <label className={`text-sm font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Message to Vendor</label>
              <textarea className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm ${darkMode ? "bg-slate-700 border-slate-600 text-white" : "bg-white border-slate-200"}`} rows={3} placeholder="Please complete the attached assessment questionnaire by the due date..." />
            </div>
            <div className={`flex justify-end gap-2 pt-4 border-t ${darkMode ? "border-slate-700" : "border-slate-200"}`}>
              <Button variant="secondary" onClick={() => setAssessmentModal({ open: false, vendor: null, assessment: null })}>Cancel</Button>
              <Button onClick={() => { setToast({ type: 'success', message: 'Assessment request sent' }); setAssessmentModal({ open: false, vendor: null, assessment: null }); setTimeout(() => setToast(null), 3000); }}>Send Assessment</Button>
            </div>
          </div>
        </Modal>
      )}
      
      {/* Offline Banner */}
      {isOffline && (
        <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-amber-500 text-white rounded-lg shadow-lg p-4 z-50 flex items-center gap-3">
          <Icons.WifiOff className="w-6 h-6 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-medium">You're offline</div>
            <div className="text-sm text-amber-100">
              {pendingOfflineChanges.length} changes will sync when connected
            </div>
          </div>
          <button 
            onClick={() => setPendingOfflineChanges([])} 
            aria-label="Dismiss offline changes notification"
            className="text-amber-200 hover:text-white"
          >
            <Icons.X className="w-5 h-5" />
          </button>
        </div>
      )}
      
      {/* Global Search Modal */}
      {showGlobalSearch && (
        <div className="fixed inset-0 bg-black/60 flex items-start justify-center pt-[10vh] z-50" onClick={() => setShowGlobalSearch(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[70vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Search Input */}
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <Icons.Search className="w-5 h-5 text-slate-500" />
                <input
                  type="text"
                  id="global-search-input"
                  aria-label="Search scenarios, thresholds, regulatory updates, and documents"
                  placeholder="Search scenarios, thresholds, regulatory updates, documents..."
                  className="flex-1 text-lg outline-none"
                  value={globalSearchQuery}
                  onChange={e => setGlobalSearchQuery(e.target.value)}
                  autoFocus
                />
                <kbd className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-500">ESC</kbd>
              </div>
            </div>
            
            {/* Search Results */}
            <div className="flex-1 overflow-y-auto p-4">
              {!globalSearchResults ? (
                <div className="text-center py-8 text-slate-500">
                  <Icons.Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Type to search across all data</p>
                  <p className="text-sm mt-2">Search scenarios, thresholds, regulatory updates, documents, and attestations</p>
                </div>
              ) : globalSearchResults.total === 0 ? (
                <div className="text-center py-12">
                  <div className={`w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-3 ${darkMode ? "bg-slate-700" : "bg-slate-100"}`}>
                    <Icons.Search className={`w-6 h-6 ${darkMode ? "text-slate-500" : "text-slate-500"}`} />
                  </div>
                  <p className={`font-medium ${darkMode ? "text-slate-300" : "text-slate-700"}`}>No results found for "{globalSearchResults.query}"</p>
                  <p className={`text-sm mt-1 ${darkMode ? "text-slate-500" : "text-slate-500"}`}>Try different keywords or check your spelling</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-sm text-slate-500 font-medium">
                    {globalSearchResults.total} result{globalSearchResults.total !== 1 ? "s" : ""} for "{globalSearchResults.query}"
                  </div>
                  
                  {/* Scenarios */}
                  {globalSearchResults.scenarios.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                        Scenarios ({globalSearchResults.scenarios.length})
                      </div>
                      {globalSearchResults.scenarios.slice(0, 5).map(s => (
                        <div
                          key={s.id}
                          className="p-3 hover:bg-slate-50 rounded-lg cursor-pointer border border-slate-100 mb-2"
                          onClick={() => { setShowGlobalSearch(false); setActiveTab("governance"); }}
                        >
                          <div className="flex items-center gap-2">
                            <Badge color="blue">Scenario</Badge>
                            <span className="font-medium text-slate-900">{s.name}</span>
                          </div>
                          {s.description && (
                            <p className="text-sm text-slate-500 mt-1 line-clamp-1">{s.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Thresholds */}
                  {globalSearchResults.thresholds.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                        Thresholds ({globalSearchResults.thresholds.length})
                      </div>
                      {globalSearchResults.thresholds.slice(0, 5).map(t => (
                        <div
                          key={t.id}
                          className="p-3 hover:bg-slate-50 rounded-lg cursor-pointer border border-slate-100 mb-2"
                          onClick={() => { setShowGlobalSearch(false); setActiveTab("governance"); }}
                        >
                          <div className="flex items-center gap-2">
                            <Badge color="amber">Threshold</Badge>
                            <span className="font-medium text-slate-900">{t.name}</span>
                          </div>
                          {t.description && (
                            <p className="text-sm text-slate-500 mt-1 line-clamp-1">{t.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Regulatory Updates */}
                  {globalSearchResults.regulatoryUpdates.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                        Regulatory Updates ({globalSearchResults.regulatoryUpdates.length})
                      </div>
                      {globalSearchResults.regulatoryUpdates.slice(0, 5).map(u => (
                        <div
                          key={u.id || u.dedupe_key}
                          className="p-3 hover:bg-slate-50 rounded-lg cursor-pointer border border-slate-100 mb-2"
                          onClick={() => { setShowGlobalSearch(false); setActiveTab("regulatory"); }}
                        >
                          <div className="flex items-center gap-2">
                            <Badge color="emerald">Update</Badge>
                            <span className="font-medium text-slate-900 line-clamp-1">{u.title}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                            <span>{u.source}</span>
                            <span>•</span>
                            <span>{formatDate(u.published_at)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Documents */}
                  {globalSearchResults.documents.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                        Documents ({globalSearchResults.documents.length})
                      </div>
                      {globalSearchResults.documents.slice(0, 5).map(d => (
                        <div
                          key={d.id}
                          className="p-3 hover:bg-slate-50 rounded-lg cursor-pointer border border-slate-100 mb-2"
                          onClick={() => { setShowGlobalSearch(false); setActiveTab("documents"); }}
                        >
                          <div className="flex items-center gap-2">
                            <Badge color="purple">Document</Badge>
                            <span className="font-medium text-slate-900">{d.filename}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Attestations */}
                  {globalSearchResults.attestations.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                        Attestations ({globalSearchResults.attestations.length})
                      </div>
                      {globalSearchResults.attestations.slice(0, 5).map(a => (
                        <div
                          key={a.id}
                          className="p-3 hover:bg-slate-50 rounded-lg cursor-pointer border border-slate-100 mb-2"
                          onClick={() => { setShowGlobalSearch(false); setActiveTab("attestations"); }}
                        >
                          <div className="flex items-center gap-2">
                            <Badge color="slate">Attestation</Badge>
                            <span className="font-medium text-slate-900">{a.period_month}</span>
                          </div>
                          <div className="text-xs text-slate-500 mt-1">
                            By {a.attested_by_email} • {formatDate(a.attested_at)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="p-3 border-t border-slate-200 bg-slate-50 text-xs text-slate-500 flex items-center justify-between">
              <span>Press <kbd className="bg-white px-1.5 py-0.5 rounded border">↵</kbd> to select • <kbd className="bg-white px-1.5 py-0.5 rounded border">ESC</kbd> to close</span>
              <span>Global Search</span>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      <HelpModal 
        open={showHelpModal} 
        onClose={() => setShowHelpModal(false)} 
        vertical={vertical}
        darkMode={darkMode}
      />

      {/* Nine Questions Explainer Modal */}
      <NineQuestionsExplainer
        open={showNineQuestionsExplainer}
        onClose={() => setShowNineQuestionsExplainer(false)}
        darkMode={darkMode}
      />

      {/* Bulk CSV Import Modal */}
      <BulkCSVImportModal
        open={showBulkCSVImport.open}
        onClose={() => setShowBulkCSVImport({ open: false, entityType: null })}
        entityType={showBulkCSVImport.entityType}
        darkMode={darkMode}
        onImport={async (rows) => {
          // Import the rows based on entity type
          const table = showBulkCSVImport.entityType === 'scenario' ? 'scenarios' : 'thresholds';
          for (const row of rows) {
            const record = {
              organization_id: orgId,
              name: row.name,
              description: row.description || '',
              owner_email: row.owner_email || session?.user?.email,
              regulatory_basis: row.regulatory_basis || '',
              next_review_date: row.next_review_date || null,
              risk_level: row.risk_level || 'medium',
            };
            if (showBulkCSVImport.entityType === 'threshold') {
              record.current_value = row.current_value || null;
              record.unit = row.unit || '';
            }
            await supabase.from(table).insert(record);
          }
          setToast({ type: 'success', message: `Imported ${rows.length} ${showBulkCSVImport.entityType}${rows.length !== 1 ? 's' : ''}` });
          setTimeout(() => setToast(null), 3000);
          loadAllData();
        }}
      />

      {/* Command Palette - Red Team Customer Journey Enhancement */}
      <CommandPalette
        open={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
        darkMode={darkMode}
        onNavigate={(action) => {
          switch (action) {
            case 'overview':
            case 'governance':
            case 'examiner':
            case 'settings':
              setActiveTab(action);
              break;
            case 'create-scenario':
              setScenarioModal(true);
              break;
            case 'create-threshold':
              setThresholdModal(true);
              break;
            case 'search':
              setShowGlobalSearch(true);
              break;
            case 'help':
              setShowHelpModal(true);
              break;
            case 'toggle-dark':
              setDarkMode(prev => !prev);
              break;
            default:
              break;
          }
        }}
      />

      {/* Help Search Modal - Red Team Customer Journey Enhancement (Cmd+?) */}
      <HelpSearchModal
        open={showHelpSearch}
        onClose={() => setShowHelpSearch(false)}
        darkMode={darkMode}
      />

      {/* Persona Selector - Shows for first-time users */}
      {showPersonaSelector && session && !userPersona && (
        <PersonaSelector
          darkMode={darkMode}
          onSelect={(persona) => {
            if (persona) {
              setUserPersona(persona.id);
              localStorage.setItem('steadfastly_persona', persona.id);
              setShowPersonaSelector(false);
              // Start tutorial for non-fast-track users
              if (!persona.fastTrack) {
                setShowTutorial(true);
              }
              // Set default tab based on persona
              if (persona.defaultTab) {
                setActiveTab(persona.defaultTab);
              }
            }
          }}
        />
      )}

      {/* Interactive Tutorial */}
      <InteractiveTutorial
        open={showTutorial}
        onClose={() => setShowTutorial(false)}
        onComplete={() => {
          setToast({ type: 'success', message: '🎉 Tutorial complete! Check the "What\'s Next" widget for guidance.' });
          setTimeout(() => setToast(null), 5000);
        }}
        darkMode={darkMode}
        currentStep={tutorialStep}
        setCurrentStep={setTutorialStep}
      />

      {/* Advanced Report Builder */}
      {showReportBuilder && (
        <ReportBuilder
          vertical={vertical}
          scenarios={scenarios}
          thresholds={thresholds}
          policies={policies}
          risks={risks}
          controls={controls}
          vendors={vendors}
          incidents={incidents}
          findings={incidents.filter(i => i.severity === 'critical' || i.severity === 'high')}
          attestations={attestations}
          documents={documents}
          regulatoryUpdates={regulatoryUpdates}
          darkMode={darkMode}
          orgName={userProfile?.organization?.name || 'Organization'}
          orgLogo={userProfile?.organization?.logo_url}
          onClose={() => setShowReportBuilder(false)}
        />
      )}

      {/* Go Live Modal - Exit training mode */}
      <GoLiveModal
        open={showGoLiveModal}
        onClose={() => setShowGoLiveModal(false)}
        darkMode={darkMode}
        onConfirm={async () => {
          try {
            // Delete synthetic data
            const { error } = await supabase.rpc('clear_synthetic_data', { p_org_id: orgId });
            if (error) throw error;
            
            setTrainingMode(false);
            localStorage.setItem('steadfastly_training_mode', 'false');
            setShowGoLiveModal(false);
            setToast({ type: 'success', message: '🚀 You\'re now live! Training data has been cleared.' });
            setTimeout(() => setToast(null), 5000);
            loadAllData();
          } catch (err) {
            setToast({ type: 'error', message: 'Failed to go live. Please try again.' });
            setTimeout(() => setToast(null), 3000);
          }
        }}
      />

      {/* Reset Training Data Modal */}
      <ResetTrainingModal
        open={showResetTrainingModal}
        onClose={() => setShowResetTrainingModal(false)}
        darkMode={darkMode}
        onConfirm={async () => {
          try {
            // Call reset function
            const { error } = await supabase.rpc('reset_to_synthetic_data', { p_org_id: orgId });
            if (error) throw error;
            
            setShowResetTrainingModal(false);
            setToast({ type: 'success', message: '🔄 Training data has been reset!' });
            setTimeout(() => setToast(null), 3000);
            loadAllData();
          } catch (err) {
            setToast({ type: 'error', message: 'Failed to reset training data. Please try again.' });
            setTimeout(() => setToast(null), 3000);
          }
        }}
      />

      {/* Simple Onboarding Wizard - Shows for new users */}
      {showOnboarding && session && (
        <SimpleOnboardingWizard
          darkMode={darkMode}
          vertical={vertical}
          onComplete={(context) => {
            setShowOnboarding(false);
            if (context) {
              // User completed onboarding - experience customized based on context
            }
          }}
        />
      )}

      <DataExportModal
        open={showDataExportModal}
        onClose={() => setShowDataExportModal(false)}
        orgId={orgId}
        vertical={vertical}
        jurisdiction={jurisdiction}
      />
      
      {/* v9.0: Citation Picker Modal */}
      <CitationPickerModal
        open={citationPickerModal.open}
        onClose={() => setCitationPickerModal({ open: false, targetField: null, onSelect: null })}
        citations={citationLibrary}
        onSelect={(citation) => {
          if (citationPickerModal.onSelect) {
            citationPickerModal.onSelect(citation);
          }
          setCitationPickerModal({ open: false, targetField: null, onSelect: null });
        }}
        darkMode={darkMode}
      />
      
      {/* v9.0: WHY Builder Modal */}
      <WhyBuilderModal
        open={whyBuilderModal.open}
        onClose={() => setWhyBuilderModal({ open: false, type: "scenario", target: null })}
        itemType={whyBuilderModal.type}
        darkMode={darkMode}
        onSave={(text) => {
          // Insert generated WHY text into the appropriate form field
          if (whyBuilderModal.type === "scenario") {
            setScenarioForm(f => ({ ...f, regulatory_basis: text }));
          } else if (whyBuilderModal.type === "threshold") {
            setThresholdForm(f => ({ ...f, regulatory_basis: text }));
          }
          setWhyBuilderModal({ open: false, type: "scenario", target: null });
        }}
      />
      
      {/* v9.0: Interview Mode Wizard */}
      <InterviewModeWizard
        open={interviewModal}
        onClose={() => { setInterviewModal(false); setNewScenarioTemplate(null); }}
        darkMode={darkMode}
        vertical={vertical}
        template={newScenarioTemplate}
        onComplete={async (formData) => {
          // Create the scenario from Interview Mode
          try {
            const scenarioId = crypto.randomUUID();
            const payload = {
              scenario_id: scenarioId,
              organization_id: orgId,
              vertical,
              name: formData.name.trim(),
              owner_email: formData.owner_email.trim() || null,
              description: formData.description.trim() || null,
              regulatory_basis: formData.regulatory_basis.trim() || null,
              alternatives_considered: formData.alternatives_considered.trim() || null,
              documentation_location: formData.documentation_location.trim() || null,
              authority_citation: formData.authority_citation.trim() || null,
              evidence_summary: formData.evidence_summary.trim() || null,
              next_review_date: formData.next_review_date || null,
              risk_level: formData.risk_level || "medium",
            };
            const { error } = await supabase.from("scenario_versions").insert(payload);
            if (error) throw error;
            setToast({ type: "success", message: "Scenario created!" });
            setTimeout(() => setToast(null), 3000);
            loadAllData();
          } catch (err) {
            setToast({ type: "error", message: `Failed to create scenario: ${sanitizeErrorMessage(err)}` });
            setTimeout(() => setToast(null), 5000);
          }
        }}
      />
      
      {/* v9.0: First Run Wizard - Complete Enterprise Onboarding */}
      <OnboardingWizard
        open={onboardingModal}
        onClose={dismissOnboarding}
        darkMode={darkMode}
        orgId={orgId}
        userLanguage={userLanguage}
        callFn={callFn}
        setToast={setToast}
        loadAllData={loadAllData}
        supabase={supabase}
        session={session}
        onComplete={(data) => {
          // Store user preferences and mark onboarding as complete
          // Update vertical if selected
          if (data.vertical) {
            setVertical(data.vertical);
          }
          // Update org name in state if provided
          if (data.companyName && org) {
            setOrg({ ...org, name: data.companyName });
          }
          dismissOnboarding();
          // If they loaded training data, don't show sample viewer (they have data to explore)
          if (!data.trainingDataLoaded) {
            setSampleViewerModal(true);
          }
          // Show success message with company name
          if (data.companyName) {
            setToast({ type: "success", message: `Welcome to Steadfastly, ${data.companyName}! ${data.rolesConfigured > 0 ? `${data.rolesConfigured} team invitation(s) sent.` : ""}` });
            setTimeout(() => setToast(null), 5000);
          }
        }}
      />
      
      {/* v9.0: Sample Scenario Viewer */}
      <SampleScenarioViewer
        open={sampleViewerModal}
        onClose={() => setSampleViewerModal(false)}
        darkMode={darkMode}
        onCreateNew={() => {
          setSampleViewerModal(false);
          setInterviewModal(true);
        }}
      />
      
      {/* v9.0: Import Wizard with AI extraction */}
      <ImportWizard
        open={importWizardModal}
        onClose={() => setImportWizardModal(false)}
        darkMode={darkMode}
        onComplete={async (formData) => {
          // Create the scenario from imported data
          try {
            const scenarioId = crypto.randomUUID();
            const payload = {
              scenario_id: scenarioId,
              organization_id: orgId,
              vertical,
              name: formData.name?.trim() || "Imported Scenario",
              owner_email: formData.owner_email?.trim() || null,
              description: formData.description?.trim() || null,
              regulatory_basis: formData.regulatory_basis?.trim() || null,
              review_frequency: formData.review_frequency || null,
              risk_level: "medium",
            };
            const { error } = await supabase.from("scenario_versions").insert(payload);
            if (error) throw error;
            setToast({ type: "success", message: "Scenario imported!" });
            setTimeout(() => setToast(null), 3000);
            loadAllData();
          } catch (err) {
            setToast({ type: "error", message: `Failed to import: ${sanitizeErrorMessage(err)}` });
            setTimeout(() => setToast(null), 5000);
          }
        }}
      />
      
      {/* Jurisdiction Selector Modal */}
      <JurisdictionSelectorModal
        open={showJurisdictionSelector}
        onClose={() => setShowJurisdictionSelector(false)}
        currentJurisdiction={jurisdiction}
        onSelect={(code) => {
          setJurisdiction(code);
          setShowJurisdictionSelector(false);
          // Load jurisdiction-specific feeds
          loadJurisdictionData(code);
          setToast({ type: "success", message: `Switched to ${JURISDICTION_CONFIG[code]?.name}` });
          setTimeout(() => setToast(null), 3000);
        }}
      />
      
      {/* SESSION TIMEOUT WARNING MODAL */}
      {showTimeoutWarning && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50">
          <div className={`max-w-sm w-full rounded-xl p-6 ${darkMode ? "bg-slate-800 border border-slate-700" : "bg-white"} shadow-xl`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center">
                <Icons.Clock className="w-6 h-6 text-amber-500" />
              </div>
              <div>
                <h3 className={`font-semibold ${darkMode ? "text-white" : "text-slate-900"}`}>Session Expiring</h3>
                <p className={`text-sm ${darkMode ? "text-slate-500" : "text-slate-500"}`}>
                  {Math.floor(timeoutCountdown / 60)}:{(timeoutCountdown % 60).toString().padStart(2, '0')} remaining
                </p>
              </div>
            </div>
            <p className={`text-sm mb-4 ${darkMode ? "text-slate-300" : "text-slate-600"}`}>
              Your session will expire due to inactivity. Click below to stay signed in.
            </p>
            <div className="flex gap-2">
              <Button 
                onClick={() => { setLastActivity(Date.now()); setShowTimeoutWarning(false); }}
                className="flex-1"
              >
                Stay Signed In
              </Button>
              <Button 
                variant="secondary"
                onClick={() => supabase.auth.signOut()}
              >
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* FEATURE COMPLETE: Command Palette (Ctrl+K) */}
      <CommandPalette
        open={showCommandPalette}
        onClose={() => { setShowCommandPalette(false); setCommandPaletteQuery(""); }}
        query={commandPaletteQuery}
        setQuery={setCommandPaletteQuery}
        darkMode={darkMode}
        actions={[
          // Hide create actions in Examiner Mode
          ...(examinerModeActive ? [] : [
            { id: 'new-scenario', name: 'New Scenario', description: 'Create a new governance scenario', shortcut: 'Ctrl+N', icon: <Icons.Plus className="w-4 h-4" />, action: () => setScenarioModal(true) },
            { id: 'new-threshold', name: 'New Threshold', description: 'Create a new threshold', shortcut: 'Ctrl+Shift+N', icon: <Icons.Plus className="w-4 h-4" />, action: () => setThresholdModal(true) },
          ]),
          { id: 'search', name: 'Search', description: 'Search across all content', shortcut: 'Ctrl+/', icon: <Icons.Search className="w-4 h-4" />, action: () => setShowGlobalSearch(true) },
          { id: 'ask-steadfastly', name: 'Ask Steadfastly', description: 'Search books + blog posts (Cmd+Shift+K)', shortcut: 'Ctrl+Shift+K', icon: <Icons.Search className="w-4 h-4" />, action: () => window.dispatchEvent(new Event('ask-steadfastly-open')) },
          { id: 'dashboard', name: 'Go to Dashboard', shortcut: 'Alt+1', icon: <Icons.Home className="w-4 h-4" />, action: () => setActiveTab('dashboard') },
          { id: 'scenarios', name: 'Go to Scenarios', shortcut: 'Alt+2', icon: <Icons.FileText className="w-4 h-4" />, action: () => setActiveTab('scenarios') },
          { id: 'thresholds', name: 'Go to Thresholds', shortcut: 'Alt+3', icon: <Icons.Activity className="w-4 h-4" />, action: () => setActiveTab('thresholds') },
          { id: 'regulatory', name: 'Go to Regulatory Updates', shortcut: 'Alt+4', icon: <Icons.Bell className="w-4 h-4" />, action: () => setActiveTab('regulatory') },
          { id: 'documents', name: 'Go to Documents', shortcut: 'Alt+5', icon: <Icons.FileText className="w-4 h-4" />, action: () => setActiveTab('documents') },
          { id: 'attest', name: 'Start Attestation', description: 'Begin the attestation process', icon: <Icons.Shield className="w-4 h-4" />, action: () => setAttestModal(true) },
          { id: 'examiner-pkg', name: 'Generate Examiner Package', description: 'Create PDF for regulators', icon: <Icons.Download className="w-4 h-4" />, action: () => setExaminerModal({ ...examinerModal, open: true }) },
          { id: 'examiner-mode', name: 'Toggle Examiner Mode', description: 'Hide internal notes', shortcut: 'Ctrl+E', icon: <Icons.Eye className="w-4 h-4" />, action: () => setExaminerModeActive(prev => !prev) },
          { id: 'export', name: 'Export Data', description: 'Export your data', icon: <Icons.Download className="w-4 h-4" />, action: () => setShowDataExportModal(true) },
          { id: 'help', name: 'Help & Documentation', shortcut: 'Ctrl+/', icon: <Icons.HelpCircle className="w-4 h-4" />, action: () => setShowHelpModal(true) },
          { id: 'theme', name: 'Toggle Theme', description: `Currently: ${themeMode}`, icon: darkMode ? <Icons.Sun className="w-4 h-4" /> : <Icons.Moon className="w-4 h-4" />, action: () => cycleTheme() },
          { id: 'settings', name: 'Settings', shortcut: 'Alt+8', icon: <Icons.Settings className="w-4 h-4" />, action: () => setActiveTab('settings') },
        ]}
      />
      
      {/* Ask Steadfastly overlay - books + blogs RAG (Cmd+Shift+K or Command Palette) */}
      <AskSteadfastlyOverlay />

      {/* FEATURE COMPLETE: Examiner Mode Banner */}
      <ExaminerModeBanner 
        active={examinerModeActive} 
        onExit={() => setExaminerModeActive(false)} 
        darkMode={darkMode} 
      />
      
      {/* FEATURE COMPLETE: Version History Modal */}
      <VersionHistoryModal
        open={versionHistoryModal.open}
        onClose={() => setVersionHistoryModal({ open: false, entityType: null, entityId: null })}
        entityType={versionHistoryModal.entityType}
        entityId={versionHistoryModal.entityId}
        historyData={versionHistoryData}
        loading={versionHistoryLoading}
        darkMode={darkMode}
        onRevert={async (version) => {
          // TODO: Implement revert functionality
          setToast({ type: "info", message: "Revert functionality coming soon", action: "Contact support for manual restore" });
          setTimeout(() => setToast(null), 3000);
        }}
      />
      
      {/* FEATURE COMPLETE: Bulk Actions Toolbar */}
      <BulkActionsToolbar
        selection={bulkSelection.scenarios.length > 0 ? bulkSelection.scenarios : bulkSelection.thresholds}
        entityType={bulkSelection.scenarios.length > 0 ? 'scenario' : 'threshold'}
        onClear={() => setBulkSelection({ scenarios: [], thresholds: [] })}
        onAction={(action) => {
          const items = bulkSelection.scenarios.length > 0 ? bulkSelection.scenarios : bulkSelection.thresholds;
          const type = bulkSelection.scenarios.length > 0 ? 'scenarios' : 'thresholds';
          if (action === 'delete') {
            showConfirmModal({
              title: `Delete ${items.length} ${type}?`,
              message: `This will permanently delete ${items.length} selected ${type}. This action cannot be undone.`,
              confirmText: 'Delete All',
              confirmVariant: 'danger',
              isDestructive: true,
              onConfirm: async () => {
                // Bulk delete would go here
                setToast({ type: "success", message: `Deleted ${items.length} ${type}` });
                setBulkSelection({ scenarios: [], thresholds: [] });
                setTimeout(() => setToast(null), 3000);
              }
            });
          } else if (action === 'export') {
            setToast({ type: "info", message: `Exporting ${items.length} ${type}...` });
            setTimeout(() => setToast(null), 3000);
          } else if (action === 'archive') {
            setToast({ type: "info", message: `Archiving ${items.length} ${type}...` });
            setTimeout(() => setToast(null), 3000);
          } else if (action === 'assignOwner') {
            setToast({ type: "info", message: "Owner assignment modal coming soon" });
            setTimeout(() => setToast(null), 3000);
          }
        }}
        darkMode={darkMode}
      />
      
      {/* U-2 FIX: Toast notification with actionable guidance */}
      {/* WCAG 4.1.3 Status Messages - Red Team Phase 11 */}
      {toast && (
        <div 
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className={`fixed bottom-4 right-4 max-w-md px-4 py-3 rounded-lg shadow-lg z-50 ${
          toast.type === 'success' ? 'bg-emerald-500 text-white' : 
          toast.type === 'info' ? 'bg-blue-500 text-white' :
          toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-slate-700 text-white'
        }`}>
          <div className="flex items-start gap-2">
            <div className="flex-shrink-0 mt-0.5">
              {toast.type === 'success' ? <Icons.Check /> : toast.type === 'info' ? <Icons.Bell /> : <Icons.Alert />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium">{toast.message}</div>
              {toast.action && (
                <div className="text-sm mt-1 opacity-90">
                  💡 <strong>Next step:</strong> {toast.action}
                </div>
              )}
            </div>
            <button 
              onClick={() => setToast(null)} 
              aria-label="Dismiss notification"
              className="flex-shrink-0 ml-2 opacity-70 hover:opacity-100 p-1"
            >
              <Icons.X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// EXPORT: App wrapped with ErrorBoundary (Red Team Phase 6)
// Catches unhandled errors and shows user-friendly error screen
// ============================================================================
export default function App() {
  return (
    <ErrorBoundary>
      <AppContent />
    </ErrorBoundary>
  );
}
